import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, Inject, NgZone, ChangeDetectorRef, Input } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormBuilder, FormGroup, AbstractControl, Validators } from '@angular/forms';
import { firstValueFrom, Observable, of, interval, Subject, takeUntil } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AudiostreamingDbService, RecordingSession, AudioChunkData, TranscriptionData } from './audiostreaming-db.service';
import { WasapiRecorderService } from './wasapi-recorder.service';
import {
  LiveKitStreamingService,
  LiveKitConfig,
  LiveKitStatus,
  LiveKitParticipant,
  LiveKitDataMessage,
  LiveKitNativeTranscriptionEvent
} from './livekit-streaming.service';
import {
  BackendService
} from '../../services/backend.service';
import { RecordingSource, StartStreamingSessionRequest, StartStreamingSessionResponse, StartTranscriptionRequest, StartTranscriptionResponse, StopStreamingSessionRequest, StopStreamingSessionResponse, StopTranscriptionRequest, StopTranscriptionResponse, StreamingDetails, TranscriptionSegment, GetFileUploadModel, Annotations, AnnotationDialogModel, AnnotationSpeaker, CreateNewCaseRequest } from '../../models/StreamingModels';
import Hls from 'hls.js';
import { CaseRolePeopleModel, CourtLocations, CourtRooms, Organizations } from '../../models/apiRequestModels';
import { NgxSpinnerService } from 'ngx-spinner';
import { jqxNotificationComponent } from 'jqwidgets-ng/jqxnotification';
import { FileAccessService } from '../../services/fileaccess.service';
import { WakeLockService } from '../../services/wake-lock.service';
import { jqxWindowComponent } from 'jqwidgets-ng/jqxwindow';
import { SelectCaseDialogComponent } from '../case-management/select-case-dialog/select-case-dialog.component';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { AnnotationComponent } from './annotation/annotation.component';
import { AnnotationsComponent } from './annotations.component';
import { Idle } from '@ng-idle/core';  // Assuming you're using ng-idle
import { DatePipe } from '@angular/common';
import { jqxExpanderComponent } from 'jqwidgets-ng/jqxexpander';
import { FieldDefinition, FieldType } from '../../models/field-definition';
import { buildFormDataSnapshot, FormDataEntry, parseFormDataSnapshot } from '../../shared/dynamic-form/form-data-snapshot';
import { normalizeFields } from '../../shared/dynamic-form/field-normalizer';
import { buildAnnotationSpeakers } from '../../shared/dynamic-form/annotation-speakers';
import { toDateValue } from '../../shared/dynamic-form/date-value';
import { NewCaseDialogComponent, NewCaseDialogData, NewCaseDialogResult } from './new-case-dialog/new-case-dialog.component';
import { ChannelMixer, computeMeterLevel, PlaybackChannelMixer } from './channel-mixer';

// Mics with more channels than this are captured without browser noise suppression/AGC so no channels are lost.
const MAX_PROCESSED_MIC_CHANNELS = 2;

interface MicrophoneDevice {
  deviceId: string;
  label: string;
}

interface LanguageOption {
  code: string;
  name: string;
}

interface TranscriptionHistory {
  text: string;
  timestamp: Date;
}

interface TranscriptWord {
  word: string;
  start: number;
  end: number;
  confidence?: number;
}

interface TranscriptResponse {
  type: string;
  participant: string;
  participant_identity: string;
  same_speaker: boolean | undefined;
  speaker: string;
  text: string;
  translated_text: string;
  translation_target: any;
  is_final: boolean;
  confidence: number;
  language: string;
  timestamp: number;
  duration: number;
  words: TranscriptWord[];
  created_at: string;
}
interface TranscriptJsonModel {
  Transcripts: TranscriptResponse[];
  SessionId: string;
}
interface IncomingTranscriptionSegment {
  speaker: string;
  text: string;
  translatedText?: string;
  translationTarget?: string;
  language?: string;
  timestamp: number;
  startTime?: number;
  endTime?: number;
  isFinal: boolean;
  sameSpeaker?: boolean | undefined;
  trackId?: string;
}

interface TranscriptionViewSegment {
  speaker: string;
  speakerNumber: number;
  speakerKey: string;
  text: string;
  translatedText: string;
  translationTarget?: string;
  language?: string;
  timestamp: number;
  startTime: number;
  endTime: number;
  trackId?: string;
  /** Divider row marking a new case starting; renders in place of a normal speaker row. */
  isCaseMarker?: boolean;
  caseLabel?: string;
}
export interface dropdownmodel {
  id: string;
  name: string;
  orderFormTypes: string;
  formName: string
}

@Component({
  selector: 'app-audiostreaming',
  templateUrl: './audiostreaming.component.html',
  styleUrls: ['./audiostreaming.component.css']
})
export class AudiostreamingComponent implements OnInit, OnDestroy, AfterViewInit {
  // @ViewChild('audioCanvas', { static: false }) audioCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('audioPlayerElement', { static: false }) audioPlayerElement!: ElementRef<HTMLAudioElement>;
  @ViewChild('audioPlayerHLS', { static: true }) audioPlayerHLS!: ElementRef<HTMLAudioElement>;
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  isTranscriptionScrolledUp = false;
  private lastTranscriptionScrollTop = 0;
  private readonly transcriptionBottomThreshold = 40;
  @ViewChild('caseTableScrollContainer') private caseTableScrollContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('msgNotification', { static: false }) msgNotification!: jqxNotificationComponent;
  @ViewChild('annotationWindow', { static: false }) annotationWindow!: jqxWindowComponent;
  @ViewChild('annotationComp', { static: false }) annotationComponentRef?: AnnotationComponent;
  @ViewChild('audioRef', { static: false }) audioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('annotationsExpander', { static: false }) annotationsExpander!: jqxExpanderComponent;
  @Input() fileToPlay: string = '';
  // Microphone and Recording
  availableMicrophones: MicrophoneDevice[] = [];
  selectedMicrophone: string = '';
  selectedMicrophoneName: string = '';
  microphoneControl = new FormControl();
  private readonly systemDefaultMicDeviceId: string = 'default';
  private usingSystemDefaultMicrophone: boolean = false;
  private readonly mediaDeviceChangeHandler = () => {
    void this.handleMediaDevicesChanged();
  };

  private updateFormControlsState(isRecording: boolean): void {
    if (isRecording) {
      this.microphoneControl.disable();
      this.languageControl.disable();
      this.organizationControl.disable();
      this.departmentControl.disable();
      this.courtLocationControl.disable();
      this.courtRoomControl.disable();
      this.caseNumberControl?.disable();
      this.judgeFirstNameControl?.disable();
      this.judgeLastNameControl?.disable();
      // emitEvent: false so toggling the lock is not read as the user picking a hearing date.
      this.hearingDateTimeControl?.disable({ emitEvent: false });
      this.formsFormGroup?.disable({ emitEvent: false });
      // The form is locked from here on, so its values are final for this recording's speakers.
      this.refreshAnnotationSpeakers();
    } else {
      this.microphoneControl.enable();
      this.languageControl.enable();
      this.organizationControl.enable();
      this.departmentControl.enable();
      this.courtLocationControl.enable();
      this.courtRoomControl.enable();
      this.caseNumberControl?.enable();
      this.judgeFirstNameControl?.enable();
      this.judgeLastNameControl?.enable();
      this.hearingDateTimeControl?.enable({ emitEvent: false });
      this.formsFormGroup?.enable({ emitEvent: false });
      // Recording is over, so the page form is the speakers' source again for the next one.
      this.activeCaseFormValues = null;
      this.refreshAnnotationSpeakers();
      // Keep lists with a single auto-selected option locked, as on initial load.
      if (this.organizationOptions.length === 1) {
        this.organizationControl.disable({ onlySelf: true });
      }
      if (this.departmentOptions.length === 1) {
        this.departmentControl.disable({ onlySelf: true });
      }
      if (this.courtlocationsOptions.length === 1) {
        this.courtLocationControl.disable({ onlySelf: true });
      }
      if (this.courtRoomsOptions.length === 1) {
        this.courtRoomControl.disable({ onlySelf: true });
      }
    }
  }
  micFilteredOptions!: Observable<MicrophoneDevice[]>;
  mediaRecorder: MediaRecorder | null = null;
  audioStream: MediaStream | null = null;
  audioChunks: Blob[] = [];
  internalStream: MediaStream | null = null;
  externalStream: MediaStream | null = null;
  private mixerAudioContext: AudioContext | null = null;
  // Per-channel gain/mute for the capture device; null until recording starts in microphone mode.
  channelMixer: ChannelMixer | null = null;
  private channelDetection: Promise<void> | null = null;
  // Per-channel gain/mute for the playback player; attached on first open of its channels panel.
  readonly playbackChannels = new PlaybackChannelMixer();
  // Recording State
  isRecording: boolean = false;
  isPaused: boolean = false;
  isStartingRecording: boolean = false;
  isStoppingRecording: boolean = false;
  recordingStatus: string = '';
  recordingDuration: string = '00:00:00';
  recordedAudioBlob: Blob | null = null;
  recordedAudioSize: string = '';
  audioFormat: 'webm' | 'wav' = 'webm'; // Fixed to WebM for current UI flow
  isConvertingToWav: boolean = false;

  private currentSessionId: string = '';
  private isStreaming: boolean = false;
  streamingMode: 'batch' | 'live' = 'live'; // batch = send after recording, live = stream in real-time
  captureMode: 'microphone' | 'system' = 'microphone'; // microphone = mic only, system = system audio (both sides of call)
  detectedDeviceChannels: number = 1; // Actual channel count from device (auto-detected)

  // LiveKit Integration
  useLiveKit: boolean = false; // Flag to enable/disable LiveKit streaming
  liveKitConnected: boolean = false;
  liveKitConfig: LiveKitConfig = {
    url: '', // Will be set from environment or config
    token: '', // Will be set from API or config
    roomName: '', // Will be auto-generated when recording starts
    participantIdentity: '', // maps to C# ParticipantIdentity
    participantName: '',    // maps to C# ParticipantName
    role: ''        // maps to C# Role
  };
  private liveKitStatus: LiveKitStatus | null = null;
  private liveKitSubscriptions: any[] = [];
  liveKitParticipants: LiveKitParticipant[] = [];

  // Room sharing
  currentRoomName: string = '';
  shareableRoomLink: string = '';
  isHost: boolean = true; // True when creating room, false when joining

  // Recording Mode Selection
  recordingMode: 'browser' | 'wasapi' = 'browser'; // browser = Browser Audio API, wasapi = WASAPI multi-channel
  wasapiInstalled: boolean = false;
  wasapiChecking: boolean = false;
  wasapiDevices: any[] = [];
  selectedWasapiDevice: string = '';
  resampleWasapiAudio: boolean = true; // Resample WASAPI audio to 16kHz mono for transcription
  currentCaseNumber: string = '';
  // Auto-detected format from WASAPI service
  private detectedWasapiFormat: {
    sampleRate: number;
    channels: number;
    bitsPerSample: number;
    isFloat: boolean;
  } | null = null;

  private streamAudioContext: AudioContext | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private processorNode: ScriptProcessorNode | null = null;
  private meterAudioContext: AudioContext | null = null;
  private meterAnalyser: AnalyserNode | null = null;
  private meterSource: MediaStreamAudioSourceNode | null = null;
  private micLevelFrameId: number | null = null;
  private wasapiAudioSubscription: any = null;
  private wasapiStatusSubscription: any = null;
  private wasapiErrorSubscription: any = null;

  // Provider settings (configured in code, not exposed to UI except language)
  private currentProvider: string = 'deepgram'; // deepgram or azure_speech - change here to switch provider
  selectedLanguage: string = 'en-US'; // User can change this in UI
  languageControl = new FormControl();
  langFilteredOptions!: Observable<LanguageOption[]>;
  availableLanguages: LanguageOption[] = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    // { code: 'es', name: 'Spanish' },
    // { code: 'fr', name: 'French' },
    // { code: 'de', name: 'German' },
    // { code: 'it', name: 'Italian' },
    // { code: 'pt', name: 'Portuguese' },
    // { code: 'hi', name: 'Hindi' },
    // { code: 'ja', name: 'Japanese' },
    // { code: 'ko', name: 'Korean' },
    // { code: 'zh', name: 'Chinese' },
    { code: 'multi', name: 'Multi-Language' }
  ];
  private selectedModel: string = 'nova-3'; // Model for Deepgram - change here if needed
  translateToEnglish: boolean = false; // Translate to English option

  // Transcription storage
  private transcriptResponses: TranscriptResponse[] = [];
  private recentTranscriptSignatures: Map<string, number> = new Map();
  private fullTranscript: string = '';
  private currentInterimText: string = '';
  serverConnected: boolean = false;

  // Cumulative speaker tracking
  private lastSpeaker: number = -1;
  private lastSpeakerKey: string = '';
  private currentSpeakerStartTime: number = 0;
  private currentSpeakerEndTime: number = 0;
  private currentParagraphLines: string[] = [];
  private lastNativeTranscriptionAt: number = 0;
  private readonly nativePriorityWindowMs: number = 15000;

  // C# ProcessTranscriptResponses state (mirrors server-side tracking)
  private lkLastFinalSpeakerKey: string = '';
  private lkLastFinalStartSeconds: number = 0;
  private lkLastFinalEndSeconds: number = 0;
  private lkLastFinalText: string = '';
  private lkLastFinalTranslatedText: string = '';
  private lkInterimSegmentText: string = '';
  private lkInterimTranslatedSegmentText: string = '';

  // Audio streaming stats
  private totalBytesSent: number = 0;
  currentVolume: number = 0;
  private offlineAudioQueue: ArrayBufferLike[] = [];

  // Live streaming audio capture for playback
  private liveStreamAudioChunks: Blob[] = [];
  private mediaRecorderForLivePlayback: MediaRecorder | null = null;
  private stopRequested: boolean = false;

  // IndexedDB for failsafe
  private currentRecordingSessionId: string = '';
  recordingId: number = 0;
  private audioChunkIndex: number = 0;
  private dbSaveInterval: any = null;
  private dbSaveInFlight = false;
  private transcriptionVersion = 0;
  private savedTranscriptionVersion = -1;

  // Timer
  private recordingStartTime: number = 0;
  private recordingTimer: any;
  private pausedDuration: number = 0;
  private pauseStartTime: number = 0;
  get annotationPauseTime(): number | null {
    return this.isPaused && this.pauseStartTime > 0 ? this.pauseStartTime : null;
  }
  pausedForDuration: string = '00:00:00';
  private pausedForTimer: any = null;

  // Audio Playback
  private audioPlayer: HTMLAudioElement | null = null;
  isPlaying: boolean = false;
  currentPlaybackTime: number = 0;
  audioDuration: number = 0;
  playbackProgress: number = 0;
  private playbackUpdateInterval: any = null;
  currentAudioUrl: string = ''; // Store object URL for cleanup
  audioChannels: number = 0; // Number of audio channels
  audioSampleRate: number = 0; // Sample rate in Hz
  currentAudioIndex: number = 0;
  audioCurrentTime: number = 0;
  audioProgressPercent: number = 0;
  audioVolume: number = 1;
  isAudioPlaying: boolean = false;
  IsAudioPlay: boolean = false;
  audioStarted: boolean = false;
  audioList: string[] = [];
  hlsEngine: any = null;
  hlsRefreshTimer: any = null;

  // Channel volume controls
  channelVolumes: number[] = []; // Volume for each channel (0-100)
  private playbackAudioContext: AudioContext | null = null;
  private playbackSourceNode: MediaElementAudioSourceNode | null = null;
  private playbackSplitter: ChannelSplitterNode | null = null;
  private playbackMerger: ChannelMergerNode | null = null;
  private playbackGainNodes: GainNode[] = [];
  showChannelControls: boolean = false;
  showPlaybackSpeed: boolean = false;
  showAudioInfo: boolean = false;

  // Enhanced playback controls
  playbackSpeed: number = 1.0;
  volume: number = 1.0;
  isMuted: boolean = false;
  bufferedProgress: number = 0;
  isLoading: boolean = false;
  seekTooltipTime: string = '00:00';
  seekTooltipVisible: boolean = false;
  seekTooltipLeft: number = 0;
  playbackSpeedOptions: number[] = [0.5, 0.75, 1, 1.25, 1.5, 2];
  recordingStartDateTime: string = '';
  private recordingStopDateTime: string = '';
  private judgeName: string = '';
  private hearingDate: string = '';
  // Computed property for progress handle position
  get progressHandleStyle(): any {
    return {
      left: `${this.playbackProgress}%`
    };
  }

  // Audio Visualizer
  private visualizerAudioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private animationFrame: number = 0;

  // Upload State
  isSending: boolean = false;
  uploadProgress: number = 0;
  successMessage: string = '';
  errorMessage: string = '';

  // Transcription
  transcriptionText: string = '';
  formattedTranscriptionHTML: string = '';
  interimTranscription: string = '';
  finalTranscriptionSegments: TranscriptionViewSegment[] = [];
  interimTranscriptionSegment: TranscriptionViewSegment | null = null;
  isTranscribing: boolean = false;
  wordCount: number = 0;
  characterCount: number = 0;
  lastUpdated: Date | null = null;
  transcriptionHistory: TranscriptionHistory[] = [];
  showTimestamps: boolean = true;
  courtLocationControl = new FormControl();
  organizationControl = new FormControl();
  organizationfilteredOptions!: Observable<Organizations[]>;
  organizationOptions: any[] = [];
  selectedOrganization!: Organizations;
  selectedOrgExtId = "";
  // The Case List (select case) button is only shown when the selected organisation is a court.
  selectedOrgIsCourt = false;
  courtlocationsOptions: any[] = [];
  courtLocationfilteredOptions!: Observable<CourtLocations[]>;
  selectedcourtLocationExtId: string = '';
  selectedcourtLocationName: string = '';
  courtRoomControl = new FormControl();
  courtRoomfilteredOptions!: Observable<CourtRooms[]>;
  selectedcourtRoomExtId: string = '';
  selectedcourtRoomName: string = '';
  courtRoomsOptions: any[] = [];
  recordingDetails: any = { AudioStart: null, Events: [], AudioEnd: null };

  // ===================== Case Management (Select Case) =====================
  selectedCaseId: number | null = null;
  // Id of the CaseDetails record (distinct from selectedCaseId, the court case id) used to tag annotations.
  selectedCaseDetailsId: number | null = null;
  isCreatingNewCase = false;
  caseProfileName: string = '';
  caseNotes: string = '';
  selectedCasePeople: any[] = [];       // CaseRolePeopleModel[]
  casePeopleGroups: { role: string, names: string[] }[] = [];
  recordingCaseRows: Array<{
    id: number;
    caseNumber: string;
    hearingDateTime: Date | null;
    startedAt: Date;
    stoppedAt: Date | null;
    // Cumulative recording pause time at the moment the case started/stopped, so a case duration
    // can be reported as recorded time only.
    startedPausedMs: number;
    stoppedPausedMs: number | null;
    formDataEntries: FormDataEntry[];
  }> = [];
  private recordingCaseClock: number = Date.now();
  isCasesExpanded: boolean = true;
  // Renumbers speaker labels back to "Speaker 1" for each new case, without touching the
  // underlying speakerKey used for same-speaker continuation merging.
  private caseSpeakerKeyMap = new Map<string, number>();
  private caseSpeakerCounter = 0;

  // API Configuration
  private apiEndpoint: string = '/api/audio/transcribe'; // Update with your API endpoint

  egressId: string = '';
  private dispatchId: string = '';
  private activeRoomSid: string = '';
  micLevel: number = 0;
  hlsUrl: string = '';
  isRecordingDetailsExpanded: boolean = true;
  includeExternalAudio: boolean = false;
  cableInputAvailable: boolean = false;
  showParticipants: boolean = false;
  participantCount: number = 2;

  isCaseDetailsExpanded: boolean = true;
  isAnnotationsExpanded: boolean = true;
  showAnnotations: boolean = false;
  showRecordingDetails: boolean = true;
  showCaseDetails: boolean = true;
  isAnnotationMaximized: boolean = false;
  autoStartTranscription: boolean = true;
  allowAnnotations: boolean = false;

  showForms: boolean = true;
  isFormsExpanded: boolean = true;
  formsTypeOptions: any[] = [];
  selectedFormType: any = null;
  formsFieldsList: FieldDefinition[] = [];
  formsFormGroup!: FormGroup;
  get recordingCaseReferenceLabel(): string {
    return this.formsFieldsList.find(field => field.unique)?.label || 'Referance';
  }
  // Auto-filled values (Unique -> case number, ReferenceField -> case/casePeople data) applied to
  // formsFieldsList when a case is selected; bound to app-dynamic-form's [value] input.
  formsFieldValues: Record<string, unknown> = {};
  // People offered in the annotation Speakers menu; rebuilt whenever the form fields or values change.
  annotationSpeakers: AnnotationSpeaker[] = [];
  // Values of a case switched to mid-recording from the New Case popup. They are never written back
  // to the page form, so while set they, not the page form, are the source of the speakers.
  // Cleared when the recording stops or the case details are reset.
  private activeCaseFormValues: Record<string, unknown> | null = null;

  ///dept details
    departmentOptions: any[];
    deptfilteredOptions: Observable<dropdownmodel[]>;
    departmentControl = new FormControl();
    selectedDepartmentId = "";
    selectedDepartmentExtId = "";
  // Case Details are fixed fields owned by this page, not part of the selected form type: they
  // are mandatory for court organizations and hidden for everyone else, which is a property of
  // the org rather than of a form definition. The dynamic form below carries custom fields only.
  readonly caseDetailsForm = new FormGroup({
    caseNumber: new FormControl<string>(''),
    hearingDateTime: new FormControl<Date | null>(null),
    judgeFirstName: new FormControl<string>(''),
    judgeLastName: new FormControl<string>(''),
  });

  setAnnotationDetailVisibility(isMaximized: boolean): void {
    this.isAnnotationMaximized = isMaximized;
    this.showRecordingDetails = !isMaximized;
    this.showCaseDetails = !isMaximized;
    this.isCaseDetailsExpanded = false;
    this.isRecordingDetailsExpanded = false;
  }


  private readonly hearingDateTimeInterval$: Observable<number> = interval(1000);
  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  currentDateTime: Date = new Date();
  private recordingStartedAt: string = '';
  /** True once the hearing date/time holds a user-picked (or case-provided) value. */
  private hearingDateTimeEdited = false;
  isMaximized: boolean = false;
  get caseNumberControl(): AbstractControl | null { return this.caseDetailsForm.get('caseNumber'); }
  get hearingDateTimeControl(): AbstractControl | null { return this.caseDetailsForm.get('hearingDateTime'); }
  get judgeFirstNameControl(): AbstractControl | null { return this.caseDetailsForm.get('judgeFirstName'); }
  get judgeLastNameControl(): AbstractControl | null { return this.caseDetailsForm.get('judgeLastName'); }

  /**
   * Court organizations must supply all four case fields; for any other org the whole section is
   * hidden and must not block recording. Called whenever the selected org's court flag resolves.
   */
  private applyCaseDetailsValidators(): void {
    for (const key of Object.keys(this.caseDetailsForm.controls)) {
      const control = this.caseDetailsForm.get(key);
      if (!control) {
        continue;
      }
      control.setValidators(this.selectedOrgIsCourt ? [Validators.required] : []);
      // emitEvent: false so re-running this never registers as a user edit of the hearing time.
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  // Custom form fields only - the four case fields are native now and travel as their own payload
  // properties. getRawValue() (not value) so fields disabled while recording still submit.
  private buildFormDataPayload(): string | undefined {
    if (!this.formsFormGroup || !this.formsFieldsList.length) {
      return undefined;
    }
    const snapshot = buildFormDataSnapshot(this.formsFieldsList, this.formsFormGroup.getRawValue() ?? {});
    return JSON.stringify(snapshot);
  }

  private resolveHearingDateTime(startedAt: string): Date {
    const hearingDateField = this.formsFieldsList.find(field =>
      field.referenceField?.trim().toLowerCase() === 'hearingdate');
    const formValues = this.formsFormGroup?.getRawValue() ?? this.formsFieldValues;
    const hearingDate = hearingDateField ? toDateValue(formValues?.[hearingDateField.key]) : null;
    return hearingDate || new Date(startedAt);
  }

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private liveKitService: LiveKitStreamingService,
    private dialog: MatDialog,
    private dbService: AudiostreamingDbService,
    public wasapiService: WasapiRecorderService,
    private backendService: BackendService,
    private spinner: NgxSpinnerService,
    private fileacces: FileAccessService,
    private ngZone: NgZone,
    private router: Router,
    private annotationsComponent: AnnotationsComponent,
    private readonly formBuilder: FormBuilder,
    private idle: Idle,
    private datePipe: DatePipe,
    private cdRef: ChangeDetectorRef,
    private wakeLockService: WakeLockService
  ) {
  }
  toggleParticipants() {
    console.log("Toggling participants panel. Current state:", this.showParticipants);
    this.showParticipants = !this.showParticipants;
  }
  
  departmentChange() {
    this.selectedDepartmentId = '';
    this.selectedDepartmentExtId='';
    this.selectedOrgExtId = '';
    this.selectedOrgIsCourt = false;
    this.clearCaseDetails();
    this.applyCaseDetailsValidators();
    this.loadFormsTypeOptions('');
    this.selectedcourtLocationExtId = '';
    this.selectedcourtRoomExtId = '';
    this.selectedcourtRoomName = '';
    this.courtLocationfilteredOptions = this.clearDropdownValues();
    this.courtRoomfilteredOptions = this.clearDropdownValues();
    this.courtLocationControl.setValue(null);
    this.courtRoomControl.setValue(null);
    this.courtLocationControl.enable({ onlySelf: true });
    this.courtRoomControl.enable({ onlySelf: true });
  }

  onDepartmentInput(value: string): void {
    if (!value || !value.trim()) {
      this.selectedDepartmentId = '';
      this.selectedDepartmentExtId = '';
      this.selectedOrgExtId = '';
      this.departmentControl.setValue(null);
      this.departmentChange();
    }
  }
  async loadDeparmentValues() {
      this.departmentOptions = [];
      var departmentListDetails = await this.backendService.getDepartments().toPromise();
      let departmentList = this.backendService.getJSONResponse(departmentListDetails!['data'].departmentList);
      for (let department of departmentList) {
        //TODO: FD
        let data = {
          'id': department.id,
          'departmentName': department.name,
          'name': department.name +
            ' (' + department.organizationname + ')',
          'deptExtId': department.deptExtId,
          'orgExtId':department.orgExtId,
          'isFutureOrder': department.isFutureOrder,
          'isForeignLanguage': department.isForeignLanguage,
          'isForeignLanguageFirstDraft': department.isForeignLanguageFirstDraft,
          'isSensitive': department.isSensitive,
          'isCertification': department.isCertification,
          'isNotarization': department.isNotarization,
          'defaultForeignLanguageService': department.defaultForeignLanguageService,
          'defaultSourceForeignLanguage': department.defaultSourceForeignLanguage,
          'allowMultipleJobs': department.allowMultipleJobs,
          'isUploadProgressively': department.isUploadProgressively,
          'isRushVisible': department.isRushVisible
        }
        this.departmentOptions.push(data);
      }
      this.deptfilteredOptions = this.departmentControl.valueChanges
        .pipe(
          startWith(''),
          // When an option object is selected, keep dropdown unfiltered on next open.
          map(value => typeof value === 'string' ? value : ''),
          map(name => name ? this.departmentfilter(name) : this.departmentOptions.slice()),
        );
      if (this.departmentOptions.length == 1) {
        this.departmentControl.setValue(this.departmentOptions[0]);
        this.departmentControl.disable({ onlySelf: true });
        this.selectedDepartmentId = this.departmentOptions[0].id;
        this.selectedDepartmentExtId = this.departmentOptions[0].deptExtId;
         this.selectedOrgExtId = this.departmentOptions[0].orgExtId;
        await this.departmentSingleOptionChangeEvent(this.departmentOptions[0]);
      }
    }
    async departmentChangeEvent(event: any): Promise<void> {
    if (event.option.value.id) {
      this.spinner.show();
      this.selectedDepartmentId = event.option.value.id;
      this.selectedDepartmentExtId = event.option.value.deptExtId;
        this.clearCourtSelection();
        this.courtLocationfilteredOptions = this.clearDropdownValues();
        this.courtRoomfilteredOptions = this.clearDropdownValues();
      await this.organizationByOrgExtId(event.option.value.orgExtId);
      this.spinner.hide();
    }
  };
  
    async departmentSingleOptionChangeEvent(event: any): Promise<void> {
      console.log('Department:', event);
        if (event.id) {
          this.selectedDepartmentId = event.id;
          this.selectedDepartmentExtId = event.deptExtId;
          this.clearCourtSelection();
          this.courtLocationfilteredOptions = this.clearDropdownValues();
          this.courtRoomfilteredOptions = this.clearDropdownValues();
          await this.organizationByOrgExtId(event.orgExtId);
        }
      };
      private departmentfilter(name: any): dropdownmodel[] {
        const filterValue = name.toLowerCase();
        return this.departmentOptions.filter(option => option.name.toLowerCase().indexOf(filterValue) >= 0);
      }
  ngOnInit(): void {
    
    var isUserlogin = this.backendService.CheckIsUserLoggedIn();
     if (isUserlogin) {
      this.loadDeparmentValues();

     }
    this.organizationControl = new FormControl('');
    this.watchHearingDateTimeEdits();
    this.applyCaseDetailsValidators();
    //this.loadOrganizations();
    this.loadAvailableMicrophones();
    if (navigator.mediaDevices) {
      const mediaDevicesAny = navigator.mediaDevices as any;
      if (typeof mediaDevicesAny.addEventListener === 'function') {
        mediaDevicesAny.addEventListener('devicechange', this.mediaDeviceChangeHandler);
      } else {
        mediaDevicesAny.ondevicechange = this.mediaDeviceChangeHandler;
      }
    }
    // this.checkForPendingRecordings(); TODO With back end code changes
    // WASAPI check is performed on-demand when user switches to WASAPI mode.
    this.initializeLiveKit();

    // Initialize microphone filter
    setTimeout(() => {
      this.micFilteredOptions = this.microphoneControl.valueChanges.pipe(
        startWith(''),
        map(value => this._filterMicrophones(value))
      );
    }, 3000);


    // Initialize language filter
    this.langFilteredOptions = this.languageControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterLanguages(value))
    );

    // Set default language
    const defaultLang = this.availableLanguages.find(l => l.code === this.selectedLanguage);
    if (defaultLang) {
      this.languageControl.setValue(defaultLang);
    }
    // (window as any).onAnnotationDeleted = (data: any) => {
    //   console.log("Delete request received for:", data);
    // };
    // // Expose the function to the global window object of the parent
    // (window as any).getPlayerTime = () => {
    //   const audio = this.audioPlayerHLS.nativeElement;
    //   if (!audio) return "20:00:00";

    //   let totalSeconds = Math.floor(audio.currentTime);
    //   const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    //   const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    //   const s = (totalSeconds % 60).toString().padStart(2, '0');
    //   return "21:00:00";
    //   //return `${h}:${m}:${s}`;
    // };

    this.hearingDateTimeInterval$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (!this.isStartingRecording && !this.isRecording) {
          this.currentDateTime = new Date();
        }

        // Show a live clock as the default hearing time only until a case is selected or the
        // user picks a date; from then on that value must stay put (do not overwrite it).
        if (!this.isStartingRecording && !this.isRecording && !this.selectedCaseId && !this.hearingDateTimeEdited) {
          // emitEvent: false so the tick is not mistaken for a user edit; the view still updates.
          this.hearingDateTimeControl?.setValue(new Date(), { emitEvent: false });
        }
      });

    // Prevent in-app navigation while recording is active.
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        try {
          const targetUrl = event.url;
          const currentUrl = this.router.url;
          if ((this.isRecording || this.isStartingRecording || this.isPaused) && targetUrl !== currentUrl) {
            // Show a concise, meaningful message to the user
            this.sendNotification('Recording in progress — please stop recording before navigating away.', 'warning');
            //this.snackBar.open('Recording in progress — please stop recording before navigating away.', 'Close', { duration: 4000 });
            // Force navigation back to current route to prevent leaving (safe short delay)
            setTimeout(() => {
              this.router.navigateByUrl(currentUrl);
            }, 0);
          }
        } catch (err) {
          // ignore
        }
      }
    });
    const roleName = localStorage.getItem('user_roleName');
    const isNetScribeLiveOnly = localStorage.getItem('isNetScribeLiveOnly');
    if(roleName == 'CustomerAdmin' && isNetScribeLiveOnly == "true" ) {
      this.spinner.hide();
    }
    // The app does not need a browser unload hook here; navigation guard logic already blocks
    // route changes while a recording is active and avoids triggering Chrome's unload policy violation.
  }
  goToList() {
    if (this.isRecording || this.isStartingRecording || this.isPaused) {
      this.sendNotification('Recording in progress — please stop recording before navigating away.', 'warning');
      //this.snackBar.open('Recording in progress — stop recording before leaving this page.', 'Close', { duration: 3000 });
      return;
    }
    const route = '/admin/netscribeliveList';
    this.router.navigate([route]); // Correct way to navigate
  }

  // ===================== Case Management (Select Case) =====================
  async openNewCaseDialog(): Promise<void> {
    if (this.isCreatingNewCase) {
      return;
    }
    if (!(this.isRecording || this.isPaused) || !this.recordingId || !this.currentRoomName) {
      this.snackBar.open('The recording session is not ready yet.', 'Close', { duration: 3000 });
      return;
    }

    if (!this.selectedCaseDetailsId) {
      this.snackBar.open('The active case details are not ready yet.', 'Close', { duration: 3000 });
      return;
    }

    // Snapshot the click moment: the case switch (and its Stop/Start annotations) must be sent
    // right now, timestamped with this click, not deferred until the optional details popup closes.
    const transitionTime = new Date();
    const transitionAudioTime = this.getRecordingElapsedSeconds(transitionTime.getTime());
    const previousCaseNumber = this.currentCaseNumber || this.resolveCaseNumberValue();
    const previousCaseDetailsId = this.selectedCaseDetailsId;
    const generatedCaseNumber = this.generateDefaultCaseNumber(this.getDepartmentPrefix());
    const createPayload: CreateNewCaseRequest = {
      recordingSessionId: this.recordingId,
      roomName: this.currentRoomName,
      caseId: null,
      caseNumber: generatedCaseNumber,
      judgeName: null,
      formData: null,
      startedAt: transitionTime
    };

    const formValues = this.formsFormGroup ? this.formsFormGroup.getRawValue() : {};
    const popupUniqueField = this.formsFieldsList.find(field => field.unique);
    if (popupUniqueField) {
      formValues[popupUniqueField.key] = generatedCaseNumber;
    }

    const judgeFirst = this.judgeFirstNameControl?.value ? String(this.judgeFirstNameControl.value) : '';
    const judgeLast = this.judgeLastNameControl?.value ? String(this.judgeLastNameControl.value) : '';
    const data: NewCaseDialogData = {
      caseDetailsId: null,
      recordingSessionId: this.recordingId,
      roomName: this.currentRoomName,
      caseId: null,
      caseNumber: generatedCaseNumber,
      judgeName: (judgeFirst + ' ' + judgeLast).trim(),
      formFields: this.formsFieldsList || [],
      formValues,
      buildCaseFormValues: (caseData: any) => this.buildCaseFormFieldValues(caseData),
      selectedFormName: this.selectedFormType?.name || '',
      isCourt: this.selectedOrgIsCourt,
      orgExtId: this.selectedOrgExtId,
      courtRoom: this.selectedcourtRoomName,
      courtLocation: this.selectedcourtLocationName
    };
    const dialogRef = this.dialog.open(NewCaseDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      panelClass: 'new-case-dialog-panel',
      disableClose: true,
      closeOnNavigation: true,
      data
    });

    this.isCreatingNewCase = true;
    let response: any;
    try {
      response = await firstValueFrom(this.backendService.createNewCase(createPayload));
    } catch (error) {
      console.error('Unable to create the new case:', error);
      this.snackBar.open('Unable to create the new case. Please try again.', 'Close', { duration: 4000 });
      dialogRef.close();
      this.isCreatingNewCase = false;
      return;
    }

    const createdCase: any = this.unwrapApiData<any>(response);
    const caseDetailsId = createdCase?.caseDetails?.id ?? createdCase?.caseDetailsId ?? createdCase?.CaseDetailsId;
    if (!caseDetailsId) {
      this.snackBar.open('The new case was created without case details.', 'Close', { duration: 4000 });
      dialogRef.close();
      this.isCreatingNewCase = false;
      return;
    }

    // Switch the active case now, using the captured click time, so Stop/Start annotations go
    // out immediately instead of waiting on the optional details popup.
    this.setCurrentCaseDetailsId(caseDetailsId);
    const startCaseAnnotationPromise = (async (): Promise<Annotations | null> => {
      if (!this.selectedOrgIsCourt || !this.allowAnnotations) {
        return null;
      }
      try {
        await this.prepareModelandAddAnnotation(
          'System: Stop Case',
          'Case Stopped: ' + (previousCaseNumber || 'Unknown') + '.',
          transitionTime.toISOString(),
          previousCaseDetailsId,
          transitionAudioTime
        );
        return await this.prepareModelandAddAnnotation(
          'System: Start Case',
          'New Case Started: ' + (generatedCaseNumber || 'Unknown') + '.',
          transitionTime.toISOString(),
          caseDetailsId,
          transitionAudioTime
        );
      } catch (error) {
        console.error('Unable to save case transition annotations:', error);
        this.snackBar.open('Case created, but annotations could not be saved.', 'Close', { duration: 4000 });
        return null;
      }
    })();

    this.finishRecordingCase(previousCaseDetailsId, transitionTime);
    this.addRecordingCase(caseDetailsId, generatedCaseNumber, transitionTime, transitionTime);
    this.currentCaseNumber = generatedCaseNumber;

    // Visibly separate the transcript at the case boundary and restart speaker numbering at 1.
    this.resetSpeakerNumberingForNewCase();
    const caseMarker = this.addCaseSeparatorMarker(generatedCaseNumber, transitionTime);

    dialogRef.afterClosed().subscribe(async (result: NewCaseDialogResult | undefined) => {
      this.isCreatingNewCase = false;
      // The case switch and its Stop/Start annotations already fired on click above. The popup
      // values belong to the new case only: they are shown in the Cases table and must not be
      // written back onto the recording page's form fields (unique number included).
      if (!result) {
        return;
      }
      this.applyNewCaseSpeakers(result.formData ?? null);

      const updatedCaseNumber = result.caseNumber || '';
      if (updatedCaseNumber && updatedCaseNumber !== generatedCaseNumber) {
        this.updateRecordingCaseNumber(caseDetailsId, updatedCaseNumber);
        this.currentCaseNumber = updatedCaseNumber;
        // The placeholder case number was already published to the transcript banner and the
        // Start Case annotation, so both are rewritten with the number entered in the popup.
        caseMarker.caseLabel = updatedCaseNumber;
        this.syncTranscriptionSnapshots();
        this.setRecordingCaseFormData(caseDetailsId, result.formData ?? null);
        this.selectedCaseId = this.selectedOrgIsCourt ? result.caseId : null;
        await this.updateAnnotationComments(
          await startCaseAnnotationPromise,
          'New Case Started: ' + updatedCaseNumber + '.'
        );
        return;
      }
      this.setRecordingCaseFormData(caseDetailsId, result.formData ?? null);
      this.selectedCaseId = this.selectedOrgIsCourt ? result.caseId : null;
    });
    dialogRef.disableClose = false;
    dialogRef.componentInstance.setCreatedCaseDetailsId(Number(caseDetailsId));
  }

  // The popup's snapshot uses the same field keys as formsFieldsList, so it maps straight onto the
  // fields' referenceField roles. No form data means no people were entered for the new case.
  private applyNewCaseSpeakers(formDataJson: string | null): void {
    const values: Record<string, unknown> = {};
    for (const entry of parseFormDataSnapshot(formDataJson)) {
      values[entry.key] = entry.value;
    }
    this.activeCaseFormValues = values;
    this.refreshAnnotationSpeakers();
  }

  private setCurrentCaseDetailsId(caseDetailsId: number | null): void {
    this.selectedCaseDetailsId = caseDetailsId;
    this.annotationsComponent.setCurrentCaseDetailsId(caseDetailsId);
  }

  private addRecordingCase(
    id: number | null,
    caseNumber: string,
    startedAtValue: Date | string,
    hearingDateTimeValue: Date | string | null
  ): void {
    if (!id || this.recordingCaseRows.some(row => row.id === id)) {
      return;
    }

    const startedAt = new Date(startedAtValue);
    const hearingDateTime = hearingDateTimeValue ? new Date(hearingDateTimeValue) : null;
    this.recordingCaseClock = Date.now();
    this.recordingCaseRows = [...this.recordingCaseRows, {
      id,
      caseNumber: caseNumber || '-',
      hearingDateTime,
      startedAt,
      stoppedAt: null,
      startedPausedMs: this.getElapsedPausedMs(),
      stoppedPausedMs: null,
      formDataEntries: []
    }];
    setTimeout(() => {
      this.cdRef.detectChanges();
      const container = this.caseTableScrollContainer?.nativeElement;
      container?.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }, 0);
  }

  onTranscriptionScroll(): void {
    const container = this.scrollContainer?.nativeElement as HTMLElement | undefined;
    if (!container) {
      return;
    }
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom <= this.transcriptionBottomThreshold) {
      this.isTranscriptionScrolledUp = false;
    } else if (container.scrollTop < this.lastTranscriptionScrollTop) {
      // Only an upward scroll counts as the user leaving the live position;
      // smooth auto-scrolls to the bottom only ever move downward.
      this.isTranscriptionScrolledUp = true;
    }
    this.lastTranscriptionScrollTop = container.scrollTop;
  }

  goToCurrentTranscription(): void {
    this.isTranscriptionScrolledUp = false;
    this.scrollTranscriptionToBottom();
  }

  private scrollTranscriptionToBottom(): void {
    const container = this.scrollContainer?.nativeElement as HTMLElement | undefined;
    container?.scroll({ top: container.scrollHeight, behavior: 'smooth' });
  }

  onAnnotationPositionSelected(seconds: number): void {
    this.seekTo(seconds);
    this.scrollTranscriptionToTime(Number(seconds));
  }

  // Scrolls the transcript to the segment spoken at the given recording time (seconds).
  private scrollTranscriptionToTime(seconds: number): void {
    const container = this.scrollContainer?.nativeElement as HTMLElement | undefined;
    if (!container || !Number.isFinite(seconds)) {
      return;
    }

    let target: HTMLElement | null = null;
    let targetStart = -Infinity;
    let first: HTMLElement | null = null;
    for (const row of Array.from(container.querySelectorAll('.transcription-segment[data-start-time]')) as HTMLElement[]) {
      let start = Number(row.getAttribute('data-start-time'));
      if (!Number.isFinite(start)) {
        continue;
      }
      // Some segments carry an absolute epoch-ms start (see formatTimestamp); convert to elapsed seconds.
      if (start > 1e12 && this.recordingStartTime > 0) {
        start = (start - this.recordingStartTime) / 1000;
      }
      first = first || row;
      if (start <= seconds + 0.5 && start >= targetStart) {
        target = row;
        targetStart = start;
      }
    }

    target = target || first;
    if (!target) {
      return;
    }

    // Leave live auto-scroll paused so new text doesn't pull the view away; "Go to Current" resumes it.
    this.isTranscriptionScrolledUp = true;
    const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTo({ top: container.scrollTop + offset - 8, behavior: 'smooth' });
    const highlighted = target;
    highlighted.style.transition = 'background-color 0.4s ease';
    highlighted.style.backgroundColor = '#fff3cd';
    setTimeout(() => highlighted.style.backgroundColor = '', 2000);
  }

  private setRecordingCaseFormData(id: number | string, formDataJson: string | null): void {
    const entries = parseFormDataSnapshot(formDataJson);
    this.recordingCaseRows = this.recordingCaseRows.map(row =>
      row.id === id ? { ...row, formDataEntries: entries } : row
    );
  }

  getRecordingCaseFormSummary(row: { formDataEntries: FormDataEntry[] }): string {
    return (row.formDataEntries || [])
      .map(entry => entry.label + ': ' + this.formatFormDataValue(entry))
      .join(' | ');
  }

  formatFormDataValue(entry: FormDataEntry): string {
    if (entry.value === null || entry.value === undefined || entry.value === '') {
      return '-';
    }
    if (typeof entry.value === 'boolean') {
      return entry.value ? 'Yes' : 'No';
    }
    if (entry.type === FieldType.Date || entry.type === FieldType.DateTime) {
      const format = entry.type === FieldType.Date ? 'MMMM dd yyyy' : 'EEE MMMM dd yyyy HH:mm:ss';
      return this.datePipe.transform(entry.value as string, format) ?? String(entry.value);
    }
    return String(entry.value);
  }

  private finishRecordingCase(id: number | null, stoppedAt: Date): void {
    const stoppedPausedMs = this.getElapsedPausedMs();
    this.recordingCaseRows = this.recordingCaseRows.map(row =>
      row.id === id && !row.stoppedAt
        ? { ...row, stoppedAt, stoppedPausedMs }
        : row
    );
  }

  private updateRecordingCaseNumber(id: number | string, caseNumber: string): void {
    this.recordingCaseRows = this.recordingCaseRows.map(row =>
      row.id === id ? { ...row, caseNumber } : row
    );
  }

  // pausedDuration only grows on resume, so an in-progress pause is added on top of it.
  private getElapsedPausedMs(): number {
    return this.pausedDuration + (this.isPaused && this.pauseStartTime ? Date.now() - this.pauseStartTime : 0);
  }

  getRecordingCaseDuration(row: {
    startedAt: Date;
    stoppedAt: Date | null;
    startedPausedMs: number;
    stoppedPausedMs: number | null;
  }): string {
    // While paused both the clock and the pause total are frozen at the pause instant, so the
    // live row holds its value instead of drifting.
    const paused = this.isPaused && this.pauseStartTime > 0;
    const endTime = row.stoppedAt ? row.stoppedAt.getTime() : (paused ? this.pauseStartTime : this.recordingCaseClock);
    const endPausedMs = row.stoppedAt ? (row.stoppedPausedMs ?? row.startedPausedMs) : this.pausedDuration;
    const recordedMs = (endTime - row.startedAt.getTime()) - (endPausedMs - row.startedPausedMs);
    return this.formatDurationHms(recordedMs / 1000);
  }

  // Kept in HH:MM:SS to match recordingDuration (used for the currently active case row);
  // formatTime() elsewhere only returns MM:SS and must not be reused here.
  // Same calculation as updateRecordingClock, but for an arbitrary instant.
  private getRecordingElapsedSeconds(atMs: number): number {
    const clock = this.isPaused && this.pauseStartTime > 0 ? this.pauseStartTime : atMs;
    return Math.max(0, Math.floor((clock - this.recordingStartTime - this.pausedDuration) / 1000));
  }

  private formatDurationHms(totalSeconds: number): string {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  openCaseListDialog(): void {
    if (this.isRecording || this.isStartingRecording || this.isPaused) {
      this.snackBar.open('Cannot change the case while recording is in progress.', 'Close', { duration: 3000 });
      return;
    }
    // Cases (and their court rooms/locations) are scoped to an organisation, so an org must be
    // selected before a case can be chosen.
    if (!this.selectedOrgExtId) {
      this.snackBar.open('Please select an organization before selecting a case.', 'Close', { duration: 3000 });
      return;
    }
    // Case Management only applies to court organisations.
    if (!this.selectedOrgIsCourt) {
      this.snackBar.open('Case selection is only available for court organizations.', 'Close', { duration: 3000 });
      return;
    }
    const dialogRef = this.dialog.open(SelectCaseDialogComponent, {
      width: '1200px',
      maxWidth: '95vw',
      data: {
        courtRoom: this.selectedcourtRoomName,
        courtLocation: this.selectedcourtLocationName,
        orgExtId: this.selectedOrgExtId
      },
      disableClose: false,
      closeOnNavigation: true
    });
    // MatDialog's closeOnNavigation only reacts to browser back/forward, not to router link clicks,
    // so close the dialog explicitly on the next in-app navigation (e.g. going to the Case List).
    const navSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        dialogRef.close();
      }
    });
    dialogRef.afterClosed().subscribe((caseId: any) => {
      navSub.unsubscribe();
      if (caseId) {
        this.loadSelectedCase(caseId);
      }
    });
  }

  loadSelectedCase(caseId: any): void {
    this.spinner.show();
    this.backendService.getCaseById(caseId).subscribe((res: any) => {
      const c: any = (res && res.data) ? res.data : res;
      if (!c) { this.spinner.hide(); return; }
      this.selectedCaseId = c.id;
      this.caseProfileName = c.caseProfileName || '';
      this.caseNotes = c.caseNotes || '';
      this.selectedCasePeople = c.casePeople || [];
      this.casePeopleGroups = (c.casePeople || []).map((p: any) => ({
        role: p.role,
        names: (p.people || []).map((pp: any) => pp.name)
      }));
      // Populate the existing Case Details form controls from the selected case.
      this.caseNumberControl?.setValue(c.caseNo || '');
      // Take the case's hearing date EXACTLY as stored, because it also becomes the recording's
      // StartedAt on the backend. hearing_date is a 'timestamp without time zone', so it can carry
      // a time; splitting on 'T' used to discard it and force midnight. toDateValue keeps a bare
      // 'yyyy-MM-dd' on its own calendar day (no UTC off-by-one) and preserves any time present.
      // This write emits on purpose: that is what stops the live clock from overwriting it.
      const caseHearingDate = toDateValue(c.hearingDate);
      if (caseHearingDate) {
        this.hearingDateTimeControl?.setValue(caseHearingDate);
      }

      const judge = (c.casePeople || []).find((p: any) => p.role && p.role.toLowerCase().indexOf('judge') > -1);
      if (judge && judge.people && judge.people.length > 0 && judge.people[0].name) {
        const parts = judge.people[0].name.trim().split(/\s+/);
        this.judgeFirstNameControl?.setValue(parts[0] || '');
        this.judgeLastNameControl?.setValue(parts.length > 1 ? parts.slice(1).join(' ') : '');
      }
      this.formsFieldValues = this.buildCaseFormFieldValues(c);
      // app-dynamic-form patches these values in its ngOnChanges with emitEvent: false, so
      // valueChanges never reports them; rebuild once that change detection pass has run.
      setTimeout(() => this.refreshAnnotationSpeakers());
      this.spinner.hide();
      this.snackBar.open('Case selected successfully. You can change the case before starting the recording.', 'Close', { duration: 4000 });
    }, () => {
      this.spinner.hide();
      this.snackBar.open('Unable to load the selected case.', 'Close', { duration: 3000 });
    });
  }
  ngOnDestroy(): void {
    //this.cleanup();
    this.stopTimer();
    this.stopPausedForTimer();
    this.stopDbSaving();
    if (this.hlsRefreshTimer) {
      clearInterval(this.hlsRefreshTimer);
      this.hlsRefreshTimer = null;
    }
    if (this.hlsEngine) {
      this.hlsEngine.destroy();
      this.hlsEngine = null;
    }
    this.playbackChannels.close();
    // Detach handlers first so stopping doesn't trigger post-recording processing on a destroyed component.
    [this.mediaRecorder, this.mediaRecorderForLivePlayback].forEach(recorder => {
      if (recorder) {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      }
    });
    this.mediaRecorder = null;
    this.mediaRecorderForLivePlayback = null;
    [this.audioStream, this.internalStream, this.externalStream].forEach(stream => {
      stream?.getTracks().forEach(track => track.stop());
    });
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = '';
    }
    this.closeMixerAudioContext();
    this.cleanupChannelRouting();
    this.stopAudioAnalyzer();
    this.cleanupWasapiSubscriptions();
    this.cleanupLiveKit();
    this.annotationsComponent.closeAnnotationWindow();
    // Disconnect WASAPI service
    if (this.wasapiService && this.wasapiService.isConnected()) {
      this.wasapiService.disconnect();
    }
    if (navigator.mediaDevices) {
      const mediaDevicesAny = navigator.mediaDevices as any;
      if (typeof mediaDevicesAny.removeEventListener === 'function') {
        mediaDevicesAny.removeEventListener('devicechange', this.mediaDeviceChangeHandler);
      } else if (mediaDevicesAny.ondevicechange === this.mediaDeviceChangeHandler) {
        mediaDevicesAny.ondevicechange = null;
      }
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribe$.unsubscribe();
  }

   private generateDefaultCaseNumber(prefix: string = '', date: Date = new Date()): string {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
    return `${prefix}${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`;
  }

  /**
   * Fills the two case fields the app can derive on its own, right before validation runs.
   *
   * This is intended, not a gap: a recording always needs an identifier and a timestamp, so the
   * app supplies them rather than stopping the user. The consequence is that for a court org the
   * `required` rules on Case Number and Date & Time of Hearing can never actually fail - only the
   * judge names block a start. Do NOT "fix" that by removing the defaults; the auto-fill is the
   * required behavior and the validators exist to mark the fields in the UI.
   */
  private setDefaultCaseDetailsFormValues(): void {
    if (this.hearingDateTimeControl?.value == null) {
      this.hearingDateTimeControl?.setValue(new Date());
    }
    if (!this.selectedOrgIsCourt) {
          const uniqueFieldValue = this.resolveCaseNumberValue();
          if (uniqueFieldValue) {
            this.caseNumberControl?.setValue(uniqueFieldValue);
            return;
          }
        }

    if (this.caseNumberControl?.value != null && typeof this.caseNumberControl?.value.trim() === 'string' && this.caseNumberControl?.value.trim().length === 0) {
       this.caseNumberControl.setValue(this.generateDefaultCaseNumber(
        this.selectedOrgIsCourt ? '' : this.getDepartmentPrefix()));
    }
  }
  private getDepartmentPrefix(): string {
    const department = this.departmentControl?.value;
    const departmentName = department && typeof department === 'object'
      ? department.departmentName || department.name
      : department;
    const prefix = String(departmentName || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 3)
      .toUpperCase();

    return prefix.padEnd(3, 'D');
  }

  private getDepartmentPathSegment(): string {
    const department = this.departmentControl?.value;
    const orgExtId = department?.orgExtId || this.selectedOrgExtId;
    const deptExtId = department?.deptExtId || this.selectedDepartmentExtId;

    return [orgExtId, deptExtId].filter(Boolean).join('/');
  }

  private captureRecordingStartedAt(): void {
    this.currentDateTime = new Date();
    this.recordingStartedAt = this.currentDateTime.toISOString();
  }

  private loadFormsTypeOptions(deptExtId: string): void {
    // Recording forms are scoped to the selected organization, so reset any
    // previous selection whenever the org changes.
    this.selectedFormType = null;
    this.setFormsFields([]);
    if (!deptExtId) {
      this.formsTypeOptions = [];
      return;
    }
    this.backendService.getRecordingFormsByDept(deptExtId).subscribe((resp: any) => {
      const jsonData = this.backendService.getJSONResponse(resp);
      const formTypeList = jsonData?.data?.formTypeList
        ?? (jsonData?.body ? JSON.parse(jsonData.body)?.data?.formTypeList : []);
      this.formsTypeOptions = formTypeList ?? [];

      if (this.formsTypeOptions.length === 1) {
        this.onFormsTypeChange(this.formsTypeOptions[0]);
      }
    });
  }

  onFormsTypeChange(formType: any): void {
    this.selectedFormType = formType;
    this.setFormsFields([]);
    if (!formType) {
      return;
    }

    // The /formtypes/list items already carry the full fieldList JSON,
    // so build the form directly from the selected option.
    if (formType.fieldList) {
      this.setFormsFields(normalizeFields(formType.fieldList));
      this.cdRef.detectChanges();
      return;
    }

    // Fallback: fetch details when the list item has no inline fieldList.
    const formTypeId = formType.formTypeId ?? formType.id;
    if (formTypeId == null) {
      return;
    }

    this.backendService.getFormTypeDetails(formTypeId).subscribe({
      next: (resp: any) => {
        const rawFieldList = this.backendService.getJSONResponse(resp)?.data?.fieldList;
        this.setFormsFields(normalizeFields(rawFieldList));
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load form type details', err);
        this.sendNotification('Unable to load the selected form.', 'error');
      }
    });
  }

  // Mirrors the *ngIf of both children: the picker (shown from 2 options up) and the rendered
  // form. A single option needs no picker, so it only counts once its fields are built.
  get hasFormsContent(): boolean {
    return this.formsTypeOptions.length > 1 || this.formsFieldsList.length > 0;
  }

  onFormsFormReady(formGroup: FormGroup): void {
    this.formsFormGroup = formGroup;
    // A form rebuilt mid-session (e.g. form type reset) must come back already locked.
    if (this.isRecording || this.isPaused) {
      this.formsFormGroup.disable({ emitEvent: false });
    }

    this.formsFormGroup.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const uniqueCaseNumber = this.resolveCaseNumberValue();
        if (uniqueCaseNumber) {
          this.currentCaseNumber = uniqueCaseNumber;
        }
        this.refreshAnnotationSpeakers();
      });
    this.refreshAnnotationSpeakers();
  }

  // Speakers come from the form's reference-linked fields; see buildAnnotationSpeakers.
  private refreshAnnotationSpeakers(): void {
    const values = this.activeCaseFormValues ?? this.formsFormGroup?.getRawValue() ?? this.formsFieldValues ?? {};
    this.annotationSpeakers = buildAnnotationSpeakers(this.formsFieldsList, field => values[field.key]);
  }

  // A hearing date pulled in from a selected case must win over the live clock. Only non-clock
  // writes reach valueChanges, since the tick sets the control with emitEvent: false - so any
  // writer that means "stop the clock" must emit. Subscribed once: the control outlives the form.
  private watchHearingDateTimeEdits(): void {
    this.hearingDateTimeControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.hearingDateTimeEdited = true;
      });
  }

  // Goes through onFormsTypeChange because the dynamic form only rebuilds when the `fields`
  // reference changes; clearing the values in place would keep whatever the operator typed.
  private resetFormsForNewCase(): void {
    if (this.selectedFormType) {
      this.onFormsTypeChange(this.selectedFormType);
    }
  }

  // Single entry point for changing the rendered form. On reset the FormGroup is dropped so no
  // stale custom-field values from the previous form type leak into the next payload.
  private setFormsFields(fields: FieldDefinition[]): void {
    this.formsFieldsList = fields;
    if (!fields.length) {
      this.formsFormGroup = undefined as unknown as FormGroup;
    }
    this.refreshAnnotationSpeakers();
  }

  // Unique field -> the selected case's case number; ReferenceField -> either a direct property on
  // the case (e.g. HearingDate) or the first person's name under a matching casePeople role
  // (e.g. Judge, Public Defender, Court Interpreter).
  private buildCaseFormFieldValues(caseData: any): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    if (!caseData) {
      return values;
    }
    const casePeople: CaseRolePeopleModel[] = caseData.casePeople || [];

    for (const field of this.formsFieldsList) {
      if (field.unique) {
        values[field.key] = caseData.caseNo ?? '';
        continue;
      }

      const ref = field.referenceField?.trim();
      if (!ref) {
        continue;
      }

      const directKey = Object.keys(caseData).find(k => k.toLowerCase() === ref.toLowerCase());
      if (directKey) {
        values[field.key] = caseData[directKey];
        continue;
      }

      const person = casePeople.find(p => (p.role || '').trim().toLowerCase() === ref.toLowerCase());
      const name = person?.people?.[0]?.name;
      if (name) {
        values[field.key] = this.matchFieldOptionValue(field, name);
      }
    }

    return values;
  }
  
  private resolveCaseNumberValue(): string {
    const uniqueField = this.formsFieldsList.find(field => field.unique);
    if (uniqueField) {
      const rawValue = this.formsFormGroup?.getRawValue?.()[uniqueField.key];
      const fallbackValue = this.formsFieldValues?.[uniqueField.key];
      const candidate = rawValue ?? fallbackValue;
      if (candidate !== undefined && candidate !== null && String(candidate).trim() !== '') {
        return String(candidate).trim();
      }
    }

    return this.caseNumberControl?.value ? String(this.caseNumberControl.value).trim() : '';
  }
  // Dropdown fields must resolve to one of their own option values; other field types just take
  // the resolved case data as-is.
  private matchFieldOptionValue(field: FieldDefinition, name: string): unknown {
    if (field.type === FieldType.Dropdown && field.options?.length) {
      const match = field.options.find(o =>
        String(o.label).toLowerCase() === name.toLowerCase() || String(o.value).toLowerCase() === name.toLowerCase());
      if (match) {
        return match.value;
      }
    }
    return name;
  }

  private cleanupChannelRouting(): void {
    if (this.playbackSourceNode) {
      this.playbackSourceNode.disconnect();
      this.playbackSourceNode = null;
    }
    if (this.playbackSplitter) {
      this.playbackSplitter.disconnect();
      this.playbackSplitter = null;
    }
    if (this.playbackMerger) {
      this.playbackMerger.disconnect();
      this.playbackMerger = null;
    }
    this.playbackGainNodes.forEach(node => node.disconnect());
    this.playbackGainNodes = [];
    if (this.playbackAudioContext && this.playbackAudioContext.state !== 'closed') {
      this.playbackAudioContext.close();
      this.playbackAudioContext = null;
    }

    // Cleanup audio object URL
    if (this.currentAudioUrl) {
      URL.revokeObjectURL(this.currentAudioUrl);
      this.currentAudioUrl = '';
    }
  }

  // Initialize LiveKit Integration
  private initializeLiveKit(): void {
    try {
      // console.log('🔵 Initializing LiveKit integration...');

      // Subscribe to LiveKit connection status
      const statusSub = this.liveKitService.connectionStatus$.subscribe((status: LiveKitStatus) => {
        this.liveKitStatus = status;
        this.liveKitConnected = status.connected;
        this.serverConnected = status.connected;

        if (status.connected) {
          // console.log('✅ LiveKit connected:', status.roomName);
          //this.successMessage = `LiveKit connected: ${status.roomName}`;
          //setTimeout(() => this.successMessage = '', 3000);
        } else if (status.error) {
          console.error('❌ LiveKit error:', status.error);
          this.errorMessage = status.error;
          this.serverConnected = false;
        }
      });

      // Subscribe to LiveKit errors
      const errorSub = this.liveKitService.error$.subscribe((error: string) => {
        console.error('❌ LiveKit error:', error);
        this.errorMessage = error;
      });

      // Subscribe to raw data-channel messages from LiveKit.
      // ngZone.run() ensures updates always trigger change detection
      // even when the LiveKit SDK fires callbacks outside the Angular zone.
      const dataReceivedSub = this.liveKitService.dataReceived$.subscribe((message: LiveKitDataMessage) => {
        this.ngZone.run(() => {
          this.handleLiveKitDataReceived(message);
          if (!this.isTranscriptionScrolledUp) {
            setTimeout(() => this.scrollTranscriptionToBottom(), 0);
          }
        });
      });

      // Subscribe to native LiveKit transcription events when emitted by SDK/runtime
      // const nativeTranscriptionSub = this.liveKitService.nativeTranscription$.subscribe((event: LiveKitNativeTranscriptionEvent) => {
      //   this.handleLiveKitTranscriptionReceived(event);
      // });

      // Subscribe to room participants for UI display
      const participantsSub = this.liveKitService.participants$.subscribe((participants: LiveKitParticipant[]) => {
        // this.liveKitParticipants = participants;
        this.liveKitParticipants = participants.filter(
          p => !p.name.toLowerCase().includes('agent')
        );
      });

      this.liveKitSubscriptions.push(statusSub, errorSub, dataReceivedSub, participantsSub);

      // console.log('✅ LiveKit initialization complete');
    } catch (error) {
      console.error('❌ Error initializing LiveKit:', error);
      this.errorMessage = 'Failed to initialize LiveKit integration';
    }
  }

  // Cleanup LiveKit
  private closeMixerAudioContext(): void {
    this.channelMixer?.dispose();
    this.channelMixer = null;
    if (this.mixerAudioContext && this.mixerAudioContext.state !== 'closed') {
      void this.mixerAudioContext.close();
    }
    this.mixerAudioContext = null;
  }

  private async cleanupLiveKit(): Promise<void> {
    try {
      console.log('🧹 Cleaning up LiveKit...');

      // Unsubscribe from all subscriptions
      this.liveKitSubscriptions.forEach(sub => {
        if (sub && typeof sub.unsubscribe === 'function') {
          sub.unsubscribe();
        }
      });
      this.liveKitSubscriptions = [];

      // Disconnect from LiveKit
      if (this.liveKitService.isConnected()) {
        await this.liveKitService.disconnect();
      }

      console.log('✅ LiveKit cleanup complete');
    } catch (error) {
      console.error('❌ Error cleaning up LiveKit:', error);
    }
  }

  // Generate unique room name for recording session
  private generateRoomName(): string {
    const rawUserName = localStorage.getItem('userName') || 'host';
    const rawCourtRoom = this.selectedcourtRoomName || 'courtroom';

    const safeUserName = rawUserName
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'host';

    const safeCourtRoom = rawCourtRoom
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'courtroom';

    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 2147483647);
    const random = randomNum.toString(36).padStart(6, '0').slice(-6);

    return this.selectedOrgIsCourt
      ? `${safeCourtRoom}-${safeUserName}-${timestamp}-${random}`
      : `${safeUserName}-${timestamp}-${random}`;
  }

  // Create shareable link for room
  private generateShareableLink(roomName: string): string {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?room=${encodeURIComponent(roomName)}`;
  }

  // Fetch LiveKit token from backend API
  async fetchLiveKitToken(isHost: boolean = true): Promise<void> {
    try {
      console.log('🔑 Fetching LiveKit token from backend...');
      console.log('   Role:', isHost ? 'Host' : 'Participant');
      console.log('   Room:', this.liveKitConfig.roomName);

      // Replace with your actual backend API endpoint
      const response = await this.http.post<{ token: string, url?: string }>(
        '/api/livekit/token',
        {
          roomName: this.liveKitConfig.roomName,
          participantName: `${isHost ? 'host' : 'participant'}_${Date.now()}`,
          isHost: isHost // Optional: backend can use this for permissions
        }
      ).toPromise();

      if (response && response.token) {
        this.liveKitConfig.token = response.token;

        // Optionally update URL if backend provides it
        if (response.url) {
          this.liveKitConfig.url = response.url;
        }

        console.log('✅ LiveKit token obtained from backend');
      } else {
        throw new Error('No token received from backend');
      }
    } catch (error: any) {
      console.error('❌ Failed to fetch LiveKit token:', error);
      throw new Error(`Failed to get LiveKit token: ${error.message || error}`);
    }
  }

  private unwrapApiData<T>(response: any): T {
    return (response?.data || response) as T;
  }

  private async startLiveKitTranscriptionSession(): Promise<void> {
    const request: StartTranscriptionRequest = {
      roomName: this.currentRoomName,
      agentName: 'transcription-agent',
      engine: this.currentProvider || 'deepgram',
      language: this.selectedLanguage,
      enableDiarization: true,
      sessionId: this.activeRoomSid || this.currentSessionId,
      translateToEnglish: this.translateToEnglish
    };

    const response: any = await this.backendService.starttranscription(request).toPromise();
    const data = this.unwrapApiData<StartTranscriptionResponse>(response);

    this.dispatchId = data.dispatchId || '';
    this.currentSessionId = data.sessionId || this.currentSessionId;
    this.isTranscribing = true;
    // console.log('✅ LiveKit transcription started:', data);
  }

  private async stopLiveKitTranscriptionAndSession(): Promise<void> {
    if (!this.currentRoomName) {
      return;
    }

    try {
      const stopTranscriptionRequest: StopTranscriptionRequest = {
        roomName: this.currentRoomName,
        dispatchId: this.dispatchId || undefined,
        sessionId: this.currentSessionId || undefined
      };

      await this.backendService.stoptranscription(stopTranscriptionRequest).toPromise();
      console.log('✅ LiveKit transcription stopped', new Date());
    } catch (error) {
      console.warn('⚠️ stoptranscription failed:', error);
    }

    // if (this.currentRoomName) {
    //   try {
    //     const stopSessionRequest: StopStreamingSessionRequest = {
    //       roomName: this.currentRoomName,
    //       //egressId: this.egressId,
    //       deleteRoom: true,
    //       recordingDetails: JSON.stringify(this.recordingDetails)
    //     };
    //     console.log("stopSessionRequest:::", stopSessionRequest, new Date());
    //     const response = await this.backendService.stopstreamingsession(stopSessionRequest).toPromise();
    //     const data = this.unwrapApiData<StopTranscriptionResponse>(response);
    //     //this.recordingStopDateTime = data.stoppedAt || new Date().toISOString();
    //     this.prepareModelandAddAnnotation("System : Stop Recording", "Stop : " + this.currentCaseNumber + ".", this.recordingStopDateTime);
    //     console.log('✅ Streaming session stopped', new Date());
    //   } catch (error) {
    //     console.warn('⚠️ stopstreamingsession failed:', error);
    //   }
    // }

    this.dispatchId = '';
    this.activeRoomSid = '';
    this.egressId = '';
    //this.currentRoomName = '';
  }

  // Create and connect to new LiveKit room as host
  async createAndConnectRoom(): Promise<void> {
    try {
      const roomName = this.generateRoomName();
      var rawUserName = localStorage.getItem('userName');
      var rawUserId = localStorage.getItem('id');
      const departmentPath = this.getDepartmentPathSegment();
      const filePath = `AudioCapture/${departmentPath}/${roomName}/${roomName}`;
      const startedAt = this.recordingStartedAt || new Date().toISOString();
      const request: StartStreamingSessionRequest = {
        roomName,
        //maxParticipants: 10,
        emptyTimeoutSeconds: 3000,
        outputPath: filePath,
        hostIdentity: rawUserName || '',
        hostName: rawUserName || '',
        enableHlsStreaming: true,
        courtLocation: this.selectedcourtLocationName,
        courtRoom: this.selectedcourtRoomName,
        microphone: this.selectedMicrophoneName,
        recordingSource: RecordingSource.Web,
        sourceSystem: "NSC",
        language: this.selectedLanguage,
        translateToEnglish: this.translateToEnglish,
        audioChannels: this.detectedDeviceChannels,
        orgExtId: this.selectedOrgExtId,
        deptExtId: this.selectedDepartmentExtId,
        userId: Number(rawUserId),
        caseId: this.selectedCaseId ?? undefined,
        caseProfileName: this.caseProfileName,
        casePeople: JSON.stringify(this.selectedCasePeople || []),
        formData: this.buildFormDataPayload(),
        caseNumber: this.resolveCaseNumberValue() || undefined,
        hearingDateTime: this.resolveHearingDateTime(startedAt).toISOString(),
        startedAt,
        judgeFirstName: this.judgeFirstNameControl?.value ? String(this.judgeFirstNameControl.value) : undefined,
        judgeLastName: this.judgeLastNameControl?.value ? String(this.judgeLastNameControl.value) : undefined
      };
      // Guard reads so an empty Case Details field yields '' instead of the strings "null"/"undefined".
       this.currentCaseNumber = this.resolveCaseNumberValue();
      const judgeFirst = this.judgeFirstNameControl?.value ? String(this.judgeFirstNameControl.value) : '';
      const judgeLast = this.judgeLastNameControl?.value ? String(this.judgeLastNameControl.value) : '';
      this.judgeName = (judgeFirst + ' ' + judgeLast).trim();
      this.hearingDate = String(request.hearingDateTime);
      //console.log("startstreamingsession request:::", request);
      const response: any = await this.backendService.startstreamingsession(request).toPromise();
      //console.log("startstreamingsession response:::", response);
      const data = this.unwrapApiData<StartStreamingSessionResponse>(response);

      this.currentRoomName = data.roomName || roomName;
      this.activeRoomSid = data.roomSid || '';
      this.currentSessionId = data.roomSid || data.roomName || roomName;
      this.egressId = data.egressId || '';
      this.recordingId = data.recordingSessionId || 0;
      const sessionStartedAt = data.createdAt || this.recordingStartedAt || new Date().toISOString();
      this.recordingStartDateTime = sessionStartedAt;
      this.recordingDetails.AudioStart = sessionStartedAt;
      this.selectedCaseDetailsId = data.caseDetailsId ?? (data as any).CaseDetailsId ?? null;
      this.recordingCaseRows = [];
      this.liveKitConfig = {
        url: data.serverUrl,
        token: data.hostToken,
        roomName: this.currentRoomName,
        participantName: rawUserName || '',
        participantIdentity: rawUserId || '',
        role: "Publisher"
      };

      this.isLoading = true;
      await this.liveKitService.connect(this.liveKitConfig);
      this.isLoading = false;

      // console.log('✅ Connected to room:', this.currentRoomName);
      if (this.autoStartTranscription) {
        await this.startLiveKitTranscriptionSession();
      }

    } catch (err) {
      this.isLoading = false;
      console.error('❌ createAndConnectRoom failed', err);
      throw err;
    }
  }

  // Connect to LiveKit server (for manual connection or joining)
  async connectToLiveKit(): Promise<void> {
    try {
      if (!this.liveKitConfig.url) {
        throw new Error('LiveKit URL is required');
      }

      // Auto-fetch token from backend if not provided
      if (!this.liveKitConfig.token) {
        console.log('🔑 No token provided, fetching from backend...');
        await this.fetchLiveKitToken(this.isHost);
      }

      // console.log('🔵 Connecting to LiveKit server...');
      this.isLoading = true;

      await this.liveKitService.connect(this.liveKitConfig);

      // If connecting as host and no room link yet, generate it
      if (this.isHost && !this.shareableRoomLink && this.liveKitConfig.roomName) {
        this.currentRoomName = this.liveKitConfig.roomName;
        this.shareableRoomLink = this.generateShareableLink(this.currentRoomName);
      }

      this.isLoading = false;
      this.snackBar.open('✅ Connected to Server', 'Close', { duration: 3000 });

    } catch (error: any) {
      this.isLoading = false;
      console.error('❌ Failed to connect to LiveKit:', error);
      this.snackBar.open(`Failed to connect to LiveKit: ${error.message}`, 'Close', { duration: 5000 });
      throw error;
    }
  }

  // Copy shareable link to clipboard
  copyRoomLink(): void {
    if (!this.shareableRoomLink) {
      this.snackBar.open('No room link available', 'Close', { duration: 2000 });
      return;
    }

    navigator.clipboard.writeText(this.shareableRoomLink)
      .then(() => {
        this.snackBar.open('📋 Link copied to clipboard!', 'Close', { duration: 2000 });
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        this.snackBar.open('Failed to copy link', 'Close', { duration: 2000 });
      });
  }

  // Disconnect from LiveKit
  async disconnectFromLiveKit(): Promise<void> {
    try {
      // console.log('🔴 Disconnecting from LiveKit...');
      await this.liveKitService.disconnect();
      // this.snackBar.open('Disconnected from LiveKit', 'Close', { duration: 2000 });
    } catch (error: any) {
      console.error('❌ Error disconnecting from LiveKit:', error);
      this.snackBar.open(`Error disconnecting: ${error.message}`, 'Close', { duration: 3000 });
    }
  }

  // Toggle LiveKit streaming
  toggleLiveKit(): void {
    this.useLiveKit = !this.useLiveKit;

    if (this.useLiveKit) {
      console.log('🟢 LiveKit streaming enabled');
      this.snackBar.open('LiveKit streaming enabled', 'Close', { duration: 2000 });

      // If not already connected and being used as host
      // Room will be created when recording starts
      if (!this.liveKitConnected && this.isHost) {
        console.log('📌 Room will be created when recording starts');
      }
    } else {
      console.log('🔴 LiveKit streaming disabled');
      this.snackBar.open('LiveKit streaming disabled', 'Close', { duration: 2000 });
    }
  }

  private addTranscriptionSegment(segment: IncomingTranscriptionSegment): void {
    const text = this.normalizeTranscriptText(segment.text);
    if (!text) {
      return;
    }

    const speaker = this.normalizeTranscriptText(segment.speaker) || 'Speaker';
    const speakerNumber = this.extractSpeakerNumber(speaker);
    const speakerKey = speakerNumber > 0 ? `speaker:${speakerNumber}` : `name:${speaker.toLowerCase()}`;
    const startTime = this.normalizeNumericValue(segment.startTime, 0);
    const endTime = this.normalizeNumericValue(segment.endTime, startTime);
    const timestamp = this.normalizeTimestamp(segment.timestamp);
    const translatedText = this.normalizeTranscriptText(segment.translatedText);

    if (!segment.isFinal) {
      this.interimTranscriptionSegment = {
        speaker,
        speakerNumber,
        speakerKey,
        text,
        translatedText,
        translationTarget: segment.translationTarget,
        language: segment.language,
        timestamp,
        startTime,
        endTime,
        trackId: segment.trackId
      };
      this.syncTranscriptionSnapshots();
      return;
    }

    this.interimTranscriptionSegment = null;

    const lastSegment = this.finalTranscriptionSegments.length
      ? this.finalTranscriptionSegments[this.finalTranscriptionSegments.length - 1]
      : null;

    // Merge into the existing block when the same speaker speaks consecutively.
    // sameSpeaker === false means the server explicitly signals a new utterance block,
    // so respect that and start a new segment even for the same speaker.
    const isSameSpeakerContinuation = this.lastSpeakerKey === speakerKey && segment.sameSpeaker !== false;

    if (lastSegment && isSameSpeakerContinuation) {
      lastSegment.text = this.mergeTranscriptText(lastSegment.text, text);
      // Only update translatedText when incoming has content so we don't wipe an existing translation.
      if (translatedText) {
        lastSegment.translatedText = this.mergeTranscriptText(lastSegment.translatedText, translatedText);
      }
      lastSegment.translationTarget = segment.translationTarget || lastSegment.translationTarget;
      lastSegment.language = segment.language || lastSegment.language;
      lastSegment.endTime = Math.max(lastSegment.endTime, endTime);
      lastSegment.timestamp = timestamp;
      lastSegment.trackId = segment.trackId || lastSegment.trackId;
      this.currentSpeakerEndTime = lastSegment.endTime;
    } else {
      const finalSegment: TranscriptionViewSegment = {
        speaker,
        speakerNumber,
        speakerKey,
        text,
        translatedText,
        translationTarget: segment.translationTarget,
        language: segment.language,
        timestamp,
        startTime,
        endTime,
        trackId: segment.trackId
      };

      this.finalTranscriptionSegments.push(finalSegment);
      this.lastSpeaker = speakerNumber;
      this.lastSpeakerKey = speakerKey;
      this.currentSpeakerStartTime = startTime;
      this.currentSpeakerEndTime = endTime;
    }

    this.syncTranscriptionSnapshots();
  }

  private normalizeTranscriptText(value: any): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private mergeTranscriptText(existingText: string, incomingText: string): string {
    const existing = this.normalizeTranscriptText(existingText);
    const incoming = this.normalizeTranscriptText(incomingText);

    if (!existing) {
      return incoming;
    }

    if (!incoming) {
      return existing;
    }

    if (existing === incoming) {
      return existing;
    }

    // Avoid repeated content when final chunks resend partial/full text.
    if (incoming.startsWith(existing) || incoming.includes(existing)) {
      return incoming;
    }

    if (existing.startsWith(incoming) || existing.includes(incoming)) {
      return existing;
    }

    return `${existing} ${incoming}`.trim();
  }

  private extractSpeakerNumber(speaker: string): number {
    const match = (speaker || '').match(/(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  private normalizeNumericValue(value: any, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private normalizeTimestamp(value: any): number {
    if (typeof value === 'string') {
      const parsedDate = Date.parse(value);
      if (!Number.isNaN(parsedDate)) {
        return parsedDate;
      }
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return Date.now();
    }

    return parsed < 100000000000 ? parsed * 1000 : parsed;
  }

  hasSegmentTranslation(segment: TranscriptionViewSegment): boolean {
    if (!this.translateToEnglish) {
      return false;
    }

    const source = this.normalizeTranscriptText(segment?.text);
    const translated = this.normalizeTranscriptText(segment?.translatedText);
    return !!translated && translated !== source;
  }

  private buildSegmentBodyText(segment: TranscriptionViewSegment): string {
    if (segment.isCaseMarker) {
      return '';
    }
    if (this.hasSegmentTranslation(segment)) {
      return `(source): ${segment.text}\n(translated): ${segment.translatedText}`;
    }
    return segment.text;
  }

  private buildSegmentHeaderText(segment: TranscriptionViewSegment, interim: boolean = false): string {
    if (segment.isCaseMarker) {
      return `----- New Case Started: ${segment.caseLabel || 'Unknown'} (${this.formatTimestamp(segment.startTime)}) -----`;
    }
    const start = this.formatTimestamp(segment.startTime);
    const end = this.formatTimestamp(segment.endTime);
    const timestampText = this.showTimestamps ? `[${start} - ${end}] ` : '';
    return `${timestampText}🎙️ ${segment.speaker}${interim ? ' (interim)' : ''}:`;
  }

  private buildTranscriptSignature(transcript: TranscriptResponse, trackId?: string): string {
    const speaker = this.normalizeTranscriptText(
      transcript.speaker || transcript.participant_identity || transcript.participant || ''
    ).toLowerCase();
    const text = this.normalizeTranscriptText(transcript.text).toLowerCase();
    const translated = this.normalizeTranscriptText(transcript.translated_text).toLowerCase();
    const words = transcript.words || [];
    const start = words.length ? this.normalizeNumericValue(words[0].start, 0).toFixed(2) : '0.00';
    const end = words.length ? this.normalizeNumericValue(words[words.length - 1].end, 0).toFixed(2) : '0.00';
    return [
      transcript.is_final ? 'f' : 'i',
      speaker,
      text,
      translated,
      start,
      end
    ].join('|');
  }

  private shouldSkipDuplicateTranscript(transcript: TranscriptResponse, trackId?: string): boolean {
    const now = Date.now();
    const signature = this.buildTranscriptSignature(transcript, trackId);
    const lastSeen = this.recentTranscriptSignatures.get(signature);

    // Keep map bounded and drop stale signatures.
    if (this.recentTranscriptSignatures.size > 400) {
      this.recentTranscriptSignatures.forEach((seenAt, key) => {
        if (now - seenAt > 10000) {
          this.recentTranscriptSignatures.delete(key);
        }
      });
    }

    if (lastSeen && (now - lastSeen) < 8000) {
      return true;
    }

    this.recentTranscriptSignatures.set(signature, now);
    return false;
  }

  private processTranscriptResponse(transcript: TranscriptResponse, trackId?: string): void {
    const text = this.normalizeTranscriptText(transcript?.text);
    if (!text) {
      return;
    }

    // if (this.shouldSkipDuplicateTranscript(transcript, trackId)) {
    //   return;
    // }

    // Only finals are persisted/uploaded; interim display is driven by processTranscriptResponses.
    if (transcript.is_final) {
      this.transcriptResponses.push(transcript);
    }
    this.processTranscriptResponses([transcript]);
  }

  // ─── process Transcript Responses─────────────────────────────────
  private processTranscriptResponses(transcripts: TranscriptResponse[]): void {
    for (const transcript of transcripts) {
      const originalText = this.normalizeTranscriptText(transcript.text);
      if (!originalText) {
        continue;
      }

      const translatedText = this.normalizeTranscriptText(transcript.translated_text);
      const hasTranslation = !!translatedText && translatedText !== originalText;
      const englishText = hasTranslation ? translatedText : originalText;

      const start = this.resolveTranscriptStartSeconds(transcript);
      const end = this.resolveTranscriptEndSeconds(transcript, start);
      const speakerKey = this.resolveSpeakerKey(transcript);
      const speakerLabel = this.resolveCaseScopedSpeakerLabel(speakerKey, this.resolveSpeakerLabel(speakerKey));
      const speakerNumber = this.extractSpeakerNumber(speakerLabel);
      // transcript.timestamp is already elapsed ms (normalized in toTranscriptResponse).
      // Add to recordingStartTime to get an absolute epoch ms.
      const timestamp = transcript.timestamp;
      if (transcript.is_final) {
        // Clear interim when a final arrives
        this.interimTranscriptionSegment = null;
        this.lkInterimSegmentText = '';
        this.lkInterimTranslatedSegmentText = '';

        const sameSpeaker =
          transcript.same_speaker !== false &&
          this.lkLastFinalSpeakerKey === speakerKey &&
          !!this.lkLastFinalSpeakerKey;

        // console.log(
        //   `[ProcessTranscripts] same_speaker=${transcript.same_speaker}, lastSpeaker=${this.lkLastFinalSpeakerKey}, current=${speakerKey}, result=${sameSpeaker}`
        // );

        if (sameSpeaker) {
          // ── Merge with the last segment ──────────────────────────────────
          this.lkLastFinalText = this.mergeTranscriptText(this.lkLastFinalText, originalText);
          this.lkLastFinalEndSeconds = Math.max(this.lkLastFinalEndSeconds, end);

          if (hasTranslation) {
            this.lkLastFinalTranslatedText = this.mergeTranscriptText(this.lkLastFinalTranslatedText, englishText);
          }

          const lastSeg = this.finalTranscriptionSegments.length
            ? this.finalTranscriptionSegments[this.finalTranscriptionSegments.length - 1]
            : null;
          if (lastSeg) {
            lastSeg.text = this.lkLastFinalText;
            lastSeg.endTime = this.lkLastFinalEndSeconds;
            lastSeg.timestamp = timestamp;
            if (hasTranslation) {
              lastSeg.translatedText = this.lkLastFinalTranslatedText;
            }
          }
        } else {
          // ── New speaker block ─────────────────────────────────────────────
          this.lkLastFinalSpeakerKey = speakerKey;
          this.lkLastFinalStartSeconds = start;
          this.lkLastFinalEndSeconds = end;
          this.lkLastFinalText = originalText;
          this.lkLastFinalTranslatedText = hasTranslation ? englishText : '';

          const newSeg: TranscriptionViewSegment = {
            speaker: speakerLabel,
            speakerNumber,
            speakerKey,
            text: originalText,
            translatedText: hasTranslation ? englishText : '',
            translationTarget: transcript.translation_target,
            language: transcript.language,
            timestamp,
            startTime: start,
            endTime: end
          };
          this.finalTranscriptionSegments.push(newSeg);

          // Keep legacy tracking in sync
          this.lastSpeakerKey = speakerKey;
          this.lastSpeaker = speakerNumber;
          this.currentSpeakerStartTime = start;
          this.currentSpeakerEndTime = end;
        }

        this.syncTranscriptionSnapshots();

      } else {
        // ── Interim segment ───────────────────────────────────────────────
        this.lkInterimSegmentText = originalText;
        this.lkInterimTranslatedSegmentText = hasTranslation ? englishText : '';

        this.interimTranscriptionSegment = {
          speaker: speakerLabel,
          speakerNumber,
          speakerKey,
          text: originalText,
          translatedText: hasTranslation ? englishText : '',
          translationTarget: transcript.translation_target,
          language: transcript.language,
          timestamp,
          startTime: start,
          endTime: end
        };

        this.syncTranscriptionSnapshots(false);
      }
    }
  }

  // ─── Speaker resolution helpers (mirrors C# resolveSpeakerKey / resolveSpeakerLabel) ──

  private resolveSpeakerKey(transcript: TranscriptResponse): string {
    const raw = this.normalizeTranscriptText(
      transcript.speaker ||
      transcript.participant_identity ||
      transcript.participant ||
      'Speaker'
    );
    // Normalise to "speaker:N" when name contains a number, else use lowercase name
    const num = this.extractSpeakerNumber(raw);
    return num > 0 ? `speaker:${num}` : `name:${raw.toLowerCase()}`;
  }

  private resolveSpeakerLabel(speakerKey: string): string {
    if (speakerKey.startsWith('speaker:')) {
      const num = speakerKey.replace('speaker:', '');
      return `Speaker ${num}`;
    }
    if (speakerKey.startsWith('name:')) {
      const name = speakerKey.replace('name:', '');
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return 'Speaker';
  }

  // Numbered speakers restart from "Speaker 1" within each case; named speakers are left as-is.
  private resolveCaseScopedSpeakerLabel(speakerKey: string, fallbackLabel: string): string {
    if (!speakerKey.startsWith('speaker:')) {
      return fallbackLabel;
    }
    let mappedNumber = this.caseSpeakerKeyMap.get(speakerKey);
    if (!mappedNumber) {
      mappedNumber = ++this.caseSpeakerCounter;
      this.caseSpeakerKeyMap.set(speakerKey, mappedNumber);
    }
    return `Speaker ${mappedNumber}`;
  }

  private resetSpeakerNumberingForNewCase(): void {
    this.caseSpeakerKeyMap.clear();
    this.caseSpeakerCounter = 0;
    // Force the next transcript to start a new block instead of merging into the previous case's speaker.
    this.lastSpeakerKey = '';
    this.lkLastFinalSpeakerKey = '';
  }

  // Inserted immediately when a new case starts so the transcript panel visibly separates it
  // from what came before, timestamped with the same click time used for the case switch.
  private addCaseSeparatorMarker(caseLabel: string, at: Date): TranscriptionViewSegment {
    const elapsedSeconds = this.recordingStartTime > 0 ? (at.getTime() - this.recordingStartTime) / 1000 : 0;
    const marker: TranscriptionViewSegment = {
      speaker: '',
      speakerNumber: 0,
      speakerKey: 'case-marker',
      text: '',
      translatedText: '',
      timestamp: at.getTime(),
      startTime: elapsedSeconds,
      endTime: elapsedSeconds,
      isCaseMarker: true,
      caseLabel
    };
    this.finalTranscriptionSegments.push(marker);
    this.interimTranscriptionSegment = null;
    this.syncTranscriptionSnapshots();
    return marker;
  }

  // ─── Timestamp resolvers (mirrors C# ResolveTranscriptStartSeconds / End) ───

  private resolveTranscriptStartSeconds(transcript: TranscriptResponse): number {
    const words = transcript.words || [];

    // 1. min(words.start > 0) — raw elapsed seconds from word timings
    const validStarts = words.map(w => w.start).filter(s => s > 0);
    if (validStarts.length) {
      return Math.min(...validStarts);
    }

    // 2. transcript.timestamp is stored as epoch ms by normalizeTimestamp.
    //    Divide by 1000 to get seconds so formatTimestamp can do:
    //      recordingStartTime + seconds * 1000
    //    If it's a large absolute epoch ms (> 1e11) pass it through directly.
    if (transcript.timestamp > 0) {
      return transcript.timestamp > 1e11 ? transcript.timestamp : transcript.timestamp / 1000;
    }

    return 0;
  }

  private resolveTranscriptEndSeconds(transcript: TranscriptResponse, start: number): number {
    const words = transcript.words || [];

    // 1. max(words.end > 0)
    const validEnds = words.map(w => w.end).filter(e => e > 0);
    if (validEnds.length) {
      return Math.max(...validEnds);
    }

    // 2. end_time / endTime field as-is
    const rawEnd = (transcript as any).end_time ?? (transcript as any).endTime;
    if (rawEnd > 0) {
      return rawEnd;
    }

    // 3. start + duration
    if (transcript.duration > 0) {
      return start + transcript.duration;
    }

    return start;
  }

  /**
   * Parses a timestamp that may be:
   *  - number (elapsed seconds)
   *  - "HH:mm:ss" string (C# hh\:mm\:ss elapsed)
   *  - "yyyy-MM-dd HH:mm:ss" string (C# absolute FormatTimestamp)
   *  - ISO date string
   */
  private parseTranscriptTimestampToSeconds(value: any): number {
    if (value === null || value === undefined) {
      return 0;
    }

    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      // epoch ms → seconds; already seconds → pass through
      return value > 1e11 ? value / 1000 : value;
    }

    if (typeof value === 'string' && value.trim()) {
      const s = value.trim();

      // "HH:mm:ss" elapsed
      const elapsed = s.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
      if (elapsed) {
        return parseInt(elapsed[1], 10) * 3600 + parseInt(elapsed[2], 10) * 60 + parseInt(elapsed[3], 10);
      }

      // "yyyy-MM-dd HH:mm:ss" or ISO with time component
      const absolute = s.match(/\b(\d{2}):(\d{2}):(\d{2})\b/);
      if (absolute) {
        return parseInt(absolute[1], 10) * 3600 + parseInt(absolute[2], 10) * 60 + parseInt(absolute[3], 10);
      }

      // Plain number string
      const num = Number(s);
      if (Number.isFinite(num) && num > 0) {
        return num > 1e11 ? num / 1000 : num;
      }
    }

    return 0;
  }

  private processTranscriptBatch(
    items: any[],
    participantIdentity?: string,
    trackId?: string,
    forceFinal: boolean = false
  ): void {
    if (!Array.isArray(items) || !items.length) {
      return;
    }

    items.forEach((item: any) => {
      const batchItem = this.normalizeTranscriptBatchItem(item);
      const normalizedItem = forceFinal
        ? { ...batchItem, is_final: batchItem?.is_final !== false }
        : batchItem;
      const transcript = this.toTranscriptResponse(normalizedItem, participantIdentity);
      this.processTranscriptResponse(transcript, trackId);
    });
  }

  private normalizeBooleanFlag(value: any): boolean {
    if (value === true) {
      return true;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'final';
    }

    return false;
  }

  private extractSpeakerIdentity(data: any, participantIdentity?: string): string {
    const speaker = this.normalizeTranscriptText(
      data?.speaker ||
      data?.speaker_name ||
      data?.speakerName ||
      data?.participant_identity ||
      data?.participantIdentity ||
      data?.participant ||
      data?.participant_name ||
      data?.participantName ||
      data?.identity ||
      participantIdentity
    );

    return speaker || 'Speaker';
  }

  private extractTranslatedText(data: any): string {
    const translated = this.normalizeTranscriptText(
      data?.translated_text ||
      data?.translatedText ||
      data?.translation ||
      data?.translated ||
      data?.target_text ||
      data?.targetText ||
      data?.english_text ||
      data?.englishText
    );

    if (translated) {
      return translated;
    }

    if (Array.isArray(data?.translations)) {
      for (const item of data.translations) {
        const text = this.normalizeTranscriptText(
          item?.text || item?.translated_text || item?.translatedText || item?.translation
        );
        if (text) {
          return text;
        }
      }
    }

    if (data?.translations && typeof data.translations === 'object') {
      const prioritizedKeys = ['en', 'en-US', 'english', 'translated_text', 'text'];
      for (const key of prioritizedKeys) {
        const text = this.normalizeTranscriptText(data.translations[key]);
        if (text) {
          return text;
        }
      }

      for (const key of Object.keys(data.translations)) {
        const text = this.normalizeTranscriptText(data.translations[key]);
        if (text) {
          return text;
        }
      }
    }

    return '';
  }

  private normalizeTranscriptBatchItem(item: any): any {
    if (!item || typeof item !== 'object') {
      return item;
    }

    if (Array.isArray(item?.alternatives) && item.alternatives.length > 0) {
      const primary = item.alternatives[0] || {};
      return {
        ...item,
        ...primary,
        speaker:
          item?.speaker ||
          primary?.speaker ||
          item?.participant_identity ||
          item?.participantIdentity ||
          item?.participant ||
          primary?.participant_identity ||
          primary?.participantIdentity ||
          primary?.participant,
        participant_identity:
          item?.participant_identity ||
          item?.participantIdentity ||
          item?.participant ||
          primary?.participant_identity ||
          primary?.participantIdentity ||
          primary?.participant,
        translated_text:
          item?.translated_text ||
          item?.translatedText ||
          item?.translation ||
          primary?.translated_text ||
          primary?.translatedText ||
          primary?.translation
      };
    }

    return item;
  }

  private formatDateTime(value: number): string {
    const timestamp = this.normalizeTimestamp(value);
    return new Date(timestamp).toLocaleString();
  }

  private formatDateTimeRange(segment: TranscriptionViewSegment): string {
    const startMs = this.normalizeTimestamp(segment?.timestamp);
    const startText = this.formatDateTime(startMs);

    const segmentDurationSeconds = Math.max(
      this.normalizeNumericValue(segment?.endTime, 0) - this.normalizeNumericValue(segment?.startTime, 0),
      0
    );
    const endMs = startMs + Math.round(segmentDurationSeconds * 1000);
    const endText = this.formatDateTime(endMs);

    return `${startText} - ${endText}`;
  }

  private syncTranscriptionSnapshots(finalsChanged: boolean = true): void {
    this.transcriptionVersion++;
    if (finalsChanged) {
      this.fullTranscript = this.finalTranscriptionSegments
        .map(seg => `${this.buildSegmentHeaderText(seg)}\n${this.buildSegmentBodyText(seg)}`)
        .join('\n\n');
      this.transcriptionText = this.fullTranscript;
      this.updateTranscriptionStats();
    }

    if (this.interimTranscriptionSegment) {
      const interimHeader = this.buildSegmentHeaderText(this.interimTranscriptionSegment, true);
      const interimBody = this.buildSegmentBodyText(this.interimTranscriptionSegment);
      this.currentInterimText = `${interimHeader}\n${interimBody}`;
      this.interimTranscription = this.currentInterimText;
    } else {
      this.currentInterimText = '';
      this.interimTranscription = '';
    }
  }

  private toTranscriptResponse(data: any, participantIdentity?: string): TranscriptResponse {
    const speakerIdentity = this.extractSpeakerIdentity(data, participantIdentity);
    const words = Array.isArray(data?.words)
      ? data.words
        .map((w: any) => ({
          word: this.normalizeTranscriptText(w?.word),
          start: this.normalizeNumericValue(w?.start, 0),
          end: this.normalizeNumericValue(w?.end, 0),
          confidence: Number.isFinite(Number(w?.confidence)) ? Number(w?.confidence) : undefined
        }))
        .filter((w: TranscriptWord) => !!w.word)
      : [];

    const normalizedTimestamp = this.normalizeTimestamp(data?.timestamp || data?.created_at || Date.now());
    const start = this.normalizeNumericValue(data?.start_time ?? data?.startTime, words.length ? words[0].start : 0);
    const end = this.normalizeNumericValue(
      data?.end_time ?? data?.endTime,
      words.length ? words[words.length - 1].end : start
    );

    return {
      type: this.normalizeTranscriptText(data?.type) || 'transcript',
      participant: this.normalizeTranscriptText(data?.participant),
      participant_identity:
        this.normalizeTranscriptText(data?.participant_identity) ||
        this.normalizeTranscriptText(data?.participantIdentity) ||
        this.normalizeTranscriptText(data?.participant) ||
        this.normalizeTranscriptText(participantIdentity) ||
        'Speaker',
      same_speaker: (data?.same_speaker !== undefined || data?.sameSpeaker !== undefined)
        ? (this.normalizeBooleanFlag(data?.same_speaker) || this.normalizeBooleanFlag(data?.sameSpeaker))
        : undefined,
      speaker: speakerIdentity,
      text: this.normalizeTranscriptText(data?.text || data?.transcript || data?.utterance || data?.sentence),
      translated_text: this.extractTranslatedText(data),
      translation_target: data?.translation_target || data?.translationTarget || data?.target_language || data?.targetLanguage,
      is_final: this.normalizeBooleanFlag(data?.is_final) || this.normalizeBooleanFlag(data?.isFinal),
      confidence: Number.isFinite(Number(data?.confidence)) ? Number(data?.confidence) : 0,
      language: this.normalizeTranscriptText(data?.language),
      timestamp: normalizedTimestamp,
      duration: this.normalizeNumericValue(data?.duration, Math.max(end - start, 0)),
      words,
      created_at:
        typeof data?.created_at === 'string'
          ? data.created_at
          : new Date(normalizedTimestamp).toISOString()
    };
  }

  private toIncomingSegment(transcript: TranscriptResponse, trackId?: string): IncomingTranscriptionSegment {
    const words = transcript.words || [];
    const fallbackStart = (() => {
      const d = new Date(this.normalizeTimestamp(transcript.timestamp));
      return (d.getHours() * 3600) + (d.getMinutes() * 60) + d.getSeconds();
    })();
    const startTime = words.length ? words[0].start : fallbackStart;
    const endFromWords = words.length ? words[words.length - 1].end : startTime;
    const duration = this.normalizeNumericValue(transcript.duration, 0);
    const endTime = duration > 0
      ? startTime + duration
      : endFromWords;

    return {
      speaker: transcript.speaker || transcript.participant_identity || transcript.participant || 'Speaker',
      text: transcript.text,
      translatedText: transcript.translated_text,
      translationTarget: transcript.translation_target,
      language: transcript.language,
      timestamp: transcript.timestamp,
      startTime,
      endTime,
      isFinal: transcript.is_final,
      sameSpeaker: transcript.same_speaker,
      trackId
    };
  }

  trackByTranscriptionSegment(index: number, segment: TranscriptionViewSegment): string {
    return `${segment.speakerKey}-${segment.startTime}-${segment.timestamp}-${index}`;
  }

  private extractTranscriptItemsFromDataPayload(data: any, participantIdentity?: string): TranscriptResponse[] {
    if (!data) {
      return [];
    }

    let parsedData = data;
    if (typeof parsedData === 'string') {
      const textPayload = this.normalizeTranscriptText(parsedData);
      if (!textPayload) {
        return [];
      }

      try {
        parsedData = JSON.parse(textPayload);
      } catch {
        return [this.toTranscriptResponse({
          type: 'transcript',
          text: textPayload,
          speaker: participantIdentity || 'Speaker',
          participant_identity: participantIdentity || 'Speaker',
          is_final: true,
          timestamp: Date.now()
        }, participantIdentity)];
      }
    }

    let rawItems: any[] = [];

    if (Array.isArray(parsedData)) {
      rawItems = parsedData;
    } else if (Array.isArray(parsedData?.segments)) {
      rawItems = parsedData.segments;
    } else if (Array.isArray(parsedData?.transcriptions)) {
      rawItems = parsedData.transcriptions;
    } else if (Array.isArray(parsedData?.transcript)) {
      rawItems = parsedData.transcript;
    } else if (parsedData?.transcript || parsedData?.text) {
      rawItems = [{
        ...parsedData,
        type: parsedData.type || 'transcript',
        speaker: parsedData.speaker || participantIdentity || 'Speaker',
        participant_identity: parsedData.participant_identity || participantIdentity || 'Speaker'
      }];
    }

    if (!rawItems.length) {
      return [];
    }

    return rawItems
      .map((item: any) => this.normalizeTranscriptBatchItem(item))
      .map((item: any) => this.toTranscriptResponse(item, participantIdentity))
      .filter((transcript: TranscriptResponse) => !!this.normalizeTranscriptText(transcript.text));
  }

  private handleLiveKitDataReceived(message: LiveKitDataMessage): void {
    try {
      const payload = message?.payload;
      const participant = message?.participant;
      const kind = message?.kind;
      const topic = message?.topic;

      if (!payload) {
        return;
      }

      // console.log('📨 Data received:', {
      //   from: participant?.identity || 'unknown',
      //   kind,
      //   topic,
      //   size: payload.length
      // });

      const textData = new TextDecoder().decode(payload);
      // console.log('📝 Raw data:', textData.substring(0, 200));

      const items = this.extractTranscriptItemsFromDataPayload(textData, participant?.identity);
      if (!items.length) {
        console.warn('⚠️ Unknown data format:', textData);
        return;
      }

      // const withinNativePriorityWindow =
      //   this.lastNativeTranscriptionAt > 0 &&
      //   (Date.now() - this.lastNativeTranscriptionAt) <= this.nativePriorityWindowMs;
      // if (withinNativePriorityWindow) {
      //   console.log('ℹ️ Skipping data-channel transcript because native transcription is active');
      //   return;
      // }

      items.forEach((transcript: TranscriptResponse) => {
        this.processTranscriptResponse(transcript, topic || undefined);
      });
    } catch (error) {
      console.error('❌ Error parsing data:', error);
    }
  }

  private handleLiveKitTranscriptionReceived(event: LiveKitNativeTranscriptionEvent): void {
    const transcriptions = event?.transcriptions;
    const participant = event?.participant;
    const publication = event?.publication;

    console.log('🎤 TranscriptionReceived event:', {
      participant: participant?.identity,
      transcriptionCount: transcriptions?.length,
      publication: publication?.trackSid
    });

    if (Array.isArray(transcriptions) && transcriptions.length > 0) {
      this.lastNativeTranscriptionAt = Date.now();
    }

    this.processTranscriptBatch(transcriptions, participant?.identity, publication?.trackSid, true);
  }

  private applyMicrophoneSelection(selectedMic: MicrophoneDevice | null | undefined): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (selectedMic && typeof selectedMic === 'object' && selectedMic.deviceId) {
      this.selectedMicrophone = selectedMic.deviceId;
      this.selectedMicrophoneName = selectedMic.label || `Microphone ${selectedMic.deviceId.substring(0, 8)}`;
      this.usingSystemDefaultMicrophone = selectedMic.deviceId === this.systemDefaultMicDeviceId;
      this.microphoneControl.setValue(selectedMic, { emitEvent: false });
      void this.detectMicrophoneChannels(selectedMic.deviceId);
    } else {
      this.selectedMicrophone = '';
      this.selectedMicrophoneName = '';
      this.usingSystemDefaultMicrophone = false;
    }
  }

  private isCableAudioDevice(device: MediaDeviceInfo): boolean {
    const label = (device.label || '').toLowerCase();
    return label.includes('cable input') || label.includes('cable output');
  }

  // Load available microphones
  async loadAvailableMicrophones(preserveSelection: boolean = true): Promise<void> {
    try {
      // Request permission only when needed so labels are available.
      let devices = await navigator.mediaDevices.enumerateDevices();
      const hasHiddenLabels = devices.some(device => device.kind === 'audioinput' && !device.label);
      if (hasHiddenLabels) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        devices = await navigator.mediaDevices.enumerateDevices();
      }

      this.cableInputAvailable = devices.some(device => this.isCableAudioDevice(device));
      if (!this.cableInputAvailable) {
        this.includeExternalAudio = false;
      }

      this.availableMicrophones = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.deviceId === this.systemDefaultMicDeviceId
            ? `System Default (${device.label || 'Microphone'})`
            : (device.label || `Microphone ${device.deviceId.substring(0, 8)}`)
        }));

      if (this.availableMicrophones.length === 0) {
        this.errorMessage = 'No microphones found on this system.';
        return;
      }

      const defaultMic = this.availableMicrophones.find(m => m.deviceId === this.systemDefaultMicDeviceId)
        || this.availableMicrophones[0];
      const currentSelectionId = preserveSelection ? this.selectedMicrophone : '';
      const selectedMic = currentSelectionId
        ? (currentSelectionId === this.systemDefaultMicDeviceId
          ? defaultMic
          : this.availableMicrophones.find(m => m.deviceId === currentSelectionId) || defaultMic)
        : defaultMic;

      this.applyMicrophoneSelection(selectedMic);
    } catch (error) {
      console.error('Error loading microphones:', error);
      this.errorMessage = 'Failed to access microphones. Please grant permission.';
    }
  }

  private async handleMediaDevicesChanged(): Promise<void> {
    try {
      await this.loadAvailableMicrophones(true);

      // If recording is ongoing with system-default selected, try to switch input immediately.
      if (this.isRecording && !this.isPaused && this.captureMode === 'microphone' && this.usingSystemDefaultMicrophone) {
        await this.applySystemDefaultMicToActiveRecording();
      }
    } catch (error) {
      console.warn('Failed to refresh microphones after device change:', error);
    }
  }

  private buildRawMicrophoneConstraints(deviceId?: string): any {
    // Chrome downmixes to mono/stereo whenever noise suppression or AGC is on, so multi-channel devices
    // (e.g. Biamp, which does its own DSP) are captured raw to keep every channel. Ordinary mics keep processing.
    const multiChannel = this.detectedDeviceChannels > MAX_PROCESSED_MIC_CHANNELS;
    const useProcessing = !multiChannel;
    return {
      deviceId: deviceId ? { exact: deviceId } : undefined,
      sampleRate: 48000,
      channelCount: this.detectedDeviceChannels > 0 ? this.detectedDeviceChannels : { ideal: 2 },
      echoCancellation: false,
      noiseSuppression: useProcessing,
      autoGainControl: useProcessing,
      latency: 0,
      googEchoCancellation: false,
      googAutoGainControl: useProcessing,
      googNoiseSuppression: useProcessing,
      googHighpassFilter: useProcessing
    };
  }

  private async applySystemDefaultMicToActiveRecording(): Promise<void> {
    if (!this.isRecording || this.captureMode !== 'microphone' || this.recordingMode !== 'browser') {
      return;
    }
    // if (this.includeExternalAudio) {
    //   this.sendNotification('System default microphone changed. It will apply on next recording when external audio mixing is disabled.', 'warning');
    //   return;
    // }

    // Detect the new device's channels first so it is opened with all of them.
    await this.detectMicrophoneChannels(this.systemDefaultMicDeviceId);
    const replacementStream = await navigator.mediaDevices.getUserMedia({
      audio: this.buildRawMicrophoneConstraints(this.systemDefaultMicDeviceId)
    });
    const replacementTrack = replacementStream.getAudioTracks()[0];
    if (!replacementTrack) {
      return;
    }

    if (this.channelMixer) {
      // The mixer output (recorder + LiveKit track) stays the same; only its input changes.
      // The recorder keeps the channel layout it started with; the panel shows the new device's channels.
      this.channelMixer.setInput(replacementStream, this.detectedDeviceChannels);
      this.internalStream?.getTracks().forEach(track => track.stop());
      this.internalStream = replacementStream;
    } else {
      if (this.liveKitService.isConnected()) {
        await this.liveKitService.replaceAudioInputTrack(replacementTrack);
      }

      // Keep recorder stream graph intact during active recording. We only swap the live
      // publishing track and refresh references so pause/resume toggles hit the current source.
      this.internalStream = replacementStream;
      if (!this.includeExternalAudio) {
        this.audioStream = replacementStream;
      }
      this.setupAudioAnalyzer(replacementStream);
    }
    this.selectedMicrophoneName = replacementTrack.label || this.selectedMicrophoneName || 'System Default Microphone';
    this.selectedMicrophone = this.systemDefaultMicDeviceId;
    this.usingSystemDefaultMicrophone = true;
    this.sendNotification(`System default microphone switched to: ${this.selectedMicrophoneName}`, 'success');
  }

  onMicrophoneChangeEvent(): void {
    const selectedMic = this.microphoneControl.value;
    this.applyMicrophoneSelection(selectedMic);
  }

  onMicrophoneChange() {
    this.selectedMicrophone = '';
    this.selectedMicrophoneName = '';
    this.usingSystemDefaultMicrophone = false;
  }

  // Detect microphone channels when device is selected. Recording start awaits this so it never uses a stale count.
  private detectMicrophoneChannels(deviceId: string): Promise<void> {
    this.channelDetection = this.runMicrophoneChannelDetection(deviceId);
    return this.channelDetection;
  }

  private async runMicrophoneChannelDetection(deviceId: string): Promise<void> {
    try {
      // Create a temporary stream to detect channels - disable processing to get true channel count
      const tempStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          channelCount: { ideal: 32 }, // Above any real device, so the browser opens it with all its channels
          sampleRate: 48000,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });

      // The track reports the channels actually delivered (a Web Audio source node's channelCount is always 2).
      this.detectedDeviceChannels = ChannelMixer.channelCountOf(tempStream, 1);
      tempStream.getTracks().forEach(track => track.stop());

      const channelType = this.detectedDeviceChannels === 1 ? 'Mono' :
        this.detectedDeviceChannels === 2 ? 'Stereo' :
          'Multi-channel';

      // console.log(`🎛️ Selected device has ${this.detectedDeviceChannels} channels (${channelType})`);
      this.snackBar.open(
        `Device detected: ${this.detectedDeviceChannels} channel${this.detectedDeviceChannels > 1 ? 's' : ''} (${channelType})`,
        'Close',
        { duration: 2500 }
      );
    } catch (error) {
      console.error('Error detecting microphone channels:', error);
      this.detectedDeviceChannels = 1; // Default to mono if detection fails
    }
  }

  // Filter microphones for autocomplete
  private _filterMicrophones(value: string | MicrophoneDevice): MicrophoneDevice[] {
    if (!value) {
      return this.availableMicrophones;
    }

    const filterValue = typeof value === 'string'
      ? value.toLowerCase()
      : '';

    if (!filterValue) {
      return this.availableMicrophones;
    }

    return this.availableMicrophones.filter(mic =>
      (mic.label || '').toLowerCase().includes(filterValue)
    );
  }

  // Display function for autocomplete
  displayMicrophoneFn(mic: MicrophoneDevice): string {
    return mic && mic.label ? mic.label : '';
  }

  // Filter languages for autocomplete
  private _filterLanguages(value: string | LanguageOption): LanguageOption[] {
    if (!value) {
      return this.availableLanguages;
    }

    const filterValue = typeof value === 'string'
      ? value.toLowerCase()
      : '';

    if (!filterValue) {
      return this.availableLanguages;
    }

    return this.availableLanguages.filter(lang =>
      lang.name.toLowerCase().includes(filterValue) ||
      lang.code.toLowerCase().includes(filterValue)
    );
  }

  // Display function for language autocomplete
  displayLanguageFn(lang: LanguageOption): string {
    return lang && lang.name ? lang.name : '';
  }

  // Language change handler
  onLanguageChangeEvent(): void {
    const selectedLang = this.languageControl.value;
    if (selectedLang && typeof selectedLang === 'object' && selectedLang.code) {
      this.selectedLanguage = selectedLang.code;
      if (this.selectedLanguage === 'multi') {
        this.translateToEnglish = true;
      } else {
        this.translateToEnglish = false;
      }
    }
  }
  onLanguageChange() {
    this.selectedLanguage = '';
  }

  // Capture system audio (both sides of conversation)
  private async captureSystemAudio(): Promise<MediaStream> {
    try {
      // First, try to find Stereo Mix or loopback device
      const loopbackStream = await this.tryLoopbackDevice();
      if (loopbackStream) {
        console.log('✅ Using loopback device (Stereo Mix / What U Hear)');
        return loopbackStream;
      }

      // Fallback to screen capture method
      return await this.captureViaScreenShare();
    } catch (error: any) {
      console.error('Error capturing system audio:', error);
      throw error;
    }
  }

  // Try to capture from loopback device (Stereo Mix, What U Hear, etc.)
  private async tryLoopbackDevice(): Promise<MediaStream | null> {
    try {
      // Get all audio input devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');

      // Look for loopback device keywords
      const loopbackKeywords = [
        'stereo mix',
        'wave out',
        'what u hear',
        'loopback',
        'mix',
        'wave out mix',
        'your pc audio'
      ];

      const loopbackDevice = audioInputs.find(device =>
        loopbackKeywords.some(keyword =>
          device.label.toLowerCase().includes(keyword)
        )
      );

      if (loopbackDevice) {
        console.log('🎵 Found loopback device:', loopbackDevice.label);

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: loopbackDevice.deviceId },
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            sampleRate: 16000
          }
        });

        this.snackBar.open(`Using ${loopbackDevice.label} for system audio`, 'Close', { duration: 5000 });
        return stream;
      }

      return null;
    } catch (error) {
      console.warn('Could not access loopback device:', error);
      return null;
    }
  }

  // Capture system audio via screen sharing (fallback method)
  private async captureViaScreenShare(): Promise<MediaStream> {
    try {
      // Check if getDisplayMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error('Screen capture API is not supported in this browser. Please use Chrome, Edge, or Firefox.');
      }

      // Check browser compatibility
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isEdge = /Edg/.test(navigator.userAgent);
      const isFirefox = /Firefox/.test(navigator.userAgent);

      if (!isChrome && !isEdge && !isFirefox) {
        throw new Error('System audio capture is only supported in Chrome, Edge, or Firefox browsers.');
      }

      console.log('🎤 Requesting system audio capture...');

      // Request screen capture with audio
      // Note: video: false might not work in all browsers, using video: true as fallback
      let displayStream: MediaStream;

      try {
        // Try audio-only first (Chrome/Edge)
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: false,
          audio: {
            echoCancellation: false,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 16000
          } as any
        });
      } catch (firstError) {
        console.warn('Audio-only capture failed, trying with video:', firstError);

        // Fallback: capture with video (required by some browsers)
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: {
            echoCancellation: false,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 16000
          } as any
        });

        // Stop video tracks if we got them
        const videoTracks = displayStream.getVideoTracks();
        videoTracks.forEach(track => track.stop());
      }

      // Extract only audio tracks
      const audioTracks = displayStream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error('No audio track captured. Make sure to check "Share audio" when selecting what to share.');
      }

      console.log(`✅ Captured ${audioTracks.length} audio track(s) from system`);

      // Get microphone stream for mixing (optional)
      let micStream: MediaStream | null = null;
      if (this.selectedMicrophone) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: this.selectedMicrophone },
              echoCancellation: false,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 16000
            }
          });
          console.log('✅ Also capturing microphone for mixing');
        } catch (error) {
          console.warn('Could not capture microphone, using system audio only:', error);
        }
      }

      // Mix system audio and microphone if both available
      if (micStream && micStream.getAudioTracks().length > 0) {
        try {
          const audioContext = new AudioContext({ sampleRate: 16000 });
          const destination = audioContext.createMediaStreamDestination();

          // System audio source
          const systemSource = audioContext.createMediaStreamSource(displayStream);
          systemSource.connect(destination);

          // Microphone source
          const micSource = audioContext.createMediaStreamSource(micStream);
          micSource.connect(destination);

          console.log('✅ Mixed system audio + microphone');
          return destination.stream;
        } catch (mixError) {
          console.warn('Failed to mix audio streams, using system audio only:', mixError);
          return displayStream;
        }
      } else {
        console.log('✅ Using system audio only');
        return displayStream;
      }
    } catch (error: any) {
      console.error('Error capturing system audio:', error);

      // Provide specific error messages
      let errorMessage = 'Failed to capture system audio. ';

      if (error.name === 'NotAllowedError') {
        errorMessage += 'Permission denied. Please allow screen sharing when prompted.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'System audio capture is not supported in this browser. Please use Chrome, Edge, or Firefox.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No audio source found. Make sure to check "Share audio" or "Share tab audio" in the sharing dialog.';
      } else if (error.name === 'AbortError') {
        errorMessage += 'Screen sharing was cancelled.';
      } else {
        errorMessage += error.message || 'Unknown error occurred.';
      }

      throw new Error(errorMessage);
    }
  }

  // Toggle capture mode
  toggleCaptureMode(mode: 'microphone' | 'system'): void {
    if (this.isRecording || this.isPaused) {
      this.snackBar.open('Stop recording before changing capture mode', 'Close', { duration: 3000 });
      return;
    }

    // Show informative message for system audio mode
    if (mode === 'system') {
      const message = `System Audio Mode: Browser will prompt for screen/tab sharing to access audio. 
      
Alternative Options:
1. Use Virtual Audio Cable software (VB-Cable, Voicemeeter)
2. Enable "Stereo Mix" in Windows Sound settings
3. Use browser extension for tab audio capture

Click "Start Recording" to proceed with browser's audio capture.`;

      console.log('ℹ️ System Audio Capture Info:', message);
    }

    this.captureMode = mode;
    console.log(`Capture mode changed to: ${mode}`);
  }

  // Recording Mode Selection Methods
  selectRecordingMode(mode: 'browser' | 'wasapi'): void {
    if (this.isRecording || this.isPaused) {
      this.snackBar.open('Stop recording before changing recording mode', 'Close', { duration: 3000 });
      return;
    }

    this.recordingMode = mode;

    if (mode === 'wasapi') {
      this.checkWasapiService();
    }

    console.log(`Recording mode changed to: ${mode}`);
  }

  private lastWasapiCheck: number = 0;
  private wasapiCheckCache: boolean = false;
  private readonly WASAPI_CHECK_INTERVAL = 5000; // Cache for 5 seconds

  async checkWasapiService(): Promise<void> {
    // Use cached result if checked recently
    const now = Date.now();
    if (now - this.lastWasapiCheck < this.WASAPI_CHECK_INTERVAL) {
      console.log('Using cached WASAPI status:', this.wasapiCheckCache);
      this.wasapiInstalled = this.wasapiCheckCache;
      if (this.wasapiInstalled && this.wasapiDevices.length === 0) {
        await this.loadWasapiDevices();
      }
      return;
    }

    this.wasapiChecking = true;
    console.log('Checking WASAPI service...');

    try {
      this.wasapiInstalled = await this.wasapiService.checkIfInstalled();
      this.wasapiCheckCache = this.wasapiInstalled;
      this.lastWasapiCheck = now;
      console.log('WASAPI service check result:', this.wasapiInstalled);

      if (this.wasapiInstalled) {
        await this.loadWasapiDevices();
        this.snackBar.open('WASAPI service connected successfully', 'Close', { duration: 3000 });
      } else {
        console.warn('WASAPI service not detected');
      }
    } catch (error) {
      console.error('Error checking WASAPI service:', error);
      this.wasapiInstalled = false;
      this.wasapiCheckCache = false;
    } finally {
      this.wasapiChecking = false;
      console.log('WASAPI checking complete. Installed:', this.wasapiInstalled);
    }
  }

  async loadWasapiDevices(): Promise<void> {
    try {
      // Ensure we're connected before getting devices
      await this.wasapiService.connect();

      this.wasapiDevices = await this.wasapiService.getDevices();

      console.log('📡 WASAPI devices loaded:', this.wasapiDevices);

      // Auto-select default device
      const defaultDevice = this.wasapiDevices.find(d => d.isDefault);
      if (defaultDevice) {
        this.selectedWasapiDevice = defaultDevice.id;
        console.log('✅ Auto-selected default WASAPI device:', defaultDevice.name);
      }

      // Keep connection alive - will be reused for recording
      // No need to disconnect and reconnect
    } catch (error) {
      console.error('Error loading WASAPI devices:', error);
      this.snackBar.open('Failed to load WASAPI devices', 'Close', { duration: 3000 });
    }
  }

  async refreshWasapiDevices(): Promise<void> {
    try {
      // Show loading indicator
      this.snackBar.open('Refreshing WASAPI devices...', '', { duration: 1000 });

      // Check if service is available
      if (!this.wasapiInstalled) {
        this.snackBar.open('WASAPI service is not running', 'Close', { duration: 3000 });
        return;
      }

      // Ensure connection is established
      if (!this.wasapiService.isConnected()) {
        console.log('🔌 Connecting to WASAPI service...');
        await this.wasapiService.connect();
      }

      // Get fresh device list
      this.wasapiDevices = await this.wasapiService.getDevices();

      console.log('🔄 WASAPI devices refreshed:', this.wasapiDevices);
      this.snackBar.open(`Found ${this.wasapiDevices.length} WASAPI device(s)`, 'Close', { duration: 2000 });

      // Re-select default if current selection is lost
      if (!this.wasapiDevices.find(d => d.id === this.selectedWasapiDevice)) {
        const defaultDevice = this.wasapiDevices.find(d => d.isDefault);
        if (defaultDevice) {
          this.selectedWasapiDevice = defaultDevice.id;
          console.log('✅ Re-selected default WASAPI device:', defaultDevice.name);
        } else {
          this.selectedWasapiDevice = '';
        }
      }
    } catch (error) {
      console.error('Error refreshing WASAPI devices:', error);
      this.snackBar.open('Failed to refresh WASAPI devices', 'Close', { duration: 3000 });
    }
  }

  getSelectedWasapiDevice(): any {
    return this.wasapiDevices.find(d => d.id === this.selectedWasapiDevice);
  }

  downloadWasapiInstaller(event: Event): void {
    event.preventDefault();

    // TODO: Implement installer download
    // For now, show a message
    this.snackBar.open('Installer download will be available soon. Please contact support.', 'Close', { duration: 5000 });
  }

  private cleanupWasapiSubscriptions(): void {
    if (this.wasapiAudioSubscription) {
      this.wasapiAudioSubscription.unsubscribe();
      this.wasapiAudioSubscription = null;
    }
    if (this.wasapiStatusSubscription) {
      this.wasapiStatusSubscription.unsubscribe();
      this.wasapiStatusSubscription = null;
    }
    if (this.wasapiErrorSubscription) {
      this.wasapiErrorSubscription.unsubscribe();
      this.wasapiErrorSubscription = null;
    }
  }

  // Start WASAPI recording
  async startWasapiRecording(): Promise<void> {
    if (!this.wasapiInstalled) {
      this.errorMessage = 'WASAPI service is not running. Please install and start the service.';
      this.snackBar.open('WASAPI service not available', 'Close', { duration: 3000 });
      return;
    }

    if (!this.selectedWasapiDevice) {
      this.errorMessage = 'Please select a WASAPI device';
      this.snackBar.open('No WASAPI device selected', 'Close', { duration: 3000 });
      return;
    }

    try {
      this.isRecording = true;
      this.updateFormControlsState(true);
      this.recordingStartTime = Date.now();
      this.pausedDuration = 0;
      this.audioChunks = [];
      this.resetSpeakerTracking();

      this.currentRecordingSessionId = `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.audioChunkIndex = 0;
      this.startDbSaving();
      this.startTimer();

      if (!this.wasapiService.isConnected()) {
        await this.wasapiService.connect();
      }

      const device = this.wasapiDevices.find(d => d.id === this.selectedWasapiDevice);
      if (!device) {
        throw new Error('Selected device not found');
      }

      this.cleanupWasapiSubscriptions();
      this.wasapiAudioSubscription = this.wasapiService.audioData$.subscribe(async (audioData: ArrayBuffer) => {
        if (!audioData || audioData.byteLength === 0) {
          return;
        }

        if (this.audioChunks.length === 0) {
          this.detectedWasapiFormat = this.detectAudioFormat(audioData);
        }

        const blob = new Blob([audioData], { type: 'application/octet-stream' });
        this.audioChunks.push(blob);

        try {
          await this.saveAudioChunkToDb(audioData);
        } catch (error) {
          console.error('Error saving WASAPI audio chunk to IndexedDB:', error);
        }

        if (this.streamingMode === 'live' && this.liveKitService.isConnected() && this.currentSessionId) {
          try {
            let audioToSend = audioData;
            if (this.resampleWasapiAudio) {
              audioToSend = await this.resampleAudioForTranscription(audioData);
            }

            const base64Audio = this.arrayBufferToBase64(audioToSend);
            await this.liveKitService.streamAudioChunk(base64Audio, {
              session_id: this.currentSessionId,
              timestamp: Date.now(),
              size: audioToSend.byteLength
            });
          } catch (error) {
            console.error('Error streaming WASAPI chunk to LiveKit:', error);
          }
        }
      });

      this.wasapiStatusSubscription = this.wasapiService.recordingStatus$.subscribe();
      this.wasapiErrorSubscription = this.wasapiService.error$.subscribe((error: string) => {
        this.errorMessage = `WASAPI error: ${error}`;
      });

      this.wasapiService.startRecording(
        this.selectedWasapiDevice,
        device.maxChannels,
        48000,
        this.resampleWasapiAudio
      );

      this.recordingStatus = this.streamingMode === 'live'
        ? `Streaming via WASAPI (${device.maxChannels} channels)`
        : `Recording via WASAPI (${device.maxChannels} channels)`;
      this.snackBar.open(`WASAPI recording started: ${device.maxChannels} channels`, 'Close', { duration: 3000 });
    } catch (error: any) {
      this.errorMessage = `Failed to start WASAPI recording: ${error.message || error}`;
      this.snackBar.open('Failed to start WASAPI recording', 'Close', { duration: 3000 });
      this.isRecording = false;
      this.updateFormControlsState(false);
    }
  }

  // Stop WASAPI recording
  async stopWasapiRecording(): Promise<void> {
    try {
      this.wasapiService.stopRecording();
      await this.delay(500);
      this.stopDbSaving();
      await this.saveRecordingSession();
      await this.saveTranscriptionToDb();
      this.cleanupWasapiSubscriptions();

      console.log(`📦 Creating audio blob from ${this.audioChunks.length} chunks`);

      if (this.audioChunks.length === 0) {
        console.error('❌ No audio chunks collected from WASAPI service!');
        this.errorMessage = 'No audio data was received from WASAPI service. Please check if the service is running correctly.';
        this.snackBar.open('⚠️ No audio data recorded - Please check WASAPI service', 'Close', { duration: 5000 });
      } else {
        try {
          // Calculate total size before combining
          const totalSize = this.audioChunks.reduce((sum, chunk) => sum + chunk.size, 0);
          console.log(`📊 Total PCM data size: ${(totalSize / 1024 / 1024).toFixed(2)} MB from ${this.audioChunks.length} chunks`);

          // Combine all PCM chunks
          const pcmBlob = new Blob(this.audioChunks, { type: 'application/octet-stream' });
          const pcmData = await pcmBlob.arrayBuffer();

          console.log(`✅ PCM blob created: ${pcmData.byteLength} bytes`);

          // Use auto-detected format instead of assumptions
          let wavHeader: ArrayBuffer;

          if (this.detectedWasapiFormat) {
            // Use the actual detected format
            wavHeader = this.createWavHeader(
              pcmData.byteLength,
              this.detectedWasapiFormat.channels,
              this.detectedWasapiFormat.sampleRate,
              this.detectedWasapiFormat.bitsPerSample,
              this.detectedWasapiFormat.isFloat
            );
            console.log('🎵 Creating WAV file using AUTO-DETECTED format:');
            console.log(`   ${this.detectedWasapiFormat.sampleRate}Hz, ${this.detectedWasapiFormat.channels}ch, ${this.detectedWasapiFormat.bitsPerSample}-bit ${this.detectedWasapiFormat.isFloat ? 'Float' : 'PCM'}`);
          } else {
            // Fallback to expected format based on resample flag
            console.warn('⚠️ No format detected, using fallback based on resample flag');
            const device = this.wasapiDevices.find(d => d.id === this.selectedWasapiDevice);
            const channels = device?.maxChannels || 2;

            if (this.resampleWasapiAudio) {
              wavHeader = this.createWavHeader(pcmData.byteLength, 1, 16000, 16, false);
              console.log('🎵 Fallback: 16kHz mono 16-bit PCM');
            } else {
              wavHeader = this.createWavHeader(pcmData.byteLength, channels, 48000, 32, true);
              console.log(`🎵 Fallback: 48kHz ${channels}ch 32-bit Float`);
            }
          }

          // Combine header + PCM data
          const wavData = this.concatenateArrayBuffers(wavHeader, pcmData);
          this.recordedAudioBlob = new Blob([wavData], { type: 'audio/wav' });
          console.log(`✅ Audio recording saved (${(this.recordedAudioBlob.size / 1024 / 1024).toFixed(2)} MB)`);

          // Create audio URL for playback
          if (this.currentAudioUrl) {
            URL.revokeObjectURL(this.currentAudioUrl);
          }
          this.currentAudioUrl = URL.createObjectURL(this.recordedAudioBlob);

          if (this.audioPlayer) {
            this.audioPlayer.src = this.currentAudioUrl;
            this.audioPlayer.load();
          }

          this.snackBar.open('Recording stopped', 'Close', { duration: 3000 });
        } catch (blobError) {
          console.error('Error creating audio blob:', blobError);
          this.errorMessage = `Failed to create audio file: ${blobError}`;
          this.snackBar.open('Error creating audio file from recorded data', 'Close', { duration: 5000 });
        }
      }

      await this.stopLiveKitTranscriptionAndSession();
      await this.liveKitService.disconnect();
      this.isStreaming = false;
      this.isTranscribing = false;
      this.serverConnected = false;

      this.wasapiService.disconnect();
      console.log('✅ WASAPI recording stopped');
    } catch (error) {
      console.error('Error stopping WASAPI recording:', error);
    }
  }


  // Get supported MIME type for MediaRecorder
  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',     // Opus codec - best for multi-channel
      'audio/webm;codecs=pcm',      // PCM if available
      'audio/webm',                  // Fallback WebM
      'audio/ogg;codecs=opus',       // Ogg Opus
      'audio/mp4',                   // MP4 fallback
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        // console.log(`✅ Using MIME type: ${type}`);
        return type;
      }
    }

    console.warn('⚠️ No preferred MIME types supported, using default');
    return '';
  }
  // Court location/room are mandatory for court organizations and optional for everyone else.
  // This must stay in step with StartSessionRequestValidator on the backend: if the UI lets a
  // recording start without them, the API rejects it with an error the user cannot act on.
  get courtLocationSelectionRequired(): boolean {
    return this.selectedOrgIsCourt;
  }

  get courtRoomSelectionRequired(): boolean {
    return this.selectedOrgIsCourt;
  }

  validationBeforeStartRecording() {
    let message = '';
    let result = true;
    if(this.selectedDepartmentExtId.trim() == '')
    {
      message = 'Please select Department.';
      result =false;
    }
    // if (this.selectedOrgExtId.trim() == '') {
    //   message = 'Please select organization.';
    //   result = false;
    // }
    // Court location/room are optional on the recording session: they only become mandatory
    // when the selected court organization actually has options to choose from. An org with
    // none configured (or a non-court org) can still record.
    else if (this.courtLocationSelectionRequired && this.selectedcourtLocationExtId.trim() == '') {
      message = 'Please select a court location.';
      result = false;
    }
    else if (this.courtRoomSelectionRequired && this.selectedcourtRoomName.trim() == '') {
      message = 'Please select a court room.';
      result = false;
    }
    else if (this.selectedMicrophone.trim() == '') {
      message = 'Please select a Micro phones.';
      result = false;
    }
    else if (this.selectedLanguage.trim() == '') {
      message = 'Please select a Language.';
      result = false;
    }

    // Runs before the Case Details check so the auto-generated case number and hearing time
    // satisfy their own required rules instead of being reported as missing.
    this.setDefaultCaseDetailsFormValues();

    // Case Details carry Validators.required only while the org is a court, so for any other org
    // the group is valid by construction and this never blocks.
    // if (result && this.caseDetailsForm.invalid) {
    //   this.caseDetailsForm.markAllAsTouched();
    //   message = 'Please fill in all required Case Details fields.';
    //   result = false;
    // }

    // The dynamic form enforces its own required/minLength/pattern rules.
    if (result && this.formsFormGroup && this.formsFormGroup.invalid) {
      this.formsFormGroup.markAllAsTouched();
      message = 'Please fill in all required fields in the form.';
      result = false;
    }

    if (!result)
      this.sendNotification(message, 'error');
    return result;
  }
  // Start recording
  async startRecording(): Promise<void> {

    if (this.validationBeforeStartRecording()) {
      this.captureRecordingStartedAt();
      this.isStartingRecording = true;
      this.stopRequested = false;
      try {
        this.errorMessage = '';
        this.successMessage = '';
        this.recordedAudioBlob = null;
        this.audioChunks = [];
        this.liveStreamAudioChunks = [];

        // Reset cumulative speaker tracking
        this.resetSpeakerTracking();

        // Always start with a fresh transcription view for the new recording.
        this.clearActiveTranscriptionForNewRecording();

        // Initialize new recording session for IndexedDB
        this.currentRecordingSessionId = `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.audioChunkIndex = 0;

        // Always create LiveKit room/session from backend on start recording.
        this.useLiveKit = true;
        // console.log('🎥 Creating LiveKit room as host...');
        this.recordingStatus = 'Creating room...';

        try {


          await this.createAndConnectRoom();
          this.idle.stop();
          this.wakeLockService.setRecordingActive(true);

          // console.log('✅ LiveKit room created successfully');
          this.snackBar.open(`Session created: ${this.currentRoomName}`, 'Close', { duration: 5000 });
        } catch (error) {
          console.error('❌ Failed to create LiveKit room:', error);
          this.errorMessage = 'Failed to create LiveKit room. Please check your connection and try again.';
          this.recordingStatus = 'Failed to create room';
          throw error;
        }

        // Check if WASAPI mode is selected
        // if (this.recordingMode === 'wasapi') {
        //   await this.startWasapiRecording();
        //   // Enforce: startstreamingsession -> join room -> start recording -> starttranscription
        //   await this.startLiveKitTranscriptionSession();
        //   return;
        // }

        // Browser Audio API mode
        // Get audio stream based on capture mode
        if (this.captureMode === 'system') {
          // Capture system audio (both sides of call)
          this.audioStream = await this.captureSystemAudio();
        } else {
          // Capture microphone only - preserve all channels like WASAPI
          // Channel detection runs on mic selection; wait for it so the constraints use the real count.
          await this.channelDetection;
          const audioConstraints: any = this.buildRawMicrophoneConstraints(this.selectedMicrophone);

          // console.log(`🎤 Requesting ${this.detectedDeviceChannels} channels with RAW audio (no processing)`);
          // 1. Capture Biamp Mics
          this.internalStream = await navigator.mediaDevices.getUserMedia({
            audio: audioConstraints
          });
          // console.log('🎤 Mic input:', this.internalStream.getAudioTracks().map(t => `${t.label} ${t.readyState} muted=${t.muted} enabled=${t.enabled}`));

          // Mixer graph: mic channels pass through per-channel gain/mute before reaching the recorder and LiveKit.
          this.closeMixerAudioContext();
          const audioCtx = new AudioContext();
          this.mixerAudioContext = audioCtx;
          // Created after several awaits, so Chrome may start it suspended and the mixed stream would be silent.
          if (audioCtx.state === 'suspended') {
            await audioCtx.resume();
          }
          const mixedDest = audioCtx.createMediaStreamDestination();
          this.channelMixer = new ChannelMixer(audioCtx, mixedDest);

          if (this.includeExternalAudio) {
            // 2. Capture Teams (VB-Cable)
            // console.log("Recording internal + External audio.");
            const devices = await navigator.mediaDevices.enumerateDevices();
            const inputs = devices.filter(d => d.kind === 'audioinput');
            const teamsCable = inputs.find(device => this.isCableAudioDevice(device));

            //const VB_CABLE_DEVICE = { audio: { deviceId: { exact: 'VB_CABLE_DEVICE_ID' } } };

            if (!teamsCable) {
              this.includeExternalAudio = false;
              this.cableInputAvailable = false;
              throw new Error('Cable Input device not found. Refresh the microphone list after connecting VB-Cable.');
            }
            this.externalStream = await navigator.mediaDevices.getUserMedia({
              audio: {
                deviceId: { exact: teamsCable.deviceId },
                echoCancellation: false, // CRITICAL: Disable these for virtual cables
                noiseSuppression: false,
                autoGainControl: false
              }
            });
            // this.externalStream = await navigator.mediaDevices.getUserMedia(VB_CABLE_DEVICE);
            // 3. Connect both streams to the Mixer (default stereo destination, as before)
            this.channelMixer.setInput(this.internalStream, this.detectedDeviceChannels);
            const sourceExternal = audioCtx.createMediaStreamSource(this.externalStream);
            sourceExternal.connect(mixedDest);

            console.log('🎛️ Mixer state:', audioCtx.state,
              '| cable:', this.externalStream.getAudioTracks().map(t => `${t.label} ${t.readyState} muted=${t.muted} enabled=${t.enabled}`));
          } else {
            // 3. LOGIC FOR SINGLE STREAM (Checkbox Unchecked)
            // Keep the device's channel layout instead of the destination's default stereo downmix.
            const deviceChannels = ChannelMixer.channelCountOf(this.internalStream, this.detectedDeviceChannels);
            mixedDest.channelCount = deviceChannels;
            mixedDest.channelCountMode = 'explicit';
            mixedDest.channelInterpretation = 'discrete';
            this.channelMixer.setInput(this.internalStream, this.detectedDeviceChannels);
          }
          // 4. Assign the mixed result to your main audioStream
          this.audioStream = mixedDest.stream;

          // Verify actual channels on the capture device (the mixed output always reports the destination's layout)
          const audioTrack = this.internalStream.getAudioTracks()[0];
          const settings = audioTrack.getSettings() as any;
          const actualChannels = settings.channelCount || this.detectedDeviceChannels || 1;
          // console.log(`✅ Stream created with ${actualChannels} channels`);

          const channelInfo = actualChannels === 1 ? '1 channel (Mono)' :
            actualChannels === 2 ? '2 channels (Stereo)' :
              `${actualChannels} channels (Multi-channel)`;
          // console.log(`🎤 Recording started with ${channelInfo}`);

          // Update detected channels if different
          if (actualChannels && actualChannels !== this.detectedDeviceChannels) {
            console.warn(`⚠️ Channel mismatch: Expected ${this.detectedDeviceChannels}, got ${actualChannels}`);
            this.detectedDeviceChannels = actualChannels;
          }
        }
        if (this.streamingMode === 'live') {
          // Live streaming mode
          await this.startLiveStreaming(this.audioStream);

          // Also record for playback purposes
          this.startMediaRecorderForPlayback(this.audioStream);
          this.isRecordingDetailsExpanded = false;
          this.isCaseDetailsExpanded = false;
          this.isFormsExpanded = false;
          this.showAnnotations = true;
          this.isAnnotationsExpanded = true;

        } else {
          // Batch mode (record then send)
          // Create MediaRecorder with options to preserve multi-channel audio
          const mimeType = this.getSupportedMimeType();
          const recordingOptions: any = {
            mimeType: mimeType,
            audioBitsPerSecond: 256000 // Higher bitrate for multi-channel
          };

          // For multi-channel, explicitly set audio channels if supported
          if (this.detectedDeviceChannels > 2) {
            recordingOptions.audioBitsPerSecond = this.detectedDeviceChannels * 64000; // 64kbps per channel
          }

          // console.log(`🎙️ Creating MediaRecorder with mimeType: ${mimeType}, Expected channels: ${this.detectedDeviceChannels}, Bitrate: ${recordingOptions.audioBitsPerSecond}`);

          this.mediaRecorder = new MediaRecorder(this.audioStream, recordingOptions);
          // this.setupAudioAnalyzer(this.audioStream);
          this.mediaRecorder.ondataavailable = async (event) => {
            if (event.data.size > 0) {
              this.audioChunks.push(event.data);

              // Save audio chunk to IndexedDB for failsafe
              try {
                const arrayBuffer = await event.data.arrayBuffer();
                await this.saveAudioChunkToDb(arrayBuffer);
              } catch (error) {
                console.error('Error saving chunk to DB:', error);
              }
            }
          };

          this.mediaRecorder.onstop = () => {
            this.handleRecordingStop();
          };

          // Start recording
          this.mediaRecorder.start(1000); // Capture data every 1 second for failsafe

        }

        this.isRecording = true;
        this.updateFormControlsState(true);
        this.recordingStatus = this.streamingMode === 'live' ? 'Live Streaming...' : 'Recording...';
        this.recordingStartTime = Date.now();
        this.pausedDuration = 0;
        this.pauseStartTime = 0;
        this.recordingCaseClock = this.recordingStartTime;
        this.recordingDuration = '00:00:00';
        this.recordingStartDateTime = new Date(this.recordingStartTime).toISOString();
        this.recordingDetails.AudioStart = this.recordingStartDateTime;
        const hearingDateTime = this.resolveHearingDateTime(this.recordingStartDateTime);
        this.hearingDate = hearingDateTime.toISOString();
        this.addRecordingCase(
          this.selectedCaseDetailsId,
          this.currentCaseNumber,
          this.recordingStartDateTime,
          hearingDateTime
        );
        if (this.selectedCaseDetailsId) {
          this.setRecordingCaseFormData(this.selectedCaseDetailsId, this.buildFormDataPayload() ?? null);
        }
        this.startTimer();
        
        void this.prepareModelandAddAnnotation(
          'System: Start Recording',
          'Start: ' + this.currentCaseNumber + ', New Case.',
          this.recordingStartDateTime
        );
        localStorage.setItem('isStreamingRecording', 'true');
        this.fileacces.start();
        //this.recordingStartDateTime = new Date().toISOString();
        // Start periodic saving to IndexedDB
        this.startDbSaving();
      } catch (error) {
        console.error('Error starting recording:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (this.captureMode === 'system') {
          this.errorMessage = 'Failed to capture system audio. Please allow screen sharing with audio when prompted. Error: ' + errorMessage;
        } else {
          this.errorMessage = 'Failed to start recording. Please check microphone permissions. Error: ' + errorMessage;
        }
      } finally {
        this.isStartingRecording = false;
      }
    }
  }
  async prepareModelandAddAnnotation(
    speaker: string,
    comment: string,
    dateTime?: any,
    caseDetailsId: number | null = this.selectedCaseDetailsId,
    audioTimeSeconds?: number
  ): Promise<Annotations | null> {
    //add create annotation
    if (this.canAddAnnotations) {
      // console.log("Recording Duration for annotation timestamp: ", this.recordingDuration);
      const totalSeconds: number = audioTimeSeconds ?? this.recordingDuration
        .split(':')
        .reduce((acc, time) => (acc * 60) + parseInt(time, 10), 0);

      const annotationdetails: Annotations = {
        timeStamp: this.formatAnnotationTimestamp(dateTime),
        speaker: speaker,
        comments: comment,
        recordingSessionId: this.recordingId,
        sourceType: "Web",
        audioTime: totalSeconds,
        caseDetailsId: caseDetailsId ?? undefined
      };

      const annotationComponent = await this.ensureAnnotationComponentRef();
      if (annotationComponent) {
        const saved = await annotationComponent.addAnnotation(annotationdetails);
        // The request model is returned (not the saved one): its timeStamp is still the local
        // HH:mm:ss form addAnnotation expects, so it can be re-sent as an edit. The id falls back
        // to the reloaded list because the save response does not always carry it.
        const id = saved?.id || annotationComponent.findAnnotationId(speaker, comment);
        return { ...annotationdetails, id: id ?? undefined };
      }
      console.error('Annotation component is not available to add annotation:', annotationdetails);
    }
    return null;
  }

  private async updateAnnotationComments(annotation: Annotations | null, comments: string): Promise<void> {
    if (!annotation?.id) {
      return;
    }
    const annotationComponent = await this.ensureAnnotationComponentRef();
    await annotationComponent?.addAnnotation({ ...annotation, comments });
  }

  // Mirrors the template conditions that render <app-annotation>; without them the component
  // never exists, so there is nothing to wait for.
  private get canAddAnnotations(): boolean {
    return this.allowAnnotations && this.selectedOrgIsCourt;
  }

  private async ensureAnnotationComponentRef(timeoutMs: number = 2000): Promise<AnnotationComponent | null> {
    if (this.annotationComponentRef) {
      return this.annotationComponentRef;
    }
    if (!this.canAddAnnotations) {
      return null;
    }

    this.showAnnotations = true;
    this.cdRef.detectChanges();

    const start = Date.now();
    while (!this.annotationComponentRef && Date.now() - start < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    return this.annotationComponentRef ?? null;
  }

  private formatAnnotationTimestamp(dateTime?: any): string {
    let dt: Date;
    if (dateTime === undefined || dateTime === null || dateTime === '') {
      dt = new Date();
    } else if (typeof dateTime === 'number') {
      // Accept seconds or milliseconds
      dt = dateTime < 1e11 ? new Date(dateTime * 1000) : new Date(dateTime);
    } else {
      dt = new Date(dateTime);
      if (isNaN(dt.getTime())) {
        // Fallback: try numeric parse
        const n = Number(dateTime);
        dt = Number.isFinite(n) ? (n < 1e11 ? new Date(n * 1000) : new Date(n)) : new Date();
      }
    }

    return dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  private clearActiveTranscriptionForNewRecording(): void {
    this.transcriptionText = '';
    this.formattedTranscriptionHTML = '';
    this.interimTranscription = '';
    this.fullTranscript = '';
    this.currentInterimText = '';
    this.finalTranscriptionSegments = [];
    this.interimTranscriptionSegment = null;
    this.transcriptResponses = [];
    this.recentTranscriptSignatures.clear();
    this.lastNativeTranscriptionAt = 0;
    this.wordCount = 0;
    this.characterCount = 0;
    this.lastUpdated = null;
    this.recordingDetails = { AudioStart: null, Events: [], AudioEnd: null };
    // Reset C# ProcessTranscriptResponses state
    this.lkLastFinalSpeakerKey = '';
    this.lkLastFinalStartSeconds = 0;
    this.lkLastFinalEndSeconds = 0;
    this.lkLastFinalText = '';
    this.lkLastFinalTranslatedText = '';
    this.lkInterimSegmentText = '';
    this.lkInterimTranslatedSegmentText = '';
  }

  // Start live streaming by publishing microphone track to LiveKit.
  private async startLiveStreaming(stream: MediaStream): Promise<void> {
    if (!this.liveKitService.isConnected()) {
      throw new Error('LiveKit is not connected');
    }

    await this.liveKitService.startAudioStreaming(stream);
    this.isStreaming = true;
    this.serverConnected = true;
    // console.log('🎤 LiveKit track publishing started');

  }

  // Start MediaRecorder for playback (parallel to live streaming)
  private startMediaRecorderForPlayback(stream: MediaStream): void {
    this.liveStreamAudioChunks = [];

    // Use same configuration as batch recording to preserve channels
    const mimeType = this.getSupportedMimeType();
    const recordingOptions: any = {
      mimeType: mimeType,
      audioBitsPerSecond: 128000
    };

    // console.log(`🎙️ Creating live playback recorder with mimeType: ${mimeType}`);
    this.mediaRecorderForLivePlayback = new MediaRecorder(stream, recordingOptions);
    this.setupAudioAnalyzer(stream);
    this.mediaRecorderForLivePlayback.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.liveStreamAudioChunks.push(event.data);
      }
    };

    this.mediaRecorderForLivePlayback.onstop = () => {
      // Create audio blob for playback
      if (this.liveStreamAudioChunks.length > 0) {
        this.recordedAudioBlob = new Blob(this.liveStreamAudioChunks, { type: 'audio/webm' });
        const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
        this.recordedAudioSize = `${sizeInMB} MB`;
        // console.log(`✅ Live stream audio recorded for playback: ${this.recordedAudioSize}`);
      }
    };

    // Start recording in parallel (capture every second)
    this.mediaRecorderForLivePlayback.start(1000);
    // console.log('🎙️ Started recording live stream for playback');
    setTimeout(async () => {
      this.playHlsForRoom(this.currentRoomName);
      //this.openAnnotationWindow();
    }, 10000);
  }

  // Convert ArrayBuffer to Base64
  private arrayBufferToBase64(buffer: ArrayBufferLike): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }


  // Convert AudioBuffer to WAV format
  private audioBufferToWav(audioBuffer: AudioBuffer): Blob {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;

    // Interleave channels
    const length = audioBuffer.length * numberOfChannels * 2;
    const buffer = new ArrayBuffer(44 + length);
    const view = new DataView(buffer);

    // Write WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true); // PCM format
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * (bitDepth / 8), true); // byte rate
    view.setUint16(32, numberOfChannels * (bitDepth / 8), true); // block align
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, length, true);

    // Write interleaved audio data
    const channels: Float32Array[] = [];
    for (let i = 0; i < numberOfChannels; i++) {
      channels.push(audioBuffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < audioBuffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        const int16 = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, int16, true);
        offset += 2;
      }
    }

    return new Blob([buffer], { type: 'audio/wav' });
  }

  // Convert WebM Blob to WAV
  private async convertWebMToWav(webmBlob: Blob): Promise<Blob> {
    try {
      // console.log('🔄 Converting WebM to WAV format...');
      this.isConvertingToWav = true;

      const audioContext = new AudioContext();
      const arrayBuffer = await webmBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const wavBlob = this.audioBufferToWav(audioBuffer);

      audioContext.close();
      this.isConvertingToWav = false;

      const sizeInMB = (wavBlob.size / (1024 * 1024)).toFixed(2);
      // console.log(`✅ Converted to WAV: ${sizeInMB} MB, ${audioBuffer.numberOfChannels} channels, ${audioBuffer.sampleRate}Hz`);

      return wavBlob;
    } catch (error) {
      // console.error('❌ Error converting to WAV:', error);
      this.isConvertingToWav = false;
      throw error;
    }
  }

  // Stop live streaming
  private async stopLiveStreaming(): Promise<void> {
    // console.log('🔴 Stopping live streaming...', new Date());

    // Stop the playback recorder first and wait for final blob
    if (this.mediaRecorderForLivePlayback && this.mediaRecorderForLivePlayback.state !== 'inactive') {
      // Setup promise to wait for final data
      const finalDataPromise = new Promise<void>((resolve) => {
        this.mediaRecorderForLivePlayback!.addEventListener('dataavailable', (event) => {
          if (event.data.size > 0) {
            this.liveStreamAudioChunks.push(event.data);
            // console.log(`📦 Final chunk added: ${event.data.size} bytes`, new Date());
          }
        }, { once: true });

        this.mediaRecorderForLivePlayback!.addEventListener('stop', () => {
          // console.log('⏹️ Live playback recorder stopped', new Date());
          resolve();
        }, { once: true });
      });

      this.mediaRecorderForLivePlayback.stop();
      await finalDataPromise;
      this.mediaRecorderForLivePlayback = null;
      // console.log('✅ Live playback recorder stopped and final data processed', new Date());
    }

    // Disconnect audio processing pipeline
    if (this.processorNode) {
      this.processorNode.disconnect();
      this.processorNode = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.streamAudioContext && this.streamAudioContext.state !== 'closed') {
      this.streamAudioContext.close();
      this.streamAudioContext = null;
    }

    //await this.liveKitService.stopAudioStreaming();

    // Allow final transcript messages to be received from LiveKit data channel.
    // console.log('⏳ Waiting for final transcriptions...', new Date());
    await this.waitForFinalTranscriptions();

    await this.stopLiveKitTranscriptionAndSession();
    //await this.liveKitService.disconnect();

    // Finalize any remaining interim transcription
    this.finalizeInterimTranscription();

    this.isStreaming = false;
    this.isTranscribing = false;
    this.serverConnected = false;
    // console.log('✅ Live streaming stopped');
  }

  // Finalize interim transcription
  private finalizeInterimTranscription(): void {
    if (this.interimTranscriptionSegment) {
      // console.log('📝 Finalizing remaining interim text:', this.interimTranscriptionSegment.text);

      this.addTranscriptionSegment({
        speaker: this.interimTranscriptionSegment.speaker,
        text: this.interimTranscriptionSegment.text,
        translatedText: this.interimTranscriptionSegment.translatedText,
        translationTarget: this.interimTranscriptionSegment.translationTarget,
        language: this.interimTranscriptionSegment.language,
        timestamp: this.interimTranscriptionSegment.timestamp,
        startTime: this.interimTranscriptionSegment.startTime,
        endTime: this.interimTranscriptionSegment.endTime,
        isFinal: true,
        trackId: this.interimTranscriptionSegment.trackId
      });

      this.saveTranscriptionToDb().catch(err => console.error('DB save error:', err));
      // console.log('✅ Interim text finalized and added to transcript');
    }
  }

  // Wait for final transcriptions with timeout
  private waitForFinalTranscriptions(): Promise<void> {
    return new Promise((resolve) => {
      // Wait up to 5 seconds for final transcriptions
      const timeout = setTimeout(() => {
        // console.log('⏱️ Timeout waiting for final transcriptions');
        resolve();
      }, 5000);

      // Listen for final transcription events
      let finalTranscriptCount = 0;
      const checkInterval = setInterval(() => {
        // If no new transcriptions for 2 seconds, consider it done
        const currentCount = this.finalTranscriptionSegments.length + (this.interimTranscriptionSegment ? 1 : 0);
        if (currentCount > finalTranscriptCount) {
          finalTranscriptCount = currentCount;
        } else {
          // No new transcriptions, we're done
          clearTimeout(timeout);
          clearInterval(checkInterval);
          // console.log('✅ Final transcriptions received');

          if (this.stopRequested && this.autoStartTranscription) {
            this.saveTranscriptionToServer(this.currentRoomName);
            // console.log('⏹ saveTranscriptionToServer requested:  transcription upload', new Date());
          } else {
            // console.log('⏹️ Stop requested: skipping transcription upload', new Date());
          }

          resolve();
        }
      }, 2000);
    });
  }

  // Helper delay function
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Pause recording
  async pauseRecording(): Promise<void> {
    if (this.isRecording) {
      this.recordingDetails.Events.push({ Action: 'Paused', Time: new Date().toISOString() });
      console.log("this.recordingDetails for pause::: ", JSON.stringify(this.recordingDetails));
      this.fileacces.pause();

      // Disable audio tracks on the stream to stop sending audio to LiveKit
      if (this.audioStream) {
        this.audioStream.getAudioTracks().forEach(track => track.enabled = false);
      }
      if (this.internalStream) {
        this.internalStream.getAudioTracks().forEach(track => track.enabled = false);
      }

      // Mute the LiveKit published track so the server stops receiving audio
      if (this.liveKitService.isConnected()) {
        await this.liveKitService.muteAudio();
      }

      // Pause audio player if playing
      if (this.audioPlayerElement && this.audioPlayerElement.nativeElement && this.isPlaying) {
        this.audioPlayerElement.nativeElement.pause();
        console.log('⏸️ Audio player paused');
      }

      // Pause batch mode recording
      if (this.mediaRecorder && this.streamingMode === 'batch' && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.pause();
      }

      // Pause live streaming playback recorder
      if (this.mediaRecorderForLivePlayback && this.streamingMode === 'live' && this.mediaRecorderForLivePlayback.state === 'recording') {
        this.mediaRecorderForLivePlayback.pause();

        // Create a temporary audio blob for playback while paused
        if (this.liveStreamAudioChunks.length > 0) {
          const webmBlob = new Blob(this.liveStreamAudioChunks, { type: 'audio/webm' });

          // Convert to WAV if format is set to WAV
          if (this.audioFormat === 'wav') {
            try {
              this.recordedAudioBlob = await this.convertWebMToWav(webmBlob);
              const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
              this.recordedAudioSize = `${sizeInMB} MB (WAV)`;
              console.log(`✅ Created temporary WAV blob for playback during pause: ${this.recordedAudioSize}`);
            } catch (error) {
              console.error('WAV conversion failed during pause, using WebM:', error);
              this.recordedAudioBlob = webmBlob;
              const sizeInMB = (webmBlob.size / (1024 * 1024)).toFixed(2);
              this.recordedAudioSize = `${sizeInMB} MB (WebM)`;
            }
          } else {
            this.recordedAudioBlob = webmBlob;
            const sizeInMB = (webmBlob.size / (1024 * 1024)).toFixed(2);
            this.recordedAudioSize = `${sizeInMB} MB (WebM)`;
            console.log(`✅ Created temporary WebM blob for playback during pause: ${this.recordedAudioSize}`);
          }
        }

        // Finalize any interim transcription when pausing
        this.finalizeInterimTranscription();
      }

      // For batch mode, create temporary blob for playback
      if (this.streamingMode === 'batch' && this.audioChunks.length > 0) {
        const webmBlob = new Blob(this.audioChunks, { type: 'audio/webm' });

        // Convert to WAV if format is set to WAV
        if (this.audioFormat === 'wav') {
          try {
            this.recordedAudioBlob = await this.convertWebMToWav(webmBlob);
            const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
            this.recordedAudioSize = `${sizeInMB} MB (WAV)`;
            console.log(`✅ Created temporary WAV blob for playback during pause: ${this.recordedAudioSize}`);
          } catch (error) {
            console.error('WAV conversion failed during pause, using WebM:', error);
            this.recordedAudioBlob = webmBlob;
            const sizeInMB = (webmBlob.size / (1024 * 1024)).toFixed(2);
            this.recordedAudioSize = `${sizeInMB} MB (WebM)`;
          }
        } else {
          this.recordedAudioBlob = webmBlob;
          const sizeInMB = (webmBlob.size / (1024 * 1024)).toFixed(2);
          this.recordedAudioSize = `${sizeInMB} MB (WebM)`;
          console.log(`✅ Created temporary WebM blob for playback during pause: ${this.recordedAudioSize}`);
        }
      }
      this.prepareModelandAddAnnotation("System: Pause Recording", "Pause: " + this.currentCaseNumber + ".", new Date().toISOString());
      // Update native player source
      setTimeout(() => this.updateNativePlayerSource(), 100);

      // For live streaming, keep isRecording true but set isPaused flag
      // This prevents the audio processor from stopping
      this.pauseStartTime = Date.now();
      this.updateRecordingClock(this.pauseStartTime);
      this.stopTimer();
      this.startPausedForTimer();
      this.ngZone.run(() => {
        this.isPaused = true;
        this.recordingStatus = 'Paused';
      });
      console.log('⏸️ Recording paused - audio chunks will be skipped but session remains active');
    }
  }

  // Resume recording
  async resumeRecording(): Promise<void> {
    if (this.isPaused) {
      this.recordingDetails.Events.push({ Action: 'Resumed', Time: new Date().toISOString() });
      console.log("this.recordingDetails for resume::: ", JSON.stringify(this.recordingDetails));

      // Re-enable audio tracks on the stream
      if (this.audioStream) {
        this.audioStream.getAudioTracks().forEach(track => track.enabled = !this.isMuted);
      }
      if (this.internalStream) {
        this.internalStream.getAudioTracks().forEach(track => track.enabled = !this.isMuted);
      }

      // Unmute the LiveKit published track so the server resumes receiving audio
      if (this.liveKitService.isConnected()) {
        if (this.isMuted) {
          await this.liveKitService.muteAudio();
        } else {
          await this.liveKitService.unmuteAudio();
        }
      }

      // Resume batch mode recording
      if (this.mediaRecorder && this.streamingMode === 'batch' && this.mediaRecorder.state === 'paused') {
        this.mediaRecorder.resume();
      }

      // Resume live streaming playback recorder
      if (this.mediaRecorderForLivePlayback && this.streamingMode === 'live' && this.mediaRecorderForLivePlayback.state === 'paused') {
        this.mediaRecorderForLivePlayback.resume();
      }
      this.prepareModelandAddAnnotation("System: Resume Recording", "Resume: " + this.currentCaseNumber + ".", new Date().toISOString());
      // For live streaming, resume sending audio chunks
      this.pausedDuration += Date.now() - this.pauseStartTime;
      this.stopPausedForTimer();
      this.ngZone.run(() => {
        this.isPaused = false;
        this.recordingStatus = this.streamingMode === 'live' ? 'Live Streaming...' : 'Recording...';
      });
      this.startTimer();
      this.fileacces.resume();
      this.snackBar.open('Recording resumed', 'Close', { duration: 2000 });
      console.log('▶️ Recording resumed');
    }
  }

  async stopStreaming() {
    if (this.currentRoomName) {
      try {
        const stopSessionRequest: StopStreamingSessionRequest = {
          roomName: this.currentRoomName,
          //egressId: this.egressId,
          deleteRoom: true,
          recordingDetails: JSON.stringify(this.recordingDetails)
        };
        // console.log("stopSessionRequest:::", stopSessionRequest, new Date());
        const response = await this.backendService.stopstreamingsession(stopSessionRequest).toPromise();
        const data = this.unwrapApiData<StopTranscriptionResponse>(response);
        //this.recordingStopDateTime = data.stoppedAt || new Date().toISOString();
        this.prepareModelandAddAnnotation("System: Stop Recording", "Stop: " + this.currentCaseNumber + ".", this.recordingStopDateTime);

        // console.log('✅ Streaming session stopped', new Date());
      } catch (error) {
        console.warn('⚠️ stopstreamingsession failed:', error);
      }
    }
  }
  // Stop recording
  confirmStopRecording(): void {
    if (!(this.isRecording || this.isPaused) || this.isStoppingRecording) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmClearDialogComponent, {
      width: '450px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'recording-confirm-dialog-panel',
      disableClose: true,
      data: {
        title: 'Stop & Finalize Recording?',
        message: 'This will stop and finalize the recording. Do you want to continue?',
        confirmLabel: 'OK',
        confirmBackgroundColor: '#2CABE2',
        showStats: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        void this.stopRecording();
      }
    });
  }

  async stopRecording(): Promise<void> {
    if (!(this.isRecording || this.isPaused) || this.isStoppingRecording) {
      return;
    }
    this.isStoppingRecording = true;
    this.stopRequested = true;
    this.recordingStatus = 'Stopping & finalizing...';
    const stoppedAt = new Date();
    this.updateRecordingClock(stoppedAt.getTime());
    this.finishRecordingCase(this.selectedCaseDetailsId, stoppedAt);
    this.recordingDetails.AudioEnd = stoppedAt.toISOString();
    this.recordingStopDateTime = this.recordingDetails.AudioEnd;
    this.stopTimer();
    this.stopPausedForTimer();
    localStorage.setItem('IsStreamingRecording', 'false');
    this.fileacces.stop();

    try {
      this.idle.watch();
      this.wakeLockService.setRecordingActive(false);
      await this.liveKitService.disconnect();
      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
        this.mediaRecorder.ondataavailable = null;
      }
      // console.log("this.recordingDetails for stop::: ", JSON.stringify(this.recordingDetails));
      await this.stopStreaming();

      // Stop audio player and reset playback state
      if (this.audioPlayerElement && this.audioPlayerElement.nativeElement) {
        this.audioPlayerElement.nativeElement.pause();
        this.audioPlayerElement.nativeElement.currentTime = 0;
        this.isPlaying = false;
        this.currentPlaybackTime = 0;
        console.log('⏹️ Audio player stopped and reset');
      }

      // Stop DB saving
      this.stopDbSaving();
      // Handle WASAPI mode
      if (this.recordingMode === 'wasapi') {
        this.isRecording = false;
        this.isPaused = false;
       
        this.updateFormControlsState(false);

        await this.stopWasapiRecording();
        this.recordingStatus = '';

        // Note: Session data kept in IndexedDB until Reset is clicked
        // console.log('✅ WASAPI recording stopped - data preserved in IndexedDB');

        this.snackBar.open('WASAPI recording stopped', 'Close', { duration: 3000 });
        return;
      }

      // Browser Audio API mode
      this.isRecording = false;
      this.isPaused = false;
      // this.wakeLockService.releaseWakeLock();

      if (this.streamingMode === 'live' && this.isStreaming) {
        this.recordingStatus = 'Finalizing transcription...';
        await this.stopLiveStreaming();

        // Wait for live stream audio blob to be created
        await this.waitForLiveStreamBlobCreation();
      }

      // Stop MediaRecorder if in batch mode


      // In batch mode, stopLiveStreaming() is not called, so enforce stop API flow here.
      if (this.streamingMode !== 'live') {
        await this.stopLiveKitTranscriptionAndSession();
        await this.liveKitService.disconnect();
      }

      this.isTranscribing = false;
      this.recordingStatus = '';
      // this.stopAudioVisualizer(); // DISABLED

      [this.audioStream, this.internalStream, this.externalStream].forEach(stream => {
        stream?.getTracks().forEach(track => track.stop());
      });
      this.internalStream = null;
      this.externalStream = null;

      // Convert to WAV if needed for live streaming
      if (this.streamingMode === 'live' && this.recordedAudioBlob && this.audioFormat === 'wav') {
        try {
          const webmBlob = this.recordedAudioBlob;
          this.recordedAudioBlob = await this.convertWebMToWav(webmBlob);
          const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
          this.recordedAudioSize = `${sizeInMB} MB (WAV)`;
        } catch (error) {
          console.error('WAV conversion failed for live stream:', error);
        }
      }

      // Update native audio player with final recording
      //setTimeout(() => {
      this.updateNativePlayerSource();
      // console.log('🎵 Audio player updated with final recording');
      //this.annotationsComponent.closeAnnotationWindow();
      this.showParticipants = false;

      //}, 500);

      // Note: Session data kept in IndexedDB until Reset is clicked
      // console.log('✅ Recording stopped - data preserved in IndexedDB');
      this.isRecordingDetailsExpanded = true;
      this.isCaseDetailsExpanded = true;
      this.isFormsExpanded = true;
      this.snackBar.open(
        this.streamingMode === 'live' ? 'Streaming stopped - transcription finalized' : 'Recording stopped',
        'Close',
        { duration: 3000 }
      );
    } finally {
      // Always return the page to the idle state, even if a stop step above threw.
      this.isRecording = false;
      this.isPaused = false;
      this.updateFormControlsState(false);
      this.isMuted = false;
      this.isRecordingDetailsExpanded = true;
      this.isCaseDetailsExpanded = true;
      this.isFormsExpanded = true;
      this.stopAudioAnalyzer();
      this.closeMixerAudioContext();
      this.cleanupCurrentSession();
      this.isStoppingRecording = false;
      this.currentRoomName = '';
      this.showAnnotations = false;
      this.isAnnotationsExpanded = false;
      this.showRecordingDetails = true;
      this.showCaseDetails = true;
      this.recordingCaseRows = [];
      this.clearActiveTranscriptionForNewRecording();
      this.clearCaseDetails();
      this.currentCaseNumber = '';
      this.resetFormsForNewCase();
      this.audioDuration = 0;
      this.recordingStartDateTime = '';
    }
  }
  async saveTranscriptionToServer(roomName: string): Promise<void> {
    var finalTranscripts = this.transcriptResponses.filter(tr => tr.is_final);
    // console.log('💾 Saving transcription ', this.transcriptResponses, roomName);
    // console.log('💾 Saving finalTranscripts ', finalTranscripts, roomName);
    const transcriptsModel: TranscriptJsonModel = {
      SessionId: roomName,
      Transcripts: finalTranscripts
    };
    const payload: GetFileUploadModel = {
      fileName: roomName + ".html",
      contentType: "text/html",
      roomName: roomName,
      fileType: "Transcription"
    };
    var response = await this.backendService.getFileUploadURL(payload).toPromise();
    // console.log('💾  transcription result', response);
    const parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;

    if (parsedResponse.success && parsedResponse.data?.presignedUrl) {
      const targetUrl = parsedResponse.data.presignedUrl;

      // Execute the upload using your array!
      this.uploadTranscriptToS3(targetUrl, transcriptsModel);
    } else {
      console.error('Failed to get a valid pre-signed URL');
    }
  }

  public uploadTranscriptToS3(presignedUrl: string, transcriptsModel: TranscriptJsonModel): void {
    // Send the model object directly. Angular's HttpClient will serialize it to JSON.
    const htmlContent = this.generateHtmlString();

  // 2. Set Content-Type to text/html
  const headers = new HttpHeaders({ 'Content-Type': 'text/html' });

  // console.log('📤 Uploading transcription HTML to S3 (object PUT)...');

  // 3. Upload the HTML string directly as the body
  this.http.put(presignedUrl, htmlContent, { headers, responseType: 'text' as 'json' }).subscribe({
      next: () => {
        // console.log('🎉 Transcription HTML uploaded to S3 successfully!');
      },
      error: (err) => {
        console.error('❌ S3 Upload failed:', err);
      }
    });
  }
private generateHtmlString(): string {
  let bodyContent = '';

  if (this.finalTranscriptionSegments && this.finalTranscriptionSegments.length > 0) {
    bodyContent = this.finalTranscriptionSegments
      .map((segment) => {
        if (segment.isCaseMarker) {
          return `<p class="transcription-case-marker" style="text-align: center; font-weight: bold; color: #4a5568; margin: 12px 0;">
            ----- New Case Started: ${this.escapeHtml(segment.caseLabel || 'Unknown')} (${this.formatTimestamp(segment.startTime)}) -----
          </p>`;
        }

        const timestamp = this.showTimestamps
          ? `[${this.formatTimestamp(segment.startTime)} - ${this.formatTimestamp(segment.endTime)}] `
          : '';

        let textContent = '';
        if (this.hasSegmentTranslation(segment)) {
          textContent = `
            <p style="margin: 4px 0 2px 12px;"><em style="color: #1976d2; font-style: italic;">(source):</em> ${this.escapeHtml(segment.text)}</p>
            <p style="margin: 8px 0 0 12px;"><em style="color: #388e3c; font-style: italic; font-weight: 700;">(translated):</em> <strong>${this.escapeHtml(segment.translatedText)}</strong></p>
          `;
        } else {
          textContent = `
            <p style="margin: 4px 0 0 12px;">${this.escapeHtml(segment.text)}</p>
          `;
        }

        // Added an explicit empty <p>&nbsp;</p> at the bottom of each segment set
        return `<p class="transcription-segment" style="margin-bottom: 0px; padding-bottom: 0px;">
          <span contenteditable='false' class="transcription-speaker" style="font-weight: bold; display: block; margin-bottom: 4px;">
            ${timestamp}<span>🎙️ ${this.escapeHtml(segment.speaker)}:</span>
          </span>
          <span class="transcription-text">
            ${textContent}
          </span>
          <p style="margin: 0; min-height: 16px; line-height: 1.5;">&nbsp;</p>
        </p>`;
      })
      .join('');
  } else if (this.transcriptionText) {
    bodyContent = `<p class="legacy-transcription-text" style="font-size: 14px; line-height: 1.6; color: #333;">${this.escapeHtml(this.transcriptionText)}</p>`;
  } else {
    bodyContent = `<p class="transcription-empty" style="color: #94a3b8; text-align: center; padding: 20px 0;">Transcription will appear here...</p>`;
  }

  return `<p class="transcription-display" style="max-width: 800px; margin: 0 auto; background: #fff; padding: 20px; border-radius: 8px;">
    ${bodyContent.trim()}
  </p>`.trim();
}

private escapeHtml(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
  // Reset recording - clear all data for new recording
  resetRecording(): void {
    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmClearDialogComponent, {
      width: '450px',
      data: {
        title: 'Reset Recording?',
        message: 'This will clear all audio and transcription data. Do you want to continue?',
        wordCount: this.wordCount,
        characterCount: this.characterCount,
        showStats: this.transcriptionText && this.transcriptionText.trim().length > 0
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // User confirmed, clear all data
        this.clearAllData();
        this.snackBar.open('Recording reset - ready for new recording', 'Close', { duration: 2000 });
      }
    });
  }

  // Wait for live stream blob to be created
  private async waitForLiveStreamBlobCreation(): Promise<void> {
    // Give the mediaRecorderForLivePlayback stop event time to create the blob
    //await this.delay(300);

    // Create final blob from live stream chunks if not already created
    if (this.liveStreamAudioChunks.length > 0) {
      this.recordedAudioBlob = new Blob(this.liveStreamAudioChunks, { type: 'audio/webm' });
      const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
      this.recordedAudioSize = `${sizeInMB} MB`;
      // console.log(`✅ Final live stream audio blob created: ${this.recordedAudioSize}`);
    }
  }

  // Handle recording stop
  private async handleRecordingStop(): Promise<void> {
    const webmBlob = new Blob(this.audioChunks, { type: 'audio/webm' });

    // Convert to WAV if format is set to WAV
    if (this.audioFormat === 'wav') {
      try {
        this.recordedAudioBlob = await this.convertWebMToWav(webmBlob);
        const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
        this.recordedAudioSize = `${sizeInMB} MB (WAV)`;
        this.successMessage = 'Recording completed and converted to WAV!';
      } catch (error) {
        console.error('WAV conversion failed, keeping WebM format:', error);
        this.recordedAudioBlob = webmBlob;
        const sizeInMB = (webmBlob.size / (1024 * 1024)).toFixed(2);
        this.recordedAudioSize = `${sizeInMB} MB (WebM)`;
        this.successMessage = 'Recording completed (WebM format - WAV conversion failed)';
      }
    } else {
      this.recordedAudioBlob = webmBlob;
      const sizeInMB = (webmBlob.size / (1024 * 1024)).toFixed(2);
      this.recordedAudioSize = `${sizeInMB} MB (WebM)`;
      this.successMessage = 'Recording completed successfully!';
    }

    // Update native player source
    setTimeout(() => this.updateNativePlayerSource(), 100);
  }

  // Timer functions
  private updateRecordingClock(now: number = Date.now()): void {
    this.recordingCaseClock = this.isPaused && this.pauseStartTime > 0 ? this.pauseStartTime : now;
    const elapsed = this.recordingCaseClock - this.recordingStartTime - this.pausedDuration;
    this.recordingDuration = this.formatDurationHms(elapsed / 1000);
  }

  private startTimer(): void {
    this.updateRecordingClock();
    this.recordingTimer = setInterval(() => {
      this.updateRecordingClock();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
  }

  private startPausedForTimer(): void {
    this.pausedForDuration = '00:00:00';
    const start = Date.now();
    this.pausedForTimer = setInterval(() => {
      // Do not touch recordingCaseClock here: it drives the case grid's live duration, which
      // must stay frozen (not keep ticking) while the recording is paused.
      const totalSeconds = Math.floor((Date.now() - start) / 1000);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      this.pausedForDuration =
        `${h.toString().padStart(2, '0')}:` +
        `${m.toString().padStart(2, '0')}:` +
        `${s.toString().padStart(2, '0')}`;
    }, 1000);
  }

  private stopPausedForTimer(): void {
    if (this.pausedForTimer) {
      clearInterval(this.pausedForTimer);
      this.pausedForTimer = null;
    }
    this.pausedForDuration = '00:00:00';
  }

  // Audio Visualizer - DISABLED
  /*
  private setupAudioVisualizer(stream: MediaStream): void {
    // Wait for canvas to be available in DOM
    setTimeout(() => {
      if (!this.audioCanvas) {
        console.warn('⚠️ Audio canvas not available for visualizer');
        return;
      }

      try {
        this.visualizerAudioContext = new AudioContext();
        this.analyser = this.visualizerAudioContext.createAnalyser();
        const source = this.visualizerAudioContext.createMediaStreamSource(stream);
        source.connect(this.analyser);
        this.analyser.fftSize = 256;
        console.log('🎨 Audio visualizer initialized');
        this.drawVisualizer();
      } catch (error) {
        console.error('❌ Error setting up audio visualizer:', error);
      }
    }, 100);
  }

  private drawVisualizer(): void {
    if (!this.analyser || !this.audioCanvas) {
      console.warn('⚠️ Analyser or canvas not available');
      return;
    }

    const canvas = this.audioCanvas.nativeElement;
    const canvasContext = canvas.getContext('2d');
    if (!canvasContext) {
      console.warn('⚠️ Could not get canvas context');
      return;
    }

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    console.log('✅ Starting visualizer animation');

    const draw = () => {
      this.animationFrame = requestAnimationFrame(draw);
      this.analyser!.getByteFrequencyData(dataArray);

      canvasContext.fillStyle = 'rgb(249, 249, 249)';
      canvasContext.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;
        // Purple gradient matching the theme
        const gradient = canvasContext.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
        gradient.addColorStop(0, '#ab47bc');
        gradient.addColorStop(1, '#8e24aa');
        canvasContext.fillStyle = gradient;
        canvasContext.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        x += barWidth + 1;
      }
    };

    draw();
  }

  private stopAudioVisualizer(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.visualizerAudioContext) {
      this.visualizerAudioContext.close();
      this.visualizerAudioContext = null;
    }
  }
  */

  // Toggle streaming mode
  toggleStreamingMode(mode: 'batch' | 'live'): void {
    if (!this.isRecording) {
      // Check if there's existing transcription or audio
      const hasTranscription = this.transcriptionText && this.transcriptionText.trim().length > 0;
      const hasAudio = this.recordedAudioBlob !== null;

      if (hasTranscription || hasAudio) {
        // Show confirmation dialog
        const dialogRef = this.dialog.open(ConfirmClearDialogComponent, {
          width: '450px',
          data: {
            title: 'Switch Recording Mode?',
            message: `You have existing ${hasTranscription ? 'transcription' : ''} ${hasTranscription && hasAudio ? 'and' : ''} ${hasAudio ? 'audio recording' : ''}. Switching modes will clear this data. Do you want to continue?`,
            wordCount: this.wordCount,
            characterCount: this.characterCount,
            showStats: hasTranscription
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result === true) {
            // User confirmed, clear data and switch mode
            this.clearAllData();
            this.streamingMode = mode;
            this.snackBar.open(
              `Switched to ${mode === 'live' ? 'Live Streaming' : 'Batch Recording'} mode`,
              'Close',
              { duration: 2000 }
            );
          }
          // If user cancelled, do nothing - keep current mode
        });
      } else {
        // No data to clear, switch immediately
        this.streamingMode = mode;
        this.snackBar.open(
          `Switched to ${mode === 'live' ? 'Live Streaming' : 'Batch Recording'} mode`,
          'Close',
          { duration: 2000 }
        );
      }
    }
  }

  // Clear all transcription and audio data
  private clearAllData(): void {
    // Clear native player FIRST (before removing recordedAudioBlob which triggers *ngIf)
    if (this.audioPlayerElement && this.audioPlayerElement.nativeElement) {
      try {
        this.audioPlayerElement.nativeElement.pause();
        this.audioPlayerElement.nativeElement.src = '';
        this.audioPlayerElement.nativeElement.load();
        console.log('🎵 Audio player cleared');
      } catch (error) {
        console.warn('⚠️ Error clearing audio player:', error);
      }
    }

    // Clear transcription
    this.transcriptionText = '';
    this.fullTranscript = '';
    this.interimTranscription = '';
    this.currentInterimText = '';
    this.finalTranscriptionSegments = [];
    this.interimTranscriptionSegment = null;
    this.wordCount = 0;
    this.characterCount = 0;
    this.lastUpdated = null;
    this.transcriptResponses = [];

    // Clear audio (this will trigger *ngIf and remove player from DOM)
    this.recordedAudioBlob = null;
    this.recordedAudioSize = '';
    this.audioDuration = 0;
    this.currentPlaybackTime = 0;
    this.playbackProgress = 0;
    this.isPlaying = false;
    this.audioChannels = 0;
    this.audioSampleRate = 0;
    this.channelVolumes = [];
    this.showChannelControls = false;
    this.cleanupChannelRouting();

    // Clear audio chunks
    this.audioChunks = [];
    this.liveStreamAudioChunks = [];

    // Clean up IndexedDB session
    if (this.currentRecordingSessionId) {
      this.dbService.cleanupSession(this.currentRecordingSessionId)
        .then(() => console.log('✅ Session cleaned up from IndexedDB'))
        .catch(error => console.error('Error cleaning up session:', error));
      this.currentRecordingSessionId = '';
      this.audioChunkIndex = 0;
    }

    console.log('✅ All transcription and audio data cleared');
  }

  // Send audio to server
  async sendAudioToServer(): Promise<void> {
    if (!this.recordedAudioBlob) {
      this.errorMessage = 'No audio to send.';
      return;
    }

    try {
      this.isSending = true;
      this.uploadProgress = 0;
      this.errorMessage = '';
      this.successMessage = '';
      this.isTranscribing = true;

      const formData = new FormData();
      formData.append('audio', this.recordedAudioBlob, 'recording.webm');

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        if (this.uploadProgress < 90) {
          this.uploadProgress += 10;
        }
      }, 200);

      const response = await this.http.post<any>(this.apiEndpoint, formData, {
        headers: new HttpHeaders({
          // Add any required headers
        }),
        reportProgress: true
      }).toPromise();

      clearInterval(progressInterval);
      this.uploadProgress = 100;

      // Handle transcription response
      if (response && response.transcription) {
        this.transcriptionText = response.transcription;
        this.updateTranscriptionStats();
        this.addToHistory(response.transcription);
        this.successMessage = 'Audio sent and transcribed successfully!';
      } else {
        this.errorMessage = 'Transcription not available in response.';
      }

      this.isSending = false;
      this.isTranscribing = false;

      setTimeout(() => {
        this.uploadProgress = 0;
      }, 2000);

    } catch (error) {
      console.error('Error sending audio:', error);
      this.errorMessage = 'Failed to send audio to server. Please try again.';
      this.isSending = false;
      this.isTranscribing = false;
      this.uploadProgress = 0;
    }
  }

  // Transcription functions
  private updateTranscriptionStats(): void {
    const trimmed = this.transcriptionText.trim();
    this.wordCount = trimmed ? trimmed.split(/\s+/).length : 0;
    this.characterCount = this.transcriptionText.length;
    this.lastUpdated = new Date();
  }

  clearTranscription(): void {
    // Show confirmation dialog
    const dialogRef = this.dialog.open(ConfirmClearDialogComponent, {
      width: '400px',
      data: {
        wordCount: this.wordCount,
        characterCount: this.characterCount
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // User confirmed, clear the transcription
        this.transcriptionText = '';
        this.fullTranscript = '';
        this.interimTranscription = '';
        this.currentInterimText = '';
        this.finalTranscriptionSegments = [];
        this.interimTranscriptionSegment = null;
        this.wordCount = 0;
        this.characterCount = 0;
        this.lastUpdated = null;
        this.transcriptResponses = [];
        this.snackBar.open('Transcription cleared', 'Close', { duration: 2000 });
      }
    });
  }

  async toggleLiveTranscription(): Promise<void> {
    if (this.isTranscribing) {
      await this.stopLiveKitTranscriptionAndSession();
      this.ngZone.run(() => { this.isTranscribing = false; });
    } else {
      await this.startLiveKitTranscriptionSession();
      this.ngZone.run(() => { this.isTranscribing = true; });
    }
  }

  copyTranscription(): void {
    navigator.clipboard.writeText(this.transcriptionText).then(() => {
      this.snackBar.open('Transcription copied to clipboard', 'Close', { duration: 2000 });
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.snackBar.open('Failed to copy transcription', 'Close', { duration: 2000 });
    });
  }

  downloadTranscription(): void {
    const blob = new Blob([this.transcriptionText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transcription_${new Date().getTime()}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
    this.snackBar.open('Transcription downloaded', 'Close', { duration: 2000 });
  }

  formatTranscriptionText(text: string): string {
    if (!text) return '';

    const lines = text.split('\n');
    let formattedHTML = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check if line starts with timestamp format [HH:MM:SS - HH:MM:SS] or [HH:MM:SS]
      const timestampMatch = line.match(/^\[(\d{2}:\d{2}:\d{2}(?:\s*-\s*\d{2}:\d{2}:\d{2})?)\]\s*/);

      if (timestampMatch) {
        const timestamp = timestampMatch[1];
        const remainingText = line.substring(timestampMatch[0].length);

        // Add timestamp in gray color only if checkbox is checked
        if (this.showTimestamps) {
          formattedHTML += `<span style="color: #999; font-size: 0.9em;">[${timestamp}]</span> `;
        }

        // Check if remaining text has Speaker label
        if (remainingText.startsWith('Speaker ')) {
          const colonIndex = remainingText.indexOf(':');
          if (colonIndex > 0) {
            // Bold speaker label (without text after colon)
            const speakerLabel = remainingText.substring(0, colonIndex + 1);
            formattedHTML += `<strong style="color: #2196F3;">${speakerLabel}</strong><br>`;

            // Regular text after colon on new line
            const transcriptionText = remainingText.substring(colonIndex + 1).trim();
            if (transcriptionText) {
              formattedHTML += `${transcriptionText}`;
            }
          } else {
            // No colon, add remaining text as is
            formattedHTML += remainingText;
          }
        } else {
          // No speaker label, just add the text
          formattedHTML += remainingText;
        }
      } else if (line.startsWith('Speaker ')) {
        // Line starts with Speaker but no timestamp
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          // Bold speaker label (without text after colon)
          const speakerLabel = line.substring(0, colonIndex + 1);
          formattedHTML += `<strong style="color: #2196F3;">${speakerLabel}</strong><br>`;

          // Regular text after colon on new line
          const transcriptionText = line.substring(colonIndex + 1).trim();
          if (transcriptionText) {
            formattedHTML += `${transcriptionText}`;
          }
        } else {
          // No colon, add as regular text
          formattedHTML += line;
        }
      } else {
        // Regular line without timestamp or speaker label
        formattedHTML += line;
      }

      // Add newline if not last line
      if (i < lines.length - 1) {
        formattedHTML += '<br>';

        // Add extra spacing between transcription blocks
        // Check if next line starts with timestamp or speaker (new transcription block)
        if (i + 1 < lines.length) {
          const nextLine = lines[i + 1];
          if (nextLine.startsWith('[') || nextLine.startsWith('Speaker ')) {
            formattedHTML += '<br>'; // Extra blank line
          }
        }
      }
    }

    return formattedHTML;
  }

  toggleTimestamps(): void {
    this.showTimestamps = !this.showTimestamps;
    this.syncTranscriptionSnapshots();
  }



  // Called many times per change detection from the template, so avoid toLocaleString and cache by second.
  private formatTimestamp(seconds: number): string {
    if (seconds == null || seconds < 0) return '';
    const base = this.recordingStartTime > 0 ? this.recordingStartTime : Date.now();
    const isEpoch = seconds > 1e12;

    const absoluteMs = isEpoch ? seconds : base + seconds * 1000;
    const key = Math.floor(absoluteMs / 1000);
    const cached = this.timestampTextCache.get(key);
    if (cached !== undefined) {
      return cached;
    }

    const d = new Date(absoluteMs);
    const pad = (n: number) => (n < 10 ? '0' : '') + n;
    const text = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    if (this.timestampTextCache.size > 10000) {
      this.timestampTextCache.clear();
    }
    this.timestampTextCache.set(key, text);
    return text;
  }
  private timestampTextCache = new Map<number, string>();

  resetSpeakerTracking(): void {
    this.lastSpeaker = -1;
    this.lastSpeakerKey = '';
    this.currentSpeakerStartTime = 0;
    this.currentSpeakerEndTime = 0;
    this.currentParagraphLines = [];
  }

  private addToHistory(text: string): void {
    this.transcriptionHistory.unshift({
      text: text,
      timestamp: new Date()
    });

    // Keep only last 10 items
    if (this.transcriptionHistory.length > 10) {
      this.transcriptionHistory = this.transcriptionHistory.slice(0, 10);
    }
  }

  loadTranscription(index: number): void {
    const item = this.transcriptionHistory[index];
    this.finalTranscriptionSegments = [];
    this.interimTranscriptionSegment = null;
    this.transcriptionText = item.text;
    this.updateTranscriptionStats();
    this.snackBar.open('Transcription loaded', 'Close', { duration: 2000 });
  }

  deleteHistoryItem(index: number): void {
    this.transcriptionHistory.splice(index, 1);
    this.snackBar.open('History item deleted', 'Close', { duration: 2000 });
  }

  // Cleanup
  private cleanup(): void {
    this.stopRecording();
    this.stopTimer();
    // this.stopAudioVisualizer(); // DISABLED
    this.stopLiveStreaming();
    this.stopDbSaving();
  }

  // Audio Playback Methods (Native HTML5)
  ngAfterViewInit(): void {
    // Audio element is inside *ngIf="recordedAudioBlob"; only attempt when blob exists.
    if (this.recordedAudioBlob) {
      setTimeout(() => {
        this.updateNativePlayerSource();
      }, 0);
    }
  }

  updateNativePlayerSource(retryCount: number = 0): void {
    if (!this.recordedAudioBlob) {
      // Expected in initial page load or after reset when player section is hidden by *ngIf.
      return;
    }

    if (!this.audioPlayerElement || !this.audioPlayerElement.nativeElement) {
      // Wait briefly for Angular to render the audio element after recordedAudioBlob becomes available.
      if (retryCount < 5) {
        setTimeout(() => this.updateNativePlayerSource(retryCount + 1), 50);
        return;
      }

      console.warn('⚠️ Audio player element not found after retries - cannot load audio');
      return;
    }

    if (this.recordedAudioBlob.size === 0) {
      console.error('❌ Recorded audio blob is empty (0 bytes)');
      this.snackBar.open('Error: No audio data recorded', 'Close', { duration: 5000 });
      return;
    }

    try {
      const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
      console.log(`📥 Loading audio into player: ${sizeInMB} MB`);
      console.log(`   Blob type: ${this.recordedAudioBlob.type}`);
      console.log(`   Recording mode: ${this.recordingMode}`);

      // Set loading state
      this.isLoading = true;

      const audioElement = this.audioPlayerElement.nativeElement as HTMLAudioElement;

      // Revoke previous object URL to prevent memory leaks
      if (this.currentAudioUrl) {
        URL.revokeObjectURL(this.currentAudioUrl);
        console.log('🗑️ Revoked previous audio URL');
      }

      // Create object URL from the complete Blob
      this.currentAudioUrl = URL.createObjectURL(this.recordedAudioBlob);
      audioElement.src = this.currentAudioUrl;

      console.log(`✅ Audio URL created: ${this.currentAudioUrl.substring(0, 50)}...`);

      // Force the browser to load the complete audio
      // preload="auto" in HTML + load() ensures full buffering
      audioElement.load();

      console.log('✅ Audio source set, browser loading audio...');

      // Immediately detect duration using AudioContext (don't wait for loadedmetadata)
      this.detectAudioChannels();
    } catch (error) {
      console.error('❌ Error loading audio into player:', error);
      this.isLoading = false;
      this.snackBar.open('Error loading audio into player', 'Close', { duration: 5000 });
    }
  }

  onAudioLoaded(event: Event): void {
    const audio = event.target as HTMLAudioElement;

    // Check if duration is valid
    if (isFinite(audio.duration) && !isNaN(audio.duration)) {
      this.audioDuration = audio.duration;
      console.log(`🎵 Audio metadata loaded - Duration: ${this.formatTime(this.audioDuration)}`);

      // Metadata is loaded, but audio might not be fully buffered yet
      // Loading state will be cleared when canplaythrough fires
    } else {
      // Duration not available yet, use AudioContext as fallback
      console.log('⚠️ Duration not available from audio element, detecting via AudioContext...');
      this.detectAudioChannels();
      return;
    }

    // Detect audio channels and sample rate
    this.detectAudioChannels();
  }

  private async detectAudioChannels(): Promise<void> {
    try {
      if (!this.recordedAudioBlob) return;

      // For WASAPI recordings, use device info instead of trying to decode
      if (this.recordingMode === 'wasapi') {
        const device = this.wasapiDevices.find(d => d.id === this.selectedWasapiDevice);
        if (device) {
          this.audioChannels = device.maxChannels;
          this.audioSampleRate = this.resampleWasapiAudio ? 16000 : 48000;
          this.channelVolumes = new Array(this.audioChannels).fill(100);

          console.log(`🎵 WASAPI Audio info - Channels: ${this.audioChannels}, Sample Rate: ${this.audioSampleRate}Hz`);

          // Setup channel routing if multi-channel
          if (this.audioChannels > 1) {
            this.setupChannelRouting();
          }
          return;
        }
      }

      // For browser recordings, decode the audio to detect channels
      const audioContext = new AudioContext();
      const arrayBuffer = await this.recordedAudioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      this.audioChannels = audioBuffer.numberOfChannels;
      this.audioSampleRate = audioBuffer.sampleRate;

      // Initialize channel volumes (100% for all channels)
      this.channelVolumes = new Array(this.audioChannels).fill(100);

      // If duration wasn't available from audio element, get it from buffer
      if (!isFinite(this.audioDuration) || isNaN(this.audioDuration) || this.audioDuration === 0) {
        this.audioDuration = audioBuffer.duration;
        console.log(`🎵 Duration from AudioBuffer: ${this.formatTime(this.audioDuration)}`);
      }

      console.log(`🎵 Audio info - Channels: ${this.audioChannels}, Sample Rate: ${this.audioSampleRate}Hz, Duration: ${this.formatTime(this.audioDuration)}`);

      // Setup channel routing if multi-channel
      if (this.audioChannels > 1) {
        this.setupChannelRouting();
      }

      // Close the audio context to free resources
      audioContext.close();
    } catch (error) {
      console.error('Error detecting audio channels:', error);
      // Set defaults if detection fails
      this.audioChannels = 1;
      this.audioSampleRate = 0;
      this.channelVolumes = [100];
      if (!isFinite(this.audioDuration) || isNaN(this.audioDuration)) {
        this.audioDuration = 0;
      }
    }
  }

  onAudioFullyLoaded(event: Event): void {
    const audio = event.target as HTMLAudioElement;
    if (isFinite(audio.duration) && !isNaN(audio.duration)) {
      this.audioDuration = audio.duration;
      console.log(`✅ Complete audio loaded from IndexedDB - Duration: ${this.formatTime(this.audioDuration)}, Ready to play`);
    }
  }

  onAudioCanPlayThrough(event: Event): void {
    const audio = event.target as HTMLAudioElement;

    // Audio is fully buffered and ready for seeking/dragging
    this.isLoading = false;

    console.log(`🎬 Audio fully buffered and ready - Duration: ${this.formatTime(audio.duration)}`);
    console.log(`✅ Seeking/dragging is now enabled for complete ${this.formatTime(audio.duration)} audio`);
  }

  onTimeUpdate(event: Event): void {
    const audio = event.target as HTMLAudioElement;
    this.currentPlaybackTime = audio.currentTime;
    this.playbackProgress = (this.currentPlaybackTime / this.audioDuration) * 100;
  }


  onAudioPlay(event: Event): void {
    this.isPlaying = true;
    console.log('▶️ Playback started');
  }

  onAudioPause(event: Event): void {
    this.isPlaying = false;
    console.log('⏸️ Playback paused');
  }

  changePlaybackSpeed(speed: number): void {
    this.playbackSpeed = speed;
    if (this.audioPlayerElement) {
      this.audioPlayerElement.nativeElement.playbackRate = speed;
      this.snackBar.open(`Playback speed: ${speed}x`, 'Close', { duration: 1500 });
      console.log(`⚡ Speed changed to ${speed}x`);
    }
  }

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private setupChannelRouting(): void {
    try {
      if (!this.audioPlayerElement) return;

      // Clean up existing routing if it exists
      this.cleanupChannelRouting();

      // Create AudioContext for playback control
      this.playbackAudioContext = new AudioContext();

      // Create source from audio element
      this.playbackSourceNode = this.playbackAudioContext.createMediaElementSource(
        this.audioPlayerElement.nativeElement
      );

      // Create splitter to separate channels
      this.playbackSplitter = this.playbackAudioContext.createChannelSplitter(this.audioChannels);

      // Create merger to combine channels back
      this.playbackMerger = this.playbackAudioContext.createChannelMerger(this.audioChannels);

      // Create gain node for each channel
      this.playbackGainNodes = [];
      for (let i = 0; i < this.audioChannels; i++) {
        const gainNode = this.playbackAudioContext.createGain();
        gainNode.gain.value = 1.0; // Start at 100%
        this.playbackGainNodes.push(gainNode);

        // Connect: splitter -> gain -> merger
        this.playbackSplitter.connect(gainNode, i);
        gainNode.connect(this.playbackMerger, 0, i);
      }

      // Connect the pipeline: source -> splitter, merger -> destination
      this.playbackSourceNode.connect(this.playbackSplitter);
      this.playbackMerger.connect(this.playbackAudioContext.destination);

      console.log(`🎛️ Channel routing setup complete for ${this.audioChannels} channels`);
    } catch (error) {
      console.error('Error setting up channel routing:', error);
    }
  }

  setChannelVolume(channelIndex: number, volume: number): void {
    this.channelVolumes[channelIndex] = volume;

    if (this.playbackGainNodes[channelIndex]) {
      // Convert percentage to gain (0-1)
      const gain = volume / 100;
      this.playbackGainNodes[channelIndex].gain.value = gain;
      console.log(`🎚️ Channel ${channelIndex + 1} volume set to ${volume}%`);
    }
  }

  getChannelLabel(index: number): string {
    if (this.audioChannels === 1) {
      return 'Mono';
    } else if (this.audioChannels === 2) {
      return index === 0 ? 'Left' : 'Right';
    } else {
      return `Channel ${index + 1}`;
    }
  }

  toggleChannelControls(): void {
    this.showChannelControls = !this.showChannelControls;
  }

  resetAllChannels(): void {
    for (let i = 0; i < this.audioChannels; i++) {
      this.setChannelVolume(i, 100);
    }
  }

  async downloadAudio(): Promise<void> {
    if (this.recordedAudioBlob) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const mode = this.streamingMode === 'live' ? 'livestream' : 'batch';
      const captureType = this.captureMode === 'system' ? 'system-audio' : 'microphone';

      // Determine format from blob type
      const isWav = this.recordedAudioBlob.type.includes('wav');
      const extension = isWav ? 'wav' : 'webm';
      const filename = `audio-recording-${mode}-${captureType}-${timestamp}.${extension}`;

      const audioUrl = URL.createObjectURL(this.recordedAudioBlob);
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(audioUrl);

      this.snackBar.open(`Audio downloaded: ${filename}`, 'Close', { duration: 3000 });
      console.log('💾 Audio downloaded:', filename);
    }
  }

  // Toggle audio format
  toggleAudioFormat(format: 'webm' | 'wav'): void {
    if (this.isRecording || this.isPaused) {
      this.snackBar.open('Stop recording before changing audio format', 'Close', { duration: 3000 });
      return;
    }

    this.audioFormat = format;
    const formatName = format === 'wav' ? 'WAV (PCM, uncompressed)' : 'WebM (Opus, compressed)';
    this.snackBar.open(`Audio format set to: ${formatName}`, 'Close', { duration: 2000 });
    console.log(`🎵 Audio format changed to: ${format.toUpperCase()}`);
  }

  // Get audio URL for native player
  getAudioUrl(): string {
    if (this.recordedAudioBlob) {
      return URL.createObjectURL(this.recordedAudioBlob);
    }
    return '';
  }

  // IndexedDB Failsafe Methods
  private async checkForPendingRecordings(): Promise<void> {
    try {
      const hasPending = await this.dbService.hasPendingSessions();
      if (hasPending) {
        const sessions = await this.dbService.getAllSessions();
        if (sessions.length > 0) {
          // Get the most recent session
          const latestSession = sessions.sort((a, b) => b.lastUpdated - a.lastUpdated)[0];
          const summary = await this.dbService.getSessionSummary(latestSession.id);

          // Show recovery dialog
          this.showRecoveryDialog(summary);
        }
      }
    } catch (error) {
      console.error('Error checking for pending recordings:', error);
    }
  }

  private showRecoveryDialog(summary: any): void {
    const dialogRef = this.dialog.open(RecordingRecoveryDialogComponent, {
      width: '500px',
      disableClose: true,
      data: summary
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === 'restore') {
        await this.restoreRecording(summary);
      } else if (result === 'discard') {
        await this.dbService.cleanupSession(summary.session.id);
        this.snackBar.open('Previous recording discarded', 'Close', { duration: 3000 });
      }
    });
  }

  private async restoreRecording(summary: any): Promise<void> {
    try {
      const session: RecordingSession = summary.session;
      const transcription: TranscriptionData = summary.transcription;

      // Restore session settings
      this.selectedMicrophone = session.microphone;
      this.streamingMode = session.mode;
      this.selectedLanguage = session.language;
      this.currentProvider = session.provider;
      this.selectedModel = session.model;
      this.currentSessionId = session.sessionId;
      this.recordingStartTime = session.startTime;
      this.pausedDuration = session.pausedDuration;
      this.currentRecordingSessionId = session.id;

      // Restore transcription if available
      if (transcription) {
        const transcriptionData: any = transcription as any;
        this.fullTranscript = transcription.fullTranscript;
        this.transcriptionText = transcription.fullTranscript;
        this.interimTranscription = transcription.interimTranscription;
        this.transcriptResponses = Array.isArray(transcriptionData.transcriptResponses)
          ? transcriptionData.transcriptResponses
          : [];
        this.wordCount = transcription.wordCount;
        this.characterCount = transcription.characterCount;
        this.lastUpdated = new Date(transcription.lastUpdated);
      }

      // Restore audio chunks for batch mode
      if (session.mode === 'batch') {
        const chunks = await this.dbService.getAudioChunks(session.id);
        if (chunks.length > 0) {
          // Convert ArrayBuffer chunks back to Blobs
          this.audioChunks = chunks.map(chunk => new Blob([chunk.data], { type: 'audio/webm' }));

          // Create combined audio blob
          this.recordedAudioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
          const sizeInMB = (this.recordedAudioBlob.size / (1024 * 1024)).toFixed(2);
          this.recordedAudioSize = `${sizeInMB} MB`;
        }
      }

      // Update recording duration display
      const elapsed = session.recordingDuration - session.pausedDuration;
      const minutes = Math.floor(elapsed / 60000);
      const seconds = Math.floor((elapsed % 60000) / 1000);
      this.recordingDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      this.snackBar.open('Recording restored successfully', 'Close', { duration: 3000 });
      console.log('✅ Recording restored from IndexedDB');
    } catch (error) {
      console.error('Error restoring recording:', error);
      this.snackBar.open('Failed to restore recording', 'Close', { duration: 3000 });
    }
  }

  private async saveRecordingSession(): Promise<void> {
    if (!this.currentRecordingSessionId) {
      return;
    }

    try {
      const session: RecordingSession = {
        id: this.currentRecordingSessionId,
        startTime: this.recordingStartTime,
        lastUpdated: Date.now(),
        mode: this.streamingMode,
        microphone: this.selectedMicrophoneName,
        language: this.selectedLanguage,
        provider: this.currentProvider,
        model: this.selectedModel,
        sessionId: this.currentSessionId,
        recordingDuration: Date.now() - this.recordingStartTime,
        pausedDuration: this.pausedDuration,
        isPaused: this.isPaused
      };

      await this.dbService.saveSession(session);
    } catch (error) {
      console.error('Error saving session to IndexedDB:', error);
    }
  }

  private async saveTranscriptionToDb(): Promise<void> {
    if (!this.currentRecordingSessionId || this.transcriptionVersion === this.savedTranscriptionVersion) {
      return;
    }

    const version = this.transcriptionVersion;
    try {
      const transcription: TranscriptionData = {
        sessionId: this.currentRecordingSessionId,
        fullTranscript: this.fullTranscript,
        interimTranscription: this.interimTranscription,
        transcriptResponses: this.transcriptResponses,
        wordCount: this.wordCount,
        characterCount: this.characterCount,
        lastUpdated: Date.now()
      };

      await this.dbService.saveTranscription(transcription);
      this.savedTranscriptionVersion = version;
    } catch (error) {
      console.error('Error saving transcription to IndexedDB:', error);
    }
  }

  /**
   * Resample WASAPI audio to 16kHz mono PCM for transcription
   * This method wraps resampleWasapiTo16kMono with current device settings
   * @param audioData - Raw PCM audio data from WASAPI service
   * @returns Resampled audio data ready for transcription (16kHz mono PCM WITH WAV header for streaming)
   */
  private async resampleAudioForTranscription(audioData: ArrayBuffer): Promise<ArrayBuffer> {
    try {
      // Get device info for source audio parameters
      const device = this.wasapiDevices.find(d => d.id === this.selectedWasapiDevice);
      const sourceChannels = device?.maxChannels || 2;
      const sourceSampleRate = 48000; // WASAPI service records at 48kHz

      console.log(`🔄 Resampling from ${sourceSampleRate}Hz ${sourceChannels}ch to 16kHz mono`);

      // Use existing resampling function - returns data WITH WAV header
      const resampledWithHeader = await this.resampleWasapiTo16kMono(audioData, sourceChannels, sourceSampleRate);

      // IMPORTANT: Keep the WAV header for streaming!
      // Most transcription services need the WAV header to understand audio format
      // The header contains critical info: sample rate, channels, bit depth

      console.log(`   Input: ${audioData.byteLength} bytes (${sourceSampleRate}Hz, ${sourceChannels}ch)`);
      console.log(`   Output: ${resampledWithHeader.byteLength} bytes (16kHz mono WAV)`);
      console.log(`   Format: WAV with header (needed for transcription service)`);

      return resampledWithHeader; // Return WITH WAV header
    } catch (error) {
      console.error('❌ Failed to resample audio:', error);
      throw error;
    }
  }

  /**
   * Resample WASAPI audio to 16kHz mono PCM for transcription
   * @param audioData - Source audio data (WAV format or raw PCM)
   * @param sourceChannels - Number of source channels
   * @param sourceSampleRate - Source sample rate (e.g., 48000)
   * @returns Resampled audio data (16kHz mono PCM with WAV header)
   */
  private async resampleWasapiTo16kMono(audioData: ArrayBuffer, sourceChannels: number, sourceSampleRate: number): Promise<ArrayBuffer> {
    try {
      // Check if the data already has a WAV header (starts with "RIFF")
      const dataView = new DataView(audioData);
      const hasWavHeader = dataView.getUint32(0, false) === 0x52494646; // "RIFF"

      let audioBufferToResample = audioData;

      // If no WAV header, assume raw PCM and add header
      if (!hasWavHeader) {
        console.log('🔧 Adding WAV header to raw PCM data');
        const wavHeader = this.createWavHeader(audioData.byteLength, sourceChannels, sourceSampleRate, 16);
        const wavData = new Uint8Array(wavHeader.byteLength + audioData.byteLength);
        wavData.set(new Uint8Array(wavHeader), 0);
        wavData.set(new Uint8Array(audioData), wavHeader.byteLength);
        audioBufferToResample = wavData.buffer;
      }

      // Create audio context for resampling
      const audioContext = new AudioContext({ sampleRate: 16000 });

      // Decode the source audio
      let audioBuffer;
      try {
        audioBuffer = await audioContext.decodeAudioData(audioBufferToResample.slice(0));
      } catch (decodeError) {
        console.error('Failed to decode audio data, trying manual PCM conversion:', decodeError);
        // Fallback: Manual PCM conversion
        await audioContext.close();
        return this.manualResamplePCM(audioData, sourceChannels, sourceSampleRate);
      }

      // Create offline context for resampling
      const offlineContext = new OfflineAudioContext(1, Math.ceil(audioBuffer.duration * 16000), 16000);

      // Create buffer source
      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;

      // Connect source to destination
      source.connect(offlineContext.destination);
      source.start(0);

      // Render the resampled audio
      const resampledBuffer = await offlineContext.startRendering();

      // Convert to PCM16
      const pcmData = new Int16Array(resampledBuffer.length);
      const channelData = resampledBuffer.getChannelData(0);

      for (let i = 0; i < resampledBuffer.length; i++) {
        // Convert float32 (-1 to 1) to int16 (-32768 to 32767)
        const sample = Math.max(-1, Math.min(1, channelData[i]));
        pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      }

      // Create WAV header for 16kHz mono PCM
      const wavHeader = this.createWavHeader(pcmData.byteLength, 1, 16000, 16);
      const wavData = new Uint8Array(wavHeader.byteLength + pcmData.byteLength);
      wavData.set(new Uint8Array(wavHeader), 0);
      wavData.set(new Uint8Array(pcmData.buffer), wavHeader.byteLength);

      // Close audio context
      await audioContext.close();

      return wavData.buffer;
    } catch (error) {
      console.error('Error resampling WASAPI audio:', error);
      // Return original data if resampling fails
      return audioData;
    }
  }

  /**
   * Manual PCM resampling (fallback when Web Audio API fails)
   */
  private manualResamplePCM(audioData: ArrayBuffer, sourceChannels: number, sourceSampleRate: number): ArrayBuffer {
    try {
      console.log('🔧 Using manual PCM resampling');

      // Convert input to Int16Array (assuming 16-bit PCM)
      const sourceData = new Int16Array(audioData);
      const sourceSamplesPerChannel = sourceData.length / sourceChannels;

      // Calculate output size
      const targetSampleRate = 16000;
      const resampleRatio = sourceSampleRate / targetSampleRate;
      const targetSamples = Math.floor(sourceSamplesPerChannel / resampleRatio);

      // Output is mono
      const outputData = new Int16Array(targetSamples);

      // Simple resampling: average all channels and downsample
      for (let i = 0; i < targetSamples; i++) {
        const sourceIndex = Math.floor(i * resampleRatio);
        let sum = 0;

        // Average all channels
        for (let ch = 0; ch < sourceChannels; ch++) {
          sum += sourceData[sourceIndex * sourceChannels + ch];
        }

        outputData[i] = Math.round(sum / sourceChannels);
      }

      // Create WAV with header
      const wavHeader = this.createWavHeader(outputData.byteLength, 1, targetSampleRate, 16);
      const wavData = new Uint8Array(wavHeader.byteLength + outputData.byteLength);
      wavData.set(new Uint8Array(wavHeader), 0);
      wavData.set(new Uint8Array(outputData.buffer), wavHeader.byteLength);

      console.log(`✅ Manual resample: ${sourceData.length} samples (${sourceChannels}ch ${sourceSampleRate}Hz) -> ${outputData.length} samples (1ch ${targetSampleRate}Hz)`);

      return wavData.buffer;
    } catch (error) {
      console.error('Error in manual PCM resampling:', error);
      return audioData;
    }
  }

  /**
   * Create WAV file header
   */
  /**
   * Auto-detect audio format by analyzing the audio data
   * Returns format parameters: sample rate, channels, bits per sample, isFloat
   */
  private detectAudioFormat(audioData: ArrayBuffer): {
    sampleRate: number;
    channels: number;
    bitsPerSample: number;
    isFloat: boolean;
  } {
    const view = new DataView(audioData);
    const byteLength = audioData.byteLength;

    // Default values
    let isFloat = false;
    let bitsPerSample = 16;
    let sampleRate = 16000;
    let channels = 1;

    if (byteLength < 128) {
      console.warn('⚠️ Audio chunk too small for reliable format detection');
      return { sampleRate, channels, bitsPerSample, isFloat };
    }

    // Analyze first 128 samples to determine format
    try {
      // Test 1: Check if values are in float range (-1.0 to 1.0)
      let likelyFloat = true;
      let likely16Bit = true;
      const samplesToCheck = Math.min(128, Math.floor(byteLength / 4));

      for (let i = 0; i < samplesToCheck; i++) {
        if (i * 4 >= byteLength) break;

        const floatVal = view.getFloat32(i * 4, true);

        // Check if value is reasonable for float (-1.0 to 1.0 range with some tolerance)
        if (!isNaN(floatVal) && Math.abs(floatVal) > 1.5) {
          likelyFloat = false;
        }

        // Check if it's reasonable as 16-bit PCM
        if (i * 2 < byteLength) {
          const int16Val = view.getInt16(i * 2, true);
          if (Math.abs(int16Val) > 32767) {
            likely16Bit = false;
          }
        }
      }

      // Determine format based on analysis
      if (likelyFloat && !likely16Bit) {
        isFloat = true;
        bitsPerSample = 32;
        console.log('   🔍 Format detected: 32-bit IEEE Float (values in -1.0 to 1.0 range)');
      } else if (likely16Bit) {
        isFloat = false;
        bitsPerSample = 16;
        console.log('   🔍 Format detected: 16-bit PCM (values in -32768 to 32767 range)');
      } else {
        console.warn('   ⚠️ Could not reliably detect format, defaulting to 16-bit PCM');
      }

      // Determine sample rate based on resample flag
      if (this.resampleWasapiAudio) {
        // If resample is requested, we expect 16kHz
        sampleRate = 16000;
        channels = 1;
        console.log('   📊 Expected format (resample=true): 16kHz mono');
      } else {
        // If no resample, we expect original format (usually 48kHz multi-channel)
        const device = this.wasapiDevices.find(d => d.id === this.selectedWasapiDevice);
        sampleRate = 48000; // Default WASAPI sample rate
        channels = device?.maxChannels || 2;
        console.log(`   📊 Expected format (resample=false): 48kHz ${channels}ch`);
      }

      // Log sample values for debugging
      const samples: string[] = [];
      for (let i = 0; i < 5 && i * (isFloat ? 4 : 2) < byteLength; i++) {
        if (isFloat) {
          samples.push(view.getFloat32(i * 4, true).toFixed(6));
        } else {
          samples.push(view.getInt16(i * 2, true).toString());
        }
      }
      console.log('   First 5 samples:', samples.join(', '));

    } catch (error) {
      console.error('   ❌ Error during format detection:', error);
    }

    return { sampleRate, channels, bitsPerSample, isFloat };
  }

  private createWavHeader(dataLength: number, channels: number, sampleRate: number, bitsPerSample: number, isFloat: boolean = false): ArrayBuffer {
    const header = new ArrayBuffer(44);
    const view = new DataView(header);

    // RIFF chunk descriptor
    view.setUint32(0, 0x52494646, false); // "RIFF"
    view.setUint32(4, 36 + dataLength, true); // File size - 8
    view.setUint32(8, 0x57415645, false); // "WAVE"

    // fmt sub-chunk
    view.setUint32(12, 0x666d7420, false); // "fmt "
    view.setUint32(16, 16, true); // Subchunk1 size (16 for PCM)
    view.setUint16(20, isFloat ? 3 : 1, true); // Audio format (1 = PCM, 3 = IEEE Float)
    view.setUint16(22, channels, true); // Number of channels
    view.setUint32(24, sampleRate, true); // Sample rate
    view.setUint32(28, sampleRate * channels * bitsPerSample / 8, true); // Byte rate
    view.setUint16(32, channels * bitsPerSample / 8, true); // Block align
    view.setUint16(34, bitsPerSample, true); // Bits per sample

    // data sub-chunk
    view.setUint32(36, 0x64617461, false); // "data"
    view.setUint32(40, dataLength, true); // Subchunk2 size

    return header;
  }

  private concatenateArrayBuffers(buffer1: ArrayBuffer, buffer2: ArrayBuffer): ArrayBuffer {
    const tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
    tmp.set(new Uint8Array(buffer1), 0);
    tmp.set(new Uint8Array(buffer2), buffer1.byteLength);
    return tmp.buffer;
  }

  private async saveAudioChunkToDb(audioData: ArrayBuffer): Promise<void> {
    if (!this.currentRecordingSessionId) {
      return;
    }

    try {
      const chunk: AudioChunkData = {
        sessionId: this.currentRecordingSessionId,
        timestamp: Date.now(),
        chunkIndex: this.audioChunkIndex++,
        data: audioData,
        size: audioData.byteLength
      };

      await this.dbService.saveAudioChunk(chunk);
    } catch (error) {
      console.error('Error saving audio chunk to IndexedDB:', error);
    }
  }

  private startDbSaving(): void {
    this.stopDbSaving();
    this.dbSaveInFlight = false;
    this.savedTranscriptionVersion = -1;
    // Save session and transcription every 5 seconds
    this.dbSaveInterval = setInterval(async () => {
      if (this.dbSaveInFlight) {
        return;
      }
      this.dbSaveInFlight = true;
      try {
        await this.saveRecordingSession();
        await this.saveTranscriptionToDb();
      } finally {
        this.dbSaveInFlight = false;
      }
    }, 5000);
  }

  private stopDbSaving(): void {
    // console.log('🛑 Stopping IndexedDB saving interval');
    if (this.dbSaveInterval) {
      clearInterval(this.dbSaveInterval);
      this.dbSaveInterval = null;
    }
  }

  private async cleanupCurrentSession(): Promise<void> {
    if (this.currentRecordingSessionId) {
      try {
        await this.dbService.cleanupSession(this.currentRecordingSessionId);
        this.currentRecordingSessionId = '';
        this.audioChunkIndex = 0;
        this.currentCaseNumber = '';
        // console.log('✅ Session cleaned up from IndexedDB');
      } catch (error) {
        console.error('Error cleaning up session:', error);
      }
    }
  }
  setupAudioAnalyzer(stream: MediaStream) {
    this.stopAudioAnalyzer();
    this.meterAudioContext = new AudioContext();
    if (this.meterAudioContext.state === 'suspended') {
      this.meterAudioContext.resume();
    }

    this.meterAnalyser = this.meterAudioContext.createAnalyser();
    this.meterAnalyser.fftSize = 2048;
    this.meterAnalyser.smoothingTimeConstant = 0.2;
    this.meterAnalyser.minDecibels = -90;
    this.meterAnalyser.maxDecibels = -6;

    this.meterSource = this.meterAudioContext.createMediaStreamSource(stream);
    this.meterSource.connect(this.meterAnalyser);

    const timeDomainData = new Float32Array(this.meterAnalyser.fftSize);

    // Sampled outside Angular at 10 Hz; only re-enter the zone when the displayed value changes.
    const updateLevel = () => {
      if (!this.meterAnalyser) {
        return;
      }

      let level = 0;
      if (this.isRecording && !this.isPaused) {
        this.meterAnalyser.getFloatTimeDomainData(timeDomainData);
        // Gate idle system/background output so a quiet or constant loopback does not sit at 100%.
        level = computeMeterLevel(timeDomainData, this.includeExternalAudio ? 0.08 : 0.045);
      }

      if (level !== this.micLevel) {
        this.ngZone.run(() => this.micLevel = level);
      }
    };

    this.ngZone.runOutsideAngular(() => {
      this.micLevelFrameId = window.setInterval(updateLevel, 100);
    });
  }

  private stopAudioAnalyzer(): void {
    if (this.micLevelFrameId !== null) {
      clearInterval(this.micLevelFrameId);
      this.micLevelFrameId = null;
    }
    if (this.meterSource) {
      this.meterSource.disconnect();
      this.meterSource = null;
    }
    if (this.meterAnalyser) {
      this.meterAnalyser.disconnect();
      this.meterAnalyser = null;
    }
    if (this.meterAudioContext && this.meterAudioContext.state !== 'closed') {
      this.meterAudioContext.close();
    }
    this.meterAudioContext = null;
    this.micLevel = 0;
  }

  /** Opening the playback channels menu is a click, so the player can be routed through Web Audio here. */
  attachPlaybackChannels(): void {
    const audio = this.audioRef?.nativeElement;
    if (audio) {
      this.playbackChannels.attach(audio);
    }
  }

  async loadOrganizations() {
    const orgs_res = await this.loadOrganizationValues();
    this.loadOrgsValuesForDropDown(orgs_res);
  }
  loadOrganizationValues() {
    this.organizationOptions = [];
    return this.backendService.getCustomers().toPromise();
  }
  loadOrgsValuesForDropDown(org_res: any) {
    this.organizationOptions = [];
    let orgsList = this.backendService.getJSONResponse(org_res).data.customerList;
    for (let org of orgsList) {
      let data = {
        "id": org.id,
        "customerName": org.customerName + ' (' + org.tenantName + ')',
        "custExtId": org.custExtId,
        "directBucket": org.directBucket,
        "dbId": org.dbId,
        "ncurl": org.ncurl,
        "tenantId": org.tenantId,
        "tenantName": org.tenantName,
        "emailTemplateId": org.emailTemplateId,
        "sourceType": org.sourceType,
        "isFutureOrder": org.isFutureOrder,
        "estimationMessage": org.estimationMessage,
        "sendOrderToNSW": org.sendOrderToNSW,
        "autoStartTranscription": org.autoStartTranscription,
        "isCourt": org.isCourt,
        "allowAnnotations": org.allowAnnotations
      }
      this.organizationOptions.push(data);
    }
    this.organizationfilteredOptions = this.organizationControl.valueChanges
      .pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : ''),
        map(name => name ? this.organizationfilter(name) : this.organizationOptions.slice())
      );
    if (this.organizationOptions.length == 1) {
      this.organizationControl.disable({ onlySelf: true });
      this.organizationControl.setValue(this.organizationOptions[0]);
      this.selectedOrgExtId = this.organizationOptions[0].custExtId;
      this.organizationSingleOptionChangeEvent(this.organizationOptions[0]);
      this.autoStartTranscription = this.organizationOptions[0].autoStartTranscription;
      this.allowAnnotations = this.organizationOptions[0].allowAnnotations;
    }
  }
  async organizationSingleOptionChangeEvent(event: any): Promise<void> {
    if (event.id) {
      let item = event;
      this.selectedOrganization = event;
      this.selectedOrgExtId = item.custExtId;
      this.clearCaseDetails();
      await this.setSelectedOrgIsCourt(item);
      if (this.selectedOrgIsCourt) {
        this.loadCourtLocations(item.custExtId);
      } else {
        this.clearCourtSelection();
      }
      this.loadFormsTypeOptions(item.custExtId);
    }
  };

  // Resolves whether the selected org is a court (gates the Select Case button).
  // Production: /customer/list returns isCourt on each org. Local/dev: that field is absent (the
  // deployed Lambda lacks the change), so fall back to the WebAPI test controller which reads the dev DB.
  private async setSelectedOrgIsCourt(item: any): Promise<void> {
    if (item && (item.isCourt === true || item.isCourt === false)) {
      this.selectedOrgIsCourt = item.isCourt;
      this.applyCaseDetailsValidators();
      return;
    }
    // Default to disabled until the dev-DB check confirms, so a stale 'true' from a previously
    // selected court org can't linger while the async fallback is in flight.
    this.selectedOrgIsCourt = false;
    try {
      const res: any = await this.backendService.getOrgIsCourt(item?.custExtId).toPromise();
      this.selectedOrgIsCourt = res?.isCourt === true || res?.data?.isCourt === true;
    } catch {
      this.selectedOrgIsCourt = false;
    }
    // Every exit path lands here, so this is the one place the Case Details rules are refreshed.
    this.applyCaseDetailsValidators();
  }

  // Case Details belong to the selected org: leaving one behind would send a previous court org's
  // case number and judge with the next recording, including recordings for a non-court org.
  private clearCaseDetails(): void {
    this.caseDetailsForm.reset({ caseNumber: '', hearingDateTime: null, judgeFirstName: '', judgeLastName: '' },
      { emitEvent: false });
    if (this.formsFormGroup) {
      this.formsFormGroup.reset({}, { emitEvent: false });
    }
    this.formsFieldValues = {};
    this.activeCaseFormValues = null;
    this.refreshAnnotationSpeakers();
    this.hearingDateTimeEdited = false;
    this.selectedCaseId = null;
    this.selectedCaseDetailsId = null;
    this.caseProfileName = '';
    this.caseNotes = '';
    this.selectedCasePeople = [];
    this.casePeopleGroups = [];
  }
  organizationChange() {
    this.selectedOrgExtId = '';
    this.selectedOrgIsCourt = false;
    this.clearCaseDetails();
    this.applyCaseDetailsValidators();
    this.loadFormsTypeOptions('');
    this.selectedcourtLocationExtId = '';
    this.selectedcourtRoomExtId = '';
    this.selectedcourtRoomName = '';
    this.courtLocationfilteredOptions = this.clearDropdownValues();
    this.courtRoomfilteredOptions = this.clearDropdownValues();
    this.courtLocationControl.setValue(null);
    this.courtRoomControl.setValue(null);
    this.courtLocationControl.enable({ onlySelf: true });
    this.courtRoomControl.enable({ onlySelf: true });
  }
  private clearCourtSelection(): void {
    this.courtlocationsOptions = [];
    this.courtRoomsOptions = [];
    this.selectedcourtLocationExtId = '';
    this.selectedcourtLocationName = '';
    this.selectedcourtRoomExtId = '';
    this.selectedcourtRoomName = '';
    this.courtLocationControl.setValue(null);
    this.courtRoomControl.setValue(null);
  }
  async organizationChangeEvent(event: any) {
    this.spinner.show();
    if (event.option.value.id) {
      let item = event.option.value;
      this.selectedOrganization = event;
      this.selectedOrgExtId = item.custExtId;
      this.courtLocationControl.enable({ onlySelf: true });
      this.courtRoomControl.enable({ onlySelf: true });
      this.clearCaseDetails();
      await this.setSelectedOrgIsCourt(item);
      if (this.selectedOrgIsCourt) {
        this.loadCourtLocations(item.custExtId);
      } else {
        this.clearCourtSelection();
        this.spinner.hide();
      }
      this.autoStartTranscription = item.autoStartTranscription;
      this.allowAnnotations = item.allowAnnotations;
      this.loadFormsTypeOptions(item.custExtId);
    }
  };
async organizationByOrgExtId(event: any) {
  // console.log("orgExtId: ",event);

  this.spinner.show();
  await this.loadFormsTypeOptions(this.selectedDepartmentExtId);
  this.backendService.getOrganizationByExtId(event).subscribe({
    next: async (resp: any) => {
      try {
        const jsonData = this.backendService.getJSONResponse(resp);

       // console.log('Organization:', jsonData);

        if (!jsonData?.data) {
          console.warn('Organization not found for:', event);
          this.clearCaseDetails();
          return;
        }

        const item = jsonData.data;

        this.selectedOrganization = item;
        this.selectedOrgExtId = item.orgExtId;

        this.courtLocationControl.enable({ onlySelf: true });
        this.courtRoomControl.enable({ onlySelf: true });
        this.clearCourtSelection();
        this.courtLocationfilteredOptions = this.clearDropdownValues();
        this.courtRoomfilteredOptions = this.clearDropdownValues();

        this.clearCaseDetails();

        await this.setSelectedOrgIsCourt(item);

        if (this.selectedOrgIsCourt) {
          this.loadCourtLocations(item.orgExtId);
        } else {
          this.clearCourtSelection();
        }

        this.autoStartTranscription = item.autoStartTranscription;
        this.allowAnnotations = item.allowAnnotations;

       

      } catch (error) {
        console.error('Error processing organization:', error);
        this.clearCaseDetails();

      } finally {
        this.spinner.hide();
      }
    },

    error: (error) => {
      console.error('Error loading organization:', error);

      this.clearCaseDetails();
      this.spinner.hide();
    }
  });
}

  displayFnForOrganization(dropdownmodel: Organizations): string {
    return dropdownmodel && dropdownmodel.customerName ? dropdownmodel.customerName : '';
  }

  private organizationfilter(name: any): Organizations[] {
    const filterValue = name.toLowerCase();
    return this.organizationOptions.filter(option => option.customerName.toLowerCase().indexOf(filterValue) === 0);
  }
  async loadCourtLocations(orgExtId: any) {
    try {
      this.spinner.show();
      const response: any = await this.loadCourtLocationsValues(orgExtId);
      const courtlocsList = response?.data?.result;
      if (!courtlocsList || courtlocsList.length === 0) {
        this.courtlocationsOptions = [];
        // Only reached for a court org, where a court location is mandatory - so an empty list
        // means recording is blocked until one is configured in NetScribe, not that it is optional.
        this.sendNotification('No court locations available for the selected organization. A court location must be configured in NetScribe before recording.', 'warning');
        this.spinner.hide();
        return;
      }
      this.loadCourtLocationsValuesForDropDown(courtlocsList);
    } catch (error) {
      this.sendNotification('Error loading court locations.', 'error');
    }
  }
  async loadCourtLocationsValues(orgExtId: any) {
    try {
      this.courtlocationsOptions = [];
      return this.backendService.getCourtLocations(orgExtId).toPromise();
    } catch (error) {
      console.log('Error loading court locations', error);
    } finally {
      //this.spinner.hide();
    }
  }
  loadCourtLocationsValuesForDropDown(courtlocs_res: any) {
    this.courtlocationsOptions = [];
    let courtlocsList = this.backendService.getJSONResponse(courtlocs_res);
    for (let cl of courtlocsList) {
      let data = {
        "id": cl.id,
        "name": cl.name,
        "address": cl.address,
        "comment": cl.comment,
        "externalId": cl.externalId
      }
      this.courtlocationsOptions.push(data);
    }
    if (this.courtlocationsOptions.length == 0) {
      this.sendNotification('No courtlocations in netScribe for this organization.', 'error')
    }
    else {
      this.courtLocationfilteredOptions = this.courtLocationControl.valueChanges
        .pipe(
          startWith(''),
          map(value => typeof value === 'string' ? value : ''),
          map(name => name ? this.courtLocationsfilter(name) : this.courtlocationsOptions.slice())
        );
      if (this.courtlocationsOptions.length == 1) {
        this.courtLocationControl.disable({ onlySelf: true });
        this.courtLocationControl.setValue(this.courtlocationsOptions[0]);
        this.selectedcourtLocationExtId = this.courtlocationsOptions[0].externalId;
        this.courtLocationSingleOptionChangeEvent(this.courtlocationsOptions[0]);
      }
      else {
        this.spinner.hide();
      }
    }
  }
  courtLocationSingleOptionChangeEvent(event: any): void {
    if (event.id) {
      if (!event.externalId) {
        this.rejectCourtLocationSelection();
        return;
      }
      this.selectedcourtLocationExtId = event.externalId;
      this.selectedcourtLocationName = event.name;
      this.courtRoomControl.enable({ onlySelf: true });
      this.loadCourtRooms(event.id);
    }
  };
  private courtLocationsfilter(name: any): CourtLocations[] {
    const filterValue = name.toLowerCase();
    return this.courtlocationsOptions.filter(option => option.name.toLowerCase().indexOf(filterValue) === 0);
  }
  displayFn(dropdownmodel: dropdownmodel): string {
    return dropdownmodel && dropdownmodel.name ? dropdownmodel.name : '';
  }

  onCourtLocationInput(value: string): void {
    const inputValue = (value || '').trim();
    if (inputValue !== (this.selectedcourtLocationName || '').trim() || !this.selectedcourtLocationExtId) {
      this.selectedcourtLocationExtId = '';
      this.selectedcourtLocationName = '';
      this.selectedcourtRoomExtId = '';
      this.selectedcourtRoomName = '';
      this.courtRoomControl.setValue(null);
      this.courtRoomfilteredOptions = this.clearDropdownValues();
    }
    if (!inputValue) {
      this.courtLocationControl.setValue(null);
    }
  }

  courtLocationChange() {
    this.selectedcourtLocationExtId = '';
    this.selectedcourtLocationName = '';
    this.courtRoomControl.setValue(null);
    this.courtRoomfilteredOptions = this.clearDropdownValues();
    this.courtLocationControl.setValue(null);
  }
  clearDropdownValues() {
    return of([])
  }
  courtLocationChangeEvent(event: any): void {
    if (event && !event.externalId) {
      this.rejectCourtLocationSelection();
      return;
    }
    this.spinner.show();
    if (event) {
      this.selectedcourtLocationExtId = event.externalId;
      this.selectedcourtLocationName = event.name;
    }
    this.selectedcourtRoomExtId = '';
    this.selectedcourtRoomName = '';
    this.courtRoomControl.setValue(null);
    this.courtRoomfilteredOptions = this.clearDropdownValues();
    this.loadCourtRooms(event.id);
    this.courtRoomControl.enable({ onlySelf: true });
  };

  private rejectCourtLocationSelection(): void {
    this.sendNotification('Selected court location does not have an External ID configured in NetScribe. Please configure an External ID before selecting it.', 'error');
    this.selectedcourtLocationExtId = '';
    this.selectedcourtLocationName = '';
    this.courtLocationControl.setValue(null);
    this.courtRoomControl.setValue(null);
    this.courtRoomfilteredOptions = this.clearDropdownValues();
    this.courtRoomControl.disable({ onlySelf: true });
  }

  onCourtRoomInput(value: string): void {
    const inputValue = (value || '').trim();
    if (inputValue !== (this.selectedcourtRoomName || '').trim() || !this.selectedcourtRoomExtId) {
      this.selectedcourtRoomExtId = '';
      this.selectedcourtRoomName = '';
    }
    if (!inputValue) {
      this.courtRoomControl.setValue(null);
    }
  }

  courtRoomChange() {
    this.selectedcourtRoomExtId = '';
    this.selectedcourtRoomName = '';
    this.courtRoomControl.setValue(null);
  }
  courtRoomChangeEvent(event: any): void {
    if (event && !event.externalId) {
      this.rejectCourtRoomSelection();
      return;
    }
    if (event) {
      this.selectedcourtRoomExtId = event.externalId;
      this.selectedcourtRoomName = event.name;
    }
  };

  private rejectCourtRoomSelection(): void {
    this.sendNotification('Selected court room does not have an External ID configured in NetScribe. Please configure an External ID before selecting it.', 'error');
    this.selectedcourtRoomExtId = '';
    this.selectedcourtRoomName = '';
    this.courtRoomControl.setValue(null);
  }
  async loadCourtRooms(id: any) {
    try {
      const response: any = await this.loadCourtRoomsValues(this.selectedOrgExtId, id);
      const courtroomsList = response?.data?.result;
      if (!courtroomsList || courtroomsList.length === 0) {
        this.courtRoomsOptions = [];
        // Same as court locations: mandatory for a court org, so an empty list blocks recording.
        this.sendNotification('No court rooms available for the selected court location. A court room must be configured in NetScribe before recording.', 'warning');
        this.spinner.hide();
        return;
      }
      this.loadCourtRoomsValuesForDropDown(courtroomsList);
    } catch (error) {
      this.sendNotification('Error loading court rooms.', 'error');
    } finally {
      this.spinner.hide();
    }
  }
  async loadCourtRoomsValues(orgExtId: any, id: any) {
    try {
      this.courtRoomsOptions = [];
      return this.backendService.getCourtRooms(orgExtId, id).toPromise();
    } catch (error) {
      this.sendNotification('Error loading court rooms.', 'error');
    } finally {
      //this.spinner.hide();
    }
  }
  loadCourtRoomsValuesForDropDown(courtrooms_res: any) {
    this.courtRoomsOptions = [];
    let courtroomsList = this.backendService.getJSONResponse(courtrooms_res);
    for (let cr of courtroomsList) {
      let data = {
        "id": cr.id,
        "name": cr.name,
        "externalId": cr.externalId
      }
      this.courtRoomsOptions.push(data);
    }
    if (this.courtRoomsOptions.length == 0) {
      this.sendNotification('No courtRooms in netScribe for this courtlocation.', 'error')
    }
    else {
      this.courtRoomfilteredOptions = this.courtRoomControl.valueChanges
        .pipe(
          startWith(''),
          map(value => typeof value === 'string' ? value : ''),
          map(name => name ? this.courtRoomsfilter(name) : this.courtRoomsOptions.slice())
        );
      if (this.courtRoomsOptions.length == 1) {
        this.courtRoomControl.disable({ onlySelf: true });
        this.courtRoomControl.setValue(this.courtRoomsOptions[0]);
        this.selectedcourtRoomExtId = this.courtRoomsOptions[0].externalId;
        this.courtRoomSingleOptionChangeEvent(this.courtRoomsOptions[0]);
      }
    }
  }
  courtRoomSingleOptionChangeEvent(event: any): void {
    if (event.id) {
      if (!event.externalId) {
        this.rejectCourtRoomSelection();
        return;
      }
      this.selectedcourtRoomExtId = event.externalId;
      this.selectedcourtRoomName = event.name;
    }
  };
  private courtRoomsfilter(name: any): CourtRooms[] {
    const filterValue = name.toLowerCase();
    return this.courtRoomsOptions.filter(option => option.name.toLowerCase().indexOf(filterValue) === 0);
  }

  /**Audioplayer */

  playNextAudio(audioRef: HTMLAudioElement) {
    if (this.currentAudioIndex < this.audioList.length - 1) {
      this.changeAudio(audioRef, ++this.currentAudioIndex);
    }
  }

  playPreviousAudio(audioRef: HTMLAudioElement) {
    if (this.currentAudioIndex > 0) {
      this.changeAudio(audioRef, --this.currentAudioIndex);
    }
  }

  pauseAudio(audioRef: HTMLAudioElement) {
    audioRef.pause();
    this.IsAudioPlay = false;
    this.isAudioPlaying = false;
  }

  toggleAudioPlayPause() {
    const audio = this.audioRef?.nativeElement;
    if (!audio) {
      return;
    }

    if (audio.paused || audio.ended) {
      audio.volume = this.audioVolume;
      // Once the channels panel has routed the player through Web Audio, a suspended context means silence.
      this.playbackChannels.resume();
      audio.play().then(() => {
        this.audioStarted = true;
        this.isAudioPlaying = true;
      }).catch((err) => {
        console.warn('Play prevented:', err);
      });
    } else {
      audio.pause();
      this.isAudioPlaying = false;
    }
  }

  onAudioLoadedMetadata() {
    const audio = this.audioRef?.nativeElement;
    if (!audio) {
      return;
    }
    this.updateDurationFromAudio(audio);
  }

  private updateDurationFromAudio(audio: HTMLAudioElement): void {
    if (!audio) {
      return;
    }

    let duration = audio.duration;

    if (!isFinite(duration) || isNaN(duration) || duration <= 0) {
      if (audio.buffered.length > 0) {
        duration = audio.buffered.end(audio.buffered.length - 1);
      }
    }

    if (!isFinite(duration) || isNaN(duration) || duration <= 0) {
      return;
    }

    if (duration !== this.audioDuration) {
      this.audioDuration = duration;
      //console.log(`🔁 Audio duration updated: ${this.formatTime(this.audioDuration)}`);
    }

    this.updateAudioProgress();
  }

  onAudioTimeUpdate() {
    const audio = this.audioRef?.nativeElement;
    if (!audio) {
      return;
    }
    this.audioCurrentTime = audio.currentTime;
    this.updateAudioProgress();
  }

  onAudioEnded() {
    this.isAudioPlaying = false;
  }

  seekAudio(event: MouseEvent) {
    const audio = this.audioRef?.nativeElement;
    if (!audio || !this.audioDuration) {
      return;
    }
    const progressEl = event.currentTarget as HTMLElement;
    const rect = progressEl.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    audio.currentTime = (clickX / rect.width) * this.audioDuration;
  }

  onVolumeChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    this.audioVolume = isNaN(value) ? 1 : value;
    const audio = this.audioRef?.nativeElement;
    if (audio) {
      audio.volume = this.audioVolume;
    }
  }

  // store the previous volume to support mute/unmute toggle
  previousVolume: number = 1;

  seekTo(value: any) {
    const audio = this.audioRef?.nativeElement;
    if (!audio) return;
    const v = parseFloat(value);
    if (!isNaN(v)) {
      audio.currentTime = v;
      this.onAudioTimeUpdate();
    }
  }

  toggleMute() {
    const audio = this.audioRef?.nativeElement;
    if (!audio) return;
    if (this.audioVolume > 0) {
      this.previousVolume = this.audioVolume;
      this.audioVolume = 0;
      audio.volume = 0;
    } else {
      this.audioVolume = this.previousVolume || 1;
      audio.volume = this.audioVolume;
    }
  }
  async toggleRecordingMute(): Promise<void> {
    this.isMuted = !this.isMuted;

    const shouldMute = this.isMuted || this.isPaused;
    for (const stream of [this.audioStream, this.internalStream]) {
      stream?.getAudioTracks().forEach(track => track.enabled = !shouldMute);
    }

    if (this.liveKitService.isConnected()) {
      if (shouldMute) {
        await this.liveKitService.muteAudio();
      } else {
        await this.liveKitService.unmuteAudio();
      }
    }
  }
  private updateAudioProgress() {
    if (!this.audioDuration || this.audioDuration === 0) {
      this.audioProgressPercent = 0;
      return;
    }
    this.audioProgressPercent = Math.min(100, (this.audioCurrentTime / this.audioDuration) * 100);
  }

  // formatTime(seconds: number): string {
  //   if (!seconds || isNaN(seconds) || seconds === Infinity) {
  //     return '00:00:00';
  //   }
  //   const hrs = Math.floor(seconds / 3600);
  //   const mins = Math.floor((seconds % 3600) / 60);
  //   const secs = Math.floor(seconds % 60);
  //   const hh = hrs.toString().padStart(2, '0');
  //   const mm = mins.toString().padStart(2, '0');
  //   const ss = secs.toString().padStart(2, '0');
  //   return `${hh}:${mm}:${ss}`;
  // }

  private changeAudio(audioRef: HTMLAudioElement, index: number) {
    audioRef.pause();
    audioRef.currentTime = 0;
    this.fileToPlay = this.audioList[index];
    audioRef.load();
    audioRef.play();
  }

  private async playHlsForRoom(roomName: string): Promise<void> {
    try {
      // this.spinner.show();
      this.IsAudioPlay = true;
      this.isAudioPlaying = false;
      this.audioStarted = false;
      this.audioVolume = 1;
      this.audioCurrentTime = 0;
      this.audioDuration = 0;
      this.audioProgressPercent = 0;
      this.hlsUrl = await firstValueFrom(this.backendService.getstreamingplaybackdetails(roomName));
      this.fileToPlay = this.hlsUrl;
      this.audioList = [this.hlsUrl];
      this.currentAudioIndex = 0;
      this.cdRef.detectChanges();
      await this.attachHlsToAudio();
    } catch (error) {
      console.error('Failed to load HLS playback URL for room', roomName, error);
      this.sendNotification('Unable to load audio playback details.', 'error');
      this.IsAudioPlay = false;
    } finally {
      // this.spinner.hide();
    }
  }

  private async attachHlsToAudio(): Promise<void> {
    const audio = this.audioRef?.nativeElement;
    if (!audio) {
      return;
    }

    audio.volume = this.audioVolume;

    if (Hls.isSupported()) {
      if (this.hlsEngine) {
        this.hlsEngine.destroy();
        this.hlsEngine = null;
      }
      if (this.hlsRefreshTimer) {
        clearInterval(this.hlsRefreshTimer);
        this.hlsRefreshTimer = null;
      }

      this.hlsEngine = new Hls({
        liveSyncDurationCount: 3,
        maxBufferLength: 60,
        backBufferLength: 90,
        enableWorker: true
      });
      this.hlsEngine.loadSource(this.hlsUrl);
      this.hlsEngine.attachMedia(audio);
      this.hlsEngine.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log("HLS Stream Loaded!");
        this.updateDurationFromAudio(audio);
        this.startHlsRefreshInterval();
      });
      this.hlsEngine.on(Hls.Events.BUFFER_CODECS, (_, data) => {
        this.playbackChannels.setChannelCount(data.audio?.metadata?.channelCount);
      });
      this.hlsEngine.on(Hls.Events.LEVEL_LOADED, () => {
        this.updateDurationFromAudio(audio);
      });
      this.hlsEngine.on(Hls.Events.FRAG_BUFFERED, () => {
        this.updateDurationFromAudio(audio);
      });
      this.hlsEngine.on(Hls.Events.BUFFER_APPENDED, () => {
        this.updateDurationFromAudio(audio);
      });
      this.hlsEngine.on(Hls.Events.ERROR, (_: any, data: any) => {
        if (!data?.fatal || !this.hlsEngine) {
          return;
        }
        console.warn('HLS fatal error, attempting recovery', data.type, data.details);
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          this.hlsEngine.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          this.hlsEngine.recoverMediaError();
        }
      });

      // this.hls.loadSource(this.hlsUrl);
      // this.hls.attachMedia(audio);
      // this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
      //   this.audioStarted = false;
      // });
      // this.hls.on(Hls.Events.ERROR, (event, data) => {
      //   console.error('HLS.js error:', event, data);
      // });
    } else {
      audio.src = this.hlsUrl;
      audio.load();
      this.audioStarted = false;
    }
  }
  // The server playlist is not refreshed automatically by hls.js, so reload it to pick up new segments.
  startHlsRefreshInterval() {
    if (this.hlsRefreshTimer) {
      clearInterval(this.hlsRefreshTimer);
    }

    this.hlsRefreshTimer = setInterval(() => {
      if (!this.hlsEngine) return;
      const audio = this.audioRef?.nativeElement;
      // Resume from the playhead so a paused player doesn't keep buffering at the live edge.
      this.hlsEngine.startLoad(audio ? audio.currentTime : -1);
    }, 5000);
  }
  //  function updateTotalDuration() {
  //     if (!isFinite(audio.duration) || audio.duration <= 0) return;
  //     timelineSlider.max = Math.floor(audio.duration);
  //     counterTotal.innerText = formatTimeDuration(audio.duration);
  // }
  stopPlayer() {
    const audio = this.audioRef?.nativeElement;
    if (audio) {
      audio.pause();          // Stops the audio from playing
      audio.currentTime = 0;   // Resets the track back to the very beginning
    }
    this.audioStarted = false;
    this.isAudioPlaying = false;
    console.log('Player stopped');
  }
  skipAudio(seconds: number): void {
    const audioElement = this.audioRef?.nativeElement;
    if (!audioElement) return;

    // Calculate the new target timestamp
    let newTime = audioElement.currentTime + seconds;

    // Prevent skipping below 0 seconds
    if (newTime < 0) {
      newTime = 0;
    }

    // Prevent skipping past the total audio duration
    if (this.audioDuration && newTime > this.audioDuration) {
      newTime = this.audioDuration;
    }

    // Update the HTML5 audio element current time position
    audioElement.currentTime = newTime;

    // Update your tracking variable for the UI timestamp display instantly
    this.audioCurrentTime = newTime;

    // Optional: Recalculate progress percentage immediately for smooth UI updates
    if (this.audioDuration) {
      this.audioProgressPercent = (this.audioCurrentTime / this.audioDuration) * 100;
    }
  }


  async openAnnotationWindow() {
    this.spinner.show();
    const recStartDate = this.recordingStartDateTime.endsWith('Z') ? this.recordingStartDateTime : `${this.recordingStartDateTime}Z`;
    this.recordingStartDateTime = this.datePipe.transform(recStartDate, 'EEE MMM dd yyyy HH:mm:ss') || '';

    const totalSeconds: number = this.recordingDuration
      .split(':')
      .reduce((acc, time) => (acc * 60) + parseInt(time, 10), 0);
    //const encodedRoom = encodeURIComponent(this.currentRoomName);
    var annotationDialogModel: AnnotationDialogModel = {
      caseNumber: this.currentCaseNumber || "",
      hlsUrl: this.hlsUrl,
      startTimestamp: this.recordingStartDateTime ?? "",
      endTimestamp: "",
      recordingId: this.recordingId ?? 0,
      sessionId: this.currentRoomName ?? "",
      judgeName: this.judgeName, // Need to replace with actual judge name if available
      hearingDate: this.hearingDate ?? "",
      caseDetailsId: this.selectedCaseDetailsId ?? undefined
    };
    const dataModel = {
      annotationDialogModel: annotationDialogModel,
      // Replace 'this.currentTime' with the exact variable holding your player's time
      getLiveAudioTime: () => totalSeconds || 0
    };
    // Opens the exact same file, utilizing the exact same API logic
    this.annotationsComponent.openAnnotationWindow(dataModel);
  }
  sendNotification(message: any, messageType: any) {
    this.msgNotification.elementRef.nativeElement.querySelector('.jqx-notification-content').innerHTML = message;
    this.msgNotification.template(messageType);
    this.msgNotification.open();
  }
}

// Confirm Clear Dialog Component
@Component({
  selector: 'app-confirm-clear-dialog',
  template: `
    <h2 mat-dialog-title class="confirmation-title">
      <mat-icon>warning</mat-icon>
      <span>{{ data?.title || 'Confirm Action' }}</span>
    </h2>
    <mat-dialog-content class="confirmation-content">
      <p>{{ data?.message || 'Are you sure you want to proceed? This will clear all current data.' }}</p>
      <div class="stats" *ngIf="data?.showStats && data?.wordCount !== undefined">
        <p><strong>Words:</strong> {{ data.wordCount }}</p>
        <p><strong>Characters:</strong> {{ data.characterCount }}</p>
      </div>
      <p class="confirmation-warning">
        <mat-icon>info</mat-icon><span>This action cannot be undone.</span>
      </p>
    </mat-dialog-content>
    <mat-dialog-actions class="confirmation-actions" align="end">
      <button mat-button [mat-dialog-close]="false">Cancel</button>
      <button mat-raised-button color="warn" [mat-dialog-close]="true"
        [style.background-color]="data?.confirmBackgroundColor">{{ data?.confirmLabel || 'Confirm' }}</button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host ::ng-deep .recording-confirm-dialog-panel .mat-dialog-container {
      overflow: hidden;
      padding: 0;
      border-radius: 4px;
    }
    .confirmation-title {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      text-align: left;
      gap: 8px;
      margin: 0;
      padding: 16px 20px;
      color: #333;
      font-size: 20px;
      line-height: 1.3;
      border-bottom: 1px solid #e0e0e0;
    }
    .confirmation-title mat-icon {
      color: #c62828;
      flex: 0 0 auto;
    }
    .confirmation-content {
      min-width: 0;
      padding: 20px;
      background: #fff;
      color: #333;
      line-height: 1.5;
    }
    .confirmation-content p {
      margin: 0;
    }
    .stats {
      margin: 15px 0;
      padding: 12px;
      background: #f5f5f5;
      border-radius: 4px;
      border-left: 3px solid #2196F3;
    }
    .stats p {
      margin: 5px 0;
      color: #333;
    }
    .confirmation-warning {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 16px 0 0;
      color: #b71c1c;
      font-weight: 600;
      line-height: 1.3;
    }
    .confirmation-warning mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      flex: 0 0 auto;
    }
    .confirmation-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 8px;
      min-height: 0;
      margin: 0;
      padding: 12px 20px;
      border-top: 1px solid #e0e0e0;
    }
  `]
})
export class ConfirmClearDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }
}

// Recovery Dialog Component
@Component({
  selector: 'app-recording-recovery-dialog',
  template: `
    <h2 mat-dialog-title>
      <mat-icon style="color: #ff9800; vertical-align: middle; margin-right: 8px;">restore</mat-icon>
      Recording Found
    </h2>
    <mat-dialog-content>
      <p>We found an unsaved recording from a previous session.</p>
      
      <div class="session-info">
        <div class="info-row">
          <strong>Started:</strong> {{formatDate(data.session.startTime)}}
        </div>
        <div class="info-row">
          <strong>Duration:</strong> {{formatDuration(data.session.recordingDuration)}}
        </div>
        <div class="info-row">
          <strong>Mode:</strong> {{data.session.mode === 'live' ? 'Live Streaming' : 'Batch Recording'}}
        </div>
        <div class="info-row" *ngIf="data.chunkCount > 0">
          <strong>Audio Chunks:</strong> {{data.chunkCount}}
        </div>
        <div class="info-row" *ngIf="data.transcription">
          <strong>Transcription:</strong> {{data.transcription.wordCount}} words
        </div>
      </div>

      <p style="margin-top: 16px;">Would you like to restore this recording?</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="'discard'" color="warn">Discard</button>
      <button mat-raised-button [mat-dialog-close]="'restore'" color="primary" cdkFocusInitial>Restore</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .session-info {
      background-color: #f5f5f5;
      padding: 16px;
      border-radius: 4px;
      margin-top: 12px;
    }
    .info-row {
      margin-bottom: 8px;
      font-size: 14px;
    }
    .info-row:last-child {
      margin-bottom: 0;
    }
    mat-dialog-content {
      min-width: 400px;
    }
    mat-icon {
      font-size: 28px;
      width: 28px;
      height: 28px;
    }
  `]
})
export class RecordingRecoveryDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }

  formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
